import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_REVIEW_PROMPT, type AgentType } from "@cord/shared";
import { useState, useEffect, useMemo, useRef, useCallback, useId, forwardRef, useImperativeHandle } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  FolderRoot,
  Terminal,
  Hammer,
  Play,
  FileKey2,
  Plus,
  Trash2,
  Loader2,
  Check,
  AlertCircle,
  Sparkles,
  CheckCircle2,
  X,
  Package,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  AlertTriangle,
  Bot,
  Blocks,
  Key,
  Eye,
  EyeOff,
  Wand2,
  Code2,
  GitFork,
  HardDrive,
  File,
  FolderOpen,
  ExternalLink,
  Lock,
  Info,
  Shield,
  Pencil,
  MonitorCheck,
  Rocket,
} from "lucide-react";
import { CaretRight } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  buildOptimisticEmptyThreadSessionRecord,
  mergeSessionRecordPreservingHasHistory,
} from "@/lib/optimistic-empty-thread-session-record";
import {
  applyOptimisticSessionMembershipToSessionListCaches,
  captureSessionCacheMutationSnapshot,
  restoreSessionCacheMutationSnapshot,
  type SessionCacheMutationSnapshot,
} from "@/lib/session-membership-cache";
import { useInvalidateReposQueryWhenBuildsSettle } from "@/hooks/use-invalidate-repos-query-when-builds-settle";
import { useMobileLayout } from "@/hooks/use-mobile-layout";

import { useUserPreferences } from "@/hooks/use-user-preferences";
import { useModelPrefs } from "@/stores/model-prefs";
import {
  formatThinkingVariantLabel,
  getPreferredThinkingVariant,
  getThinkingVariants,
  normalizeThinkingVariant,
  THINKING_OFF_VARIANT,
} from "@/config/model-capabilities";
import { PROVIDER_LABELS } from "@/config/model-selector";
import { filterVisibleModels, isProviderModelCurated, parseReleaseDate } from "@/lib/model-visibility";
import {
  getProviderDiscoveryBadgeLabel,
  getProviderDiscoveryDescription,
  type ProviderDiscoveryMetadata,
} from "@/lib/model-discovery-presentation";
import {
  buildRepositoryOverridePatch,
  getRepositoryDefaultsOverride,
} from "@/lib/repository-defaults";
import {
  createDefaultDockerComposeRuntimeProcessDefinition,
  createDefaultRuntimeEndpointDefinition,
  createDefaultRuntimeProcessDefinition,
  EMPTY_BUILD_SETTINGS,
  formatBuildHooksInput,
  isEmptyBuildSettings,
  normalizeBuildSettings,
  parseBuildHooksInput,
  settingsRequireDocker,
  type BuildSettings,
  type RuntimeEndpointDefinition,
  type RuntimeProcessDefinition,
  type RuntimeProcessDockerComposeRunner,
} from "@/lib/build-settings-form";
import {
  deriveRepoBuildsViewModel,
  type RepoBuildRecord,
  type RepoBuildStatus,
} from "@/lib/repo-builds-view-model";
import { getSandboxProviderUiPolicy } from "@/lib/sandbox-provider-ui-policy";
import { api } from "@/lib/api";
import { canShowRepositoryDeployUi } from "@/lib/repository-deploy-ui";
import { normalizeArrayResponse } from "@/lib/repository-route-response-normalizers";
import { BuildConfigSummary } from "@/components/repo-settings/BuildConfigSummary";
import { AIChatPanel } from "@/components/repo-settings/AIChatPanel";
import { SetupChat } from "@/components/repo-settings/SetupChat";
import { OnboardingProgressBar, type OnboardingStep } from "@/components/repo-settings/OnboardingProgressBar";
import { OnboardingFooter } from "@/components/repo-settings/OnboardingFooter";
import { SkipSetupButton } from "@/components/repo-settings/SkipSetupButton";



type RepoSettingsTab = "build" | "environment" | "builds" | "agent-model" | "agent" | "testing" | "prompts";

/** Valid tab identifiers accepted via the `?tab=` search parameter for deep-linking. */
const VALID_TAB_IDS = new Set<string>(["build", "environment", "builds", "agent-model", "agent", "testing", "prompts"]);

export const Route = createFileRoute("/_authed/$repoId/settings")({
  component: RepoSettingsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    // Only treat explicit truthy values as onboarding — "false", "0", etc. should not activate it.
    onboarding: search.onboarding === true || search.onboarding === "true" || search.onboarding === "1",
  }),
});

const API_BASE = import.meta.env.VITE_API_URL || "";
const EMPTY_REPOSITORY_AI_SETUP_UNAVAILABLE_MESSAGE = "AI setup needs committed files to inspect. Create the first commit from a thread, or edit settings manually for now.";

const SANDBOX_PROVIDER_OPTION_METADATA = {
  modal: {
    label: "Modal",
    hint: "Current default provider with the existing deployed sandbox manager",
  },
  daytona: {
    label: "Daytona",
    hint: "Use Daytona for new image builds and new root session sandboxes for this repo",
  },
  freestyle: {
    label: "Freestyle",
    hint: "Use Freestyle for the VM-backed sandbox provider path in environments where it is allowed and configured",
  },
} as const;

const SANDBOX_PROVIDER_LABELS: Record<string, string> = {
  modal: "Modal",
  daytona: "Daytona",
  freestyle: "Freestyle",
};

function getSandboxProviderOptionMetadata(providerId: string): {
  label: string;
  hint: string;
} {
  const metadata = SANDBOX_PROVIDER_OPTION_METADATA[
    providerId as keyof typeof SANDBOX_PROVIDER_OPTION_METADATA
  ];

  return metadata ?? {
    label: SANDBOX_PROVIDER_LABELS[providerId] ?? providerId,
    hint: "Sandbox provider available on this deployment",
  };
}

interface BuildSettingsResponse extends Partial<BuildSettings> {
  defaultBranch?: string;
  allowAllRepositoryAccess?: boolean;
  availableSandboxProviders?: Array<{
    id: string;
    configured: boolean;
    capabilities?: {
      supportsDockerInWorkspace: boolean;
      supportsComposeInWorkspace: boolean;
    };
  }>;
}

async function apiFetch<T = unknown>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...opts,
    headers: { "Content-Type": "application/json", ...opts?.headers },
  });
  if (!res.ok) {
    const body = await res.text();
    let parsedErrorMessage: string | null = null;
    try {
      const parsedBody = JSON.parse(body) as { error?: unknown };
      if (typeof parsedBody.error === "string" && parsedBody.error.trim()) {
        parsedErrorMessage = parsedBody.error;
      }
    } catch {
      // Fall through to the raw response body when the server did not return a
      // structured JSON error payload.
    }
    throw new Error(parsedErrorMessage || body || `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

type BuildConfigStepMode = "choosing" | "assistant" | "manual";

/**
 * Imperative handle exposed by tab components that have a save action.
 * Used by the onboarding footer to save before advancing steps, and by
 * the ProposedConfigCard to switch the build tab into advanced editing mode.
 */
interface TabSaveHandle {
  /** Trigger a save. Returns true on success or if nothing needed saving. */
  save: () => Promise<boolean>;
  /** Switch to advanced editing mode (only implemented by BuildSettingsTab). */
  switchToAdvanced?: () => void;
  /** Whether the tab has unsaved changes. Used by OnboardingFooter to show indicator. */
  isDirty?: boolean;
}

const TABS: { id: RepoSettingsTab; label: string }[] = [
  { id: "build", label: "Build Config" },
  { id: "environment", label: "Environment" },
  { id: "builds", label: "Builds" },
  { id: "agent-model", label: "Agent & Model" },
  { id: "agent", label: "AI Tools" },
  { id: "testing", label: "Testing" },
  { id: "prompts", label: "Prompts" },
];

/** Maps each onboarding step to the corresponding settings tab ID. */
const ONBOARDING_STEP_TAB_MAP: Record<OnboardingStep, RepoSettingsTab> = {
  1: "build",
  2: "environment",
  3: "builds",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function RepoSettingsPage() {
  const { repoId } = Route.useParams();
  const { onboarding } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobileLayout = useMobileLayout();

  // Read ?tab= from the URL for deep-linking. This is intentionally read from
  // the raw URL rather than validateSearch so callers don't need to supply it.
  const [activeTab, setActiveTab] = useState<RepoSettingsTab>(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam && VALID_TAB_IDS.has(tabParam)) return tabParam as RepoSettingsTab;
    return "build";
  });
  const [isChatOpen, setIsChatOpen] = useState(onboarding);

  // Mobile two-level navigation state (list → content → back)
  const [mobileShowContent, setMobileShowContent] = useState(false);
  const handleMobileBack = () => setMobileShowContent(false);

  /**
   * Whether the AI chat should auto-start analysis. Starts false during
   * onboarding (user must click "Set up automatically" first). Set to true
   * when the user explicitly chooses AI setup or when triggered from empty state.
   */
  const [chatAutoStart, setChatAutoStart] = useState(false);

  // ---------------------------------------------------------------------------
  // Onboarding state
  // ---------------------------------------------------------------------------
  const [onboardingStep, setOnboardingStep] = useState<OnboardingStep>(1);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [buildConfigStepMode, setBuildConfigStepMode] = useState<BuildConfigStepMode>(
    onboarding ? "choosing" : "manual",
  );

  /**
   * Onboarding is only active when the user was explicitly sent here from the
   * repo-connect flow (via ?onboarding=true). We no longer auto-detect
   * onboarding from empty settings because that's confusing when the user
   * navigates to settings for other tabs (env vars, agent config, etc.).
   */
  const isOnboarding = !onboardingComplete && onboarding;

  /**
   * Step 1 of onboarding uses a dedicated full-screen shell so the user stays
   * in one guided flow whether they choose the AI assistant or manual editing.
   * This avoids the jarring feeling of "leaving onboarding" when switching to
   * manual setup.
   */
  const showBuildConfigOnboardingShell = isOnboarding && onboardingStep === 1;
  const showFullScreenChat = showBuildConfigOnboardingShell && buildConfigStepMode === "assistant";
  const showFullScreenManualEdit = showBuildConfigOnboardingShell && buildConfigStepMode === "manual";

  /**
   * Tracks whether the user has ever clicked "Set up automatically" in this
   * session. Once true, SetupChat stays mounted (even when switching to manual
   * mode) so that the AI conversation state is preserved across mode switches.
   * Never resets back to false — the chat stays alive for the entire step 1.
   */
  const [aiSetupMounted, setAiSetupMounted] = useState(false);

  // ---------------------------------------------------------------------------
  // AI proposal bridge — pending proposal flows from chat into BuildSettingsTab
  // ---------------------------------------------------------------------------
  const [pendingProposal, setPendingProposal] = useState<BuildSettings | null>(null);

  // Imperative save handles for child tab components so the onboarding footer
  // can trigger a save before advancing to the next step.
  const buildSettingsRef = useRef<TabSaveHandle>(null);
  const envFilesRef = useRef<TabSaveHandle>(null);

  /**
   * Reactive dirty state from child tabs. Lifted here via onDirtyChange
   * callbacks so the OnboardingFooter can show an "unsaved" indicator.
   * Ref reads (buildSettingsRef.current?.isDirty) don't trigger re-renders,
   * so we need real state that child tabs push into.
   */
  const [buildTabDirty, setBuildTabDirty] = useState(false);
  const [envTabDirty, setEnvTabDirty] = useState(false);

  const { data: buildSettingsData, isLoading: buildSettingsLoading } = useQuery<BuildSettingsResponse>({
    queryKey: ["build-settings", repoId],
    queryFn: () => apiFetch(`/api/repos/${repoId}/build-settings`),
  });

  const { data: repos } = useQuery({
    queryKey: ["repos"],
    queryFn: async () => {
      const res = await api.api.repos.$get();
      if (!res.ok) return [];
      return normalizeArrayResponse<{
        id: string;
        deployEnabled?: boolean;
        isCordNewProject?: boolean;
      }>({ value: await res.json() });
    },
  });
  const repo = (repos ?? []).find((r) => r.id === repoId);
  const showDeployShortcut = canShowRepositoryDeployUi({ repository: repo });

  const assistantUnavailableReason = buildSettingsData?.defaultBranch === ""
    ? EMPTY_REPOSITORY_AI_SETUP_UNAVAILABLE_MESSAGE
    : null;
  const assistantAvailabilityPending = buildSettingsLoading && !buildSettingsData;
  const assistantSetupDisabled = assistantAvailabilityPending || assistantUnavailableReason !== null;

  const handleOpenChat = useCallback(() => {
    if (assistantSetupDisabled) return;
    if (showBuildConfigOnboardingShell) {
      setBuildConfigStepMode("assistant");
      setAiSetupMounted(true);
      setChatAutoStart(true);
      return;
    }
    if (isChatOpen) return;
    setChatAutoStart(true);
    setIsChatOpen(true);
  }, [assistantSetupDisabled, isChatOpen, showBuildConfigOnboardingShell]);
  const handleCloseChat = useCallback(() => {
    setIsChatOpen(false);
    setChatAutoStart(false);
  }, []);

  /**
   * When the AI proposes a build config via proposeBuildConfig, store it so
   * BuildSettingsTab can pick it up and apply it as unsaved local edits.
   * The user then reviews and explicitly saves — same flow as manual editing.
   */
  const handleConfigProposed = useCallback((config: BuildSettings) => {
    setPendingProposal(config);
  }, []);

  const clearPendingProposal = useCallback(() => {
    setPendingProposal(null);
  }, []);

  /**
   * Tracks whether a "Use this config" save is currently in flight.
   * Used to disable the ProposedConfigCard buttons and prevent double-clicks.
   */
  const [isAcceptingConfig, setIsAcceptingConfig] = useState(false);
  const isAcceptingConfigRef = useRef(false);

  /**
   * "Use this config" — triggered from the ProposedConfigCard in the chat.
   * Saves the current (AI-proposed) build settings and advances to the next
   * onboarding step. Closes the full-screen chat so steps 2+ show the
   * regular settings layout. Outside of onboarding it saves, closes the
   * side chat panel, and surfaces a toast so the user gets clear feedback
   * (otherwise the click silently updates the form and looks broken).
   */
  const handleAcceptConfig = useCallback(async () => {
    if (isAcceptingConfigRef.current) return;
    isAcceptingConfigRef.current = true;
    setIsAcceptingConfig(true);
    try {
      const saved = await buildSettingsRef.current?.save() ?? true;
      if (!saved) return;
      if (isOnboarding && onboardingStep === 1) {
        setOnboardingStep(2);
        setActiveTab("environment");
        setBuildConfigStepMode("assistant");
        setIsChatOpen(false);
        setChatAutoStart(false);
      } else {
        // Non-onboarding: the assistant lives in a side panel. Close it and
        // confirm the save with a toast so the action feels completed.
        setIsChatOpen(false);
        setChatAutoStart(false);
        toast.success("Build config updated");
      }
    } finally {
      isAcceptingConfigRef.current = false;
      setIsAcceptingConfig(false);
    }
  }, [isOnboarding, onboardingStep]);


  const handleOnboardingContinue = useCallback(async () => {
    if (onboardingStep === 1) {
      // Save build settings before advancing if there are unsaved changes.
      const saved = await buildSettingsRef.current?.save() ?? true;
      if (!saved) return;
      setOnboardingStep(2);
      setActiveTab("environment");
      setIsChatOpen(false);
      setChatAutoStart(false);
    } else if (onboardingStep === 2) {
      // Save env files before advancing if there are unsaved changes.
      const saved = await envFilesRef.current?.save() ?? true;
      if (!saved) return;
      setOnboardingStep(3);
      setActiveTab("builds");
    } else {
      // Step 3 → finish
      setOnboardingComplete(true);
      navigate({ to: "/$repoId", params: { repoId } });
    }
  }, [onboardingStep, navigate, repoId]);

  const handleOnboardingSkip = useCallback(() => {
    if (onboardingStep < 3) {
      const nextStep = (onboardingStep + 1) as OnboardingStep;
      setOnboardingStep(nextStep);
      setActiveTab(ONBOARDING_STEP_TAB_MAP[nextStep]);
    } else {
      setOnboardingComplete(true);
      navigate({ to: "/$repoId", params: { repoId } });
    }
  }, [onboardingStep, navigate, repoId]);

  const handleOnboardingStepClick = useCallback((step: OnboardingStep) => {
    setOnboardingStep(step);
    setActiveTab(ONBOARDING_STEP_TAB_MAP[step]);
  }, []);

  /** Switch from the guided AI view to manual editing without leaving onboarding. */
  const handleSkipToManualSetup = useCallback(() => {
    setActiveTab("build");
    setBuildConfigStepMode("manual");
    // Immediately open the edit form so the user doesn't have to find and click the
    // separate "Edit" button inside the summary card — that extra click is confusing
    // for a first-time user who just said they want to set up manually.
    buildSettingsRef.current?.switchToAdvanced?.();
  }, []);

  /**
   * Return from manual editing (or the initial choice screen) to the guided AI
   * setup within the same shell. Mounts SetupChat permanently and enables
   * auto-start. Subsequent calls (e.g. switching back from manual) just reveal
   * the already-mounted chat — the auto-start effect is guarded by its own
   * `initialSentRef` so it won't re-trigger.
   */
  const handleUseAssistantSetup = useCallback(() => {
    if (assistantSetupDisabled) return;
    setActiveTab("build");
    setBuildConfigStepMode("assistant");
    setAiSetupMounted(true);
    setChatAutoStart(true);
  }, [assistantSetupDisabled]);

  return (
    <div
      className="flex h-full"
      style={isMobileLayout ? { paddingTop: "var(--safe-area-top, 0px)" } : undefined}
    >
      {/* ── Full-screen build-config onboarding shell ────────────── */}
      {showBuildConfigOnboardingShell && (
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div className="mx-auto w-full max-w-4xl px-8 pt-6">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <OnboardingProgressBar
                  currentStep={onboardingStep}
                  onStepClick={handleOnboardingStepClick}
                />
              </div>
              <SkipSetupButton
                repoId={repoId}
                defaultBranch={buildSettingsData?.defaultBranch ?? null}
                loading={buildSettingsLoading}
              />
            </div>
          </div>

          {/* ── Banner ──────────────────────────────────────────────── */}
          <div
            className={cn(
              "mx-auto flex w-full max-w-4xl px-4",
              buildConfigStepMode === "choosing"
                ? "flex-1 items-center pb-20 pt-4"
                : "pt-4",
            )}
          >
            <div className="w-full rounded-2xl border border-zinc-800/60 bg-zinc-900/30 px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500/20 to-blue-500/20 ring-1 ring-white/[0.06]">
                  <Code2 className="size-4 text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-base font-medium text-zinc-100">
                    Configure your repository
                  </div>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-zinc-400">
                    {buildConfigStepMode === "choosing"
                      ? assistantUnavailableReason
                        ? "This repository is empty, so AI setup cannot inspect project files yet. You can configure settings manually now, or start a thread to create the first commit."
                        : "Set up how your project builds and runs. Let AI inspect the repo and draft a configuration for you, or configure everything yourself."
                      : buildConfigStepMode === "assistant"
                        ? "AI is inspecting your repo and suggesting a configuration. Review it, accept it, or ask for changes in the chat below."
                        : "You're editing the build settings directly. Adjust them below, then continue when you're happy."}
                  </p>

                  {assistantUnavailableReason && buildConfigStepMode === "choosing" && (
                    <div className="mt-3 flex max-w-2xl items-start gap-2 rounded-xl border border-amber-500/15 bg-amber-500/10 px-3 py-2.5 text-xs leading-5 text-amber-200/90">
                      <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
                      <span>{assistantUnavailableReason}</span>
                    </div>
                  )}

                  {/* ── Mode buttons ─────────────────────────────────── */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleUseAssistantSetup}
                      disabled={assistantSetupDisabled}
                      title={assistantUnavailableReason ?? undefined}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-50",
                        buildConfigStepMode === "assistant" || buildConfigStepMode === "choosing"
                          ? "bg-zinc-100 text-zinc-900 shadow-sm"
                          : "border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-100",
                      )}
                    >
                      <Wand2 className="size-3" />
                      Set up automatically
                    </button>
                    <button
                      type="button"
                      onClick={handleSkipToManualSetup}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all",
                        showFullScreenManualEdit
                          ? "bg-zinc-100 text-zinc-900 shadow-sm"
                          : "border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:bg-zinc-800/60 hover:text-zinc-100",
                      )}
                    >
                      <Code2 className="size-3" />
                      Set up manually
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── SetupChat — mounted once AI setup is started, hidden in
                manual mode so conversation state is preserved ───────── */}
          <div
            className={cn(
              "mx-auto flex w-full max-w-4xl flex-1 flex-col overflow-hidden px-4 pt-4",
              !showFullScreenChat && "hidden",
            )}
          >
            {aiSetupMounted && (
              <SetupChat
                repoId={repoId}
                autoStart={chatAutoStart}
                onConfigProposed={handleConfigProposed}
                onAcceptConfig={handleAcceptConfig}
                isAcceptPending={isAcceptingConfig}
              />
            )}
          </div>

          {/* ── BuildSettingsTab — single always-mounted instance so form
                state (including AI proposals) is preserved when switching
                between AI and manual modes. Visible in manual mode, hidden
                otherwise. Uses CSS display:none so React keeps the component
                mounted and the imperative save ref stays functional. ──── */}
          <div
            className={cn(
              "mx-auto flex w-full max-w-4xl flex-1 flex-col overflow-y-auto px-4 pb-6 pt-4",
              !showFullScreenManualEdit && "hidden",
            )}
          >
            <BuildSettingsTab
              ref={buildSettingsRef}
              repoId={repoId}
              onOpenChat={handleOpenChat}
              pendingProposal={pendingProposal}
              onProposalConsumed={clearPendingProposal}
              hideInlineSave={isOnboarding}
              assistantUnavailableReason={assistantUnavailableReason}
              onDirtyChange={setBuildTabDirty}
            />
          </div>

          {/* ── Footer ─────────────────────────────────────────────── */}
          {buildConfigStepMode !== "choosing" && (
            <div className="border-t border-zinc-800/40 bg-zinc-950/95 backdrop-blur-sm">
              <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-4 px-8 py-3">
                {showFullScreenChat ? (
                  <>
                    <div className="text-xs text-zinc-500">
                      Step 1 of 3 · Build configuration
                    </div>
                    <button
                      type="button"
                      onClick={handleOnboardingContinue}
                      className="inline-flex items-center gap-1.5 rounded-md bg-zinc-100 px-4 py-1.5 text-xs font-medium text-zinc-900 transition-colors hover:bg-white"
                    >
                      Continue
                      <ArrowRight className="size-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500">Step 1 of 3 · Build configuration</span>
                      {buildTabDirty && (
                        <span className="flex items-center gap-1 text-xs text-amber-400/70">
                          <span className="inline-block size-1 rounded-full bg-amber-400/70" />
                          unsaved
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleOnboardingContinue}
                      className="inline-flex items-center gap-1.5 rounded-md bg-zinc-100 px-4 py-1.5 text-xs font-medium text-zinc-900 transition-colors hover:bg-white"
                    >
                      Continue
                      <ArrowRight className="size-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Main settings area ─────────────────────────────────────
       *  Always rendered but hidden (display:none) during full-screen chat
       *  so that BuildSettingsTab stays mounted and the save ref works when
       *  the user clicks "Use this config".
       */}
      <div
        className="flex min-w-0 flex-1 flex-col overflow-hidden"
        style={showBuildConfigOnboardingShell ? { display: "none" } : undefined}
      >
        <div className="flex-1 overflow-y-auto"
          style={isMobileLayout ? {
            paddingTop: "var(--safe-area-top, 0px)",
            paddingBottom: "var(--safe-area-bottom, 0px)",
          } : undefined}
        >
        <div
          className={cn(
            "mx-auto animate-[fade-in_0.2s_ease-out]",
            isMobileLayout ? "max-w-3xl px-4 py-4" : "max-w-3xl px-8 py-6",
          )}
        >
          {/* Onboarding stepper — visible when onboarding is active */}
          {isOnboarding && (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <OnboardingProgressBar
                  currentStep={onboardingStep}
                  onStepClick={handleOnboardingStepClick}
                />
              </div>
              <SkipSetupButton
                repoId={repoId}
                defaultBranch={buildSettingsData?.defaultBranch ?? null}
                loading={buildSettingsLoading}
              />
            </div>
          )}

          {/* Header — back, title, AI button on one line */}
          {isMobileLayout ? (
            <div className="mb-4 flex items-center gap-3">
              <button
                type="button"
                onClick={mobileShowContent ? handleMobileBack : () => navigate({ to: `/${repoId}` })}
                aria-label={mobileShowContent ? "Back to settings list" : "Back to repo"}
                className="flex size-10 shrink-0 items-center justify-center rounded-full text-zinc-400 transition-colors active:bg-zinc-800"
              >
                <ArrowLeft className="size-5" />
              </button>
              <h1 className="text-[17px] font-semibold text-zinc-200">
                {mobileShowContent
                  ? TABS.find((t) => t.id === activeTab)?.label ?? "Settings"
                  : "Settings"}
              </h1>

              {/* Deploy shortcut — only for Cord-created projects with shareable URL enabled. */}
              {!isOnboarding && showDeployShortcut && (
                <Link
                  to="/$repoId/deploy"
                  params={{ repoId }}
                  search={{ sessionId: undefined }}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-zinc-800 px-2.5 py-1 text-xs font-medium text-zinc-500 transition-colors hover:border-zinc-700 hover:text-zinc-300"
                >
                  <Rocket className="size-3" />
                  Deploy
                </Link>
              )}

              {/* AI Assistant button */}
              {!isChatOpen && !isOnboarding && !mobileShowContent && (
                <button
                  type="button"
                  onClick={handleOpenChat}
                  disabled={assistantSetupDisabled}
                  title={assistantUnavailableReason ?? undefined}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-zinc-800 px-2.5 py-1 text-xs font-medium text-zinc-500 transition-colors hover:border-zinc-700 hover:text-zinc-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Sparkles className="size-3" />
                  AI Assistant
                </button>
              )}
            </div>
          ) : (
            <div className="mb-4 flex items-center gap-3">
              <Link
                to="/$repoId"
                params={{ repoId }}
                className="inline-flex items-center text-zinc-500 transition-colors hover:text-zinc-300"
                aria-label="Back"
              >
                <ArrowLeft className="size-4" />
              </Link>
              <h1 className="text-sm font-semibold tracking-tight text-zinc-100">
                Settings
              </h1>

              {/* Deploy shortcut — only for Cord-created projects with shareable URL enabled. */}
              {!isOnboarding && showDeployShortcut && (
                <Link
                  to="/$repoId/deploy"
                  params={{ repoId }}
                  search={{ sessionId: undefined }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 px-2.5 py-1 text-xs font-medium text-zinc-500 transition-colors hover:border-zinc-700 hover:text-zinc-300"
                >
                  <Rocket className="size-3" />
                  Deploy
                </Link>
              )}

              {/* AI Assistant button — only show outside of onboarding.
                  During onboarding, the AI chat is part of Step 1 only and
                  shouldn't appear as a separate button in Steps 2/3. */}
              {!isChatOpen && !isOnboarding && (
                <button
                  type="button"
                  onClick={handleOpenChat}
                  disabled={assistantSetupDisabled}
                  title={assistantUnavailableReason ?? undefined}
                  className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-zinc-800 px-2.5 py-1 text-xs font-medium text-zinc-500 transition-colors hover:border-zinc-700 hover:text-zinc-300 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Sparkles className="size-3" />
                  AI Assistant
                </button>
              )}
            </div>
          )}

          {/* Tab Navigation — hidden during onboarding since progress bar handles it */}
          {!isOnboarding && !isMobileLayout && (
            <nav role="tablist" className="mb-4 flex gap-1 border-b border-zinc-800/60 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
              {TABS.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={activeTab === t.id}
                  aria-controls={`tabpanel-${t.id}`}
                  onClick={() => setActiveTab(t.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-xs font-medium transition-colors",
                    activeTab === t.id
                      ? "border-zinc-100 text-zinc-100"
                      : "border-transparent text-zinc-500 hover:text-zinc-300",
                  )}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          )}

          {/* Mobile: iOS Settings-style tab list (level 1) */}
          {!isOnboarding && isMobileLayout && !mobileShowContent && (
            <div className="overflow-hidden rounded-xl bg-zinc-900/60">
              {TABS.map((t, idx) => (
                <button
                  key={t.id}
                  onClick={() => { setActiveTab(t.id); setMobileShowContent(true); }}
                  className={cn(
                    "flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors active:bg-zinc-800",
                    idx !== 0 && "border-t border-zinc-800/60",
                  )}
                >
                  <span className="flex-1 text-[15px] text-zinc-200">{t.label}</span>
                  <CaretRight className="size-4 text-zinc-600" />
                </button>
              ))}
            </div>
          )}

          {/* Tab Content — all panels stay mounted to preserve form state */}
          {/* On mobile, only show when mobileShowContent is true (level 2) */}
          <div className={isMobileLayout && !mobileShowContent && !isOnboarding ? "hidden" : undefined}>
            <div role="tabpanel" id="tabpanel-build" className={activeTab !== "build" ? "hidden" : undefined}>
              {!showBuildConfigOnboardingShell && (
                <BuildSettingsTab
                  ref={buildSettingsRef}
                  repoId={repoId}
                  onOpenChat={handleOpenChat}
                  pendingProposal={pendingProposal}
                  onProposalConsumed={clearPendingProposal}
                  hideInlineSave={isOnboarding}
                  assistantUnavailableReason={assistantUnavailableReason}
                  onDirtyChange={setBuildTabDirty}
                />
              )}
            </div>
            <div role="tabpanel" id="tabpanel-environment" className={activeTab !== "environment" ? "hidden" : undefined}>
              <EnvFilesTab ref={envFilesRef} repoId={repoId} onDirtyChange={setEnvTabDirty} />
            </div>
            <div role="tabpanel" id="tabpanel-builds" className={activeTab !== "builds" ? "hidden" : undefined}>
              <BuildStatusTab repoId={repoId} />
            </div>
            <div role="tabpanel" id="tabpanel-agent-model" className={activeTab !== "agent-model" ? "hidden" : undefined}>
              <RepoAgentAndModelTab repoId={repoId} />
            </div>
            <div role="tabpanel" id="tabpanel-agent" className={activeTab !== "agent" ? "hidden" : undefined}>
              <AgentConfigTab repoId={repoId} />
            </div>
            <div role="tabpanel" id="tabpanel-testing" className={activeTab !== "testing" ? "hidden" : undefined}>
              <TestingTab repoId={repoId} />
            </div>
            <div role="tabpanel" id="tabpanel-prompts" className={activeTab !== "prompts" ? "hidden" : undefined}>
              <PromptsTab repoId={repoId} />
            </div>
          </div>
        </div>
        </div>

        {/* Onboarding footer — sticky at bottom, outside the scrollable area */}
        {isOnboarding && (
          <OnboardingFooter
            currentStep={onboardingStep}
            onContinue={handleOnboardingContinue}
            onSkip={handleOnboardingSkip}
            hasUnsavedChanges={
              (onboardingStep === 1 && buildTabDirty) ||
              (onboardingStep === 2 && envTabDirty) ||
              false
            }
          />
        )}
      </div>

      {/* AI Chat side panel — only shown in regular (non-full-screen) mode */}
      {!showBuildConfigOnboardingShell && isChatOpen && (
        <AIChatPanel
          repoId={repoId}
          autoStart={chatAutoStart}
          onConfigProposed={handleConfigProposed}
          onClose={handleCloseChat}
          onAcceptConfig={handleAcceptConfig}
          isAcceptPending={isAcceptingConfig}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared UI
// ---------------------------------------------------------------------------

function TabHeader({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-zinc-100">{title}</h2>
      <p className="mt-1 text-sm text-zinc-400">{description}</p>
    </div>
  );
}

function SettingsCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-zinc-800/60 bg-zinc-900/40 p-5", className)}>
      {children}
    </div>
  );
}

function SectionHeader({ title, icon }: { title: string; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-zinc-500">{icon}</span>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-zinc-400">{title}</h3>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Agent & Model tab
// ---------------------------------------------------------------------------

const REPOSITORY_AGENT_OPTIONS: Array<{ value: AgentType; label: string; description: string }> = [
  {
    value: "opencode",
    label: "OpenCode",
    description: "Open-source coding agent, supports all providers.",
  },
  {
    value: "claude_code",
    label: "Claude Code",
    description: "Anthropic's native coding agent. Requires an Anthropic API key or Claude subscription OAuth.",
  },
  // {
  //   value: "cursor",
  //   label: "Cursor",
  //   description: "Cursor's native agent via ACP. Requires a Cursor API key or manually supplied auth token.",
  // },
];

interface RepoSettingsProviderModel {
  id: string;
  name: string;
  release_date?: string;
  variants?: Record<string, Record<string, unknown>>;
  status?: string;
}

interface RepoSettingsProvider {
  id: string;
  name: string;
  models: Record<string, RepoSettingsProviderModel>;
  discovery?: ProviderDiscoveryMetadata;
}

interface RepoSettingsProvidersResponse {
  all: RepoSettingsProvider[];
  default: Record<string, string>;
  connected: string[];
}

interface RepoSettingsModelOption {
  id: string;
  name: string;
  variants?: string[];
}

function getVisibleRepoSettingsModels(
  providerID: string,
  models: Record<string, RepoSettingsProviderModel>,
  getVisibility: (key: { providerID: string; modelID: string }) => "show" | "hide" | undefined,
  pinnedModelID?: string | null,
): RepoSettingsModelOption[] {
  const sorted = Object.values(models)
    .filter((model) => model.status !== "deprecated")
    .filter((model) => isProviderModelCurated(providerID, model.id))
    .sort((a, b) => {
      const byRelease = parseReleaseDate(b.release_date) - parseReleaseDate(a.release_date);
      if (byRelease !== 0) return byRelease;
      return a.name.localeCompare(b.name);
    })
    .map((model) => ({
      id: model.id,
      name: model.name ?? model.id,
      providerID,
      active: true,
      releaseDate: model.release_date,
      variants: model.variants ? Object.keys(model.variants) : undefined,
    }));

  const visible = filterVisibleModels(sorted, getVisibility).map((model) => ({
    id: model.id,
    name: model.name,
    variants: model.variants,
  }));

  if (!pinnedModelID || visible.some((model) => model.id === pinnedModelID)) {
    return visible;
  }

  const pinned = sorted.find((model) => model.id === pinnedModelID);
  if (!pinned) return visible;

  return [
    {
      id: pinned.id,
      name: pinned.name,
      variants: pinned.variants,
    },
    ...visible,
  ];
}

function RepoAgentAndModelTab({ repoId }: { repoId: string }) {
  return (
    <div className="space-y-6">
      <RepoDefaultAgentSettings repoId={repoId} />
      <RepoDefaultModelSettings repoId={repoId} />
      <RepositoryAccessSettings repoId={repoId} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cross-repo access
// ---------------------------------------------------------------------------

/**
 * Lets the user toggle whether this repo's sandboxes get a broad GitHub App
 * installation token (access to all installed repos) instead of the default
 * narrow token scoped to only this repository.
 *
 * Practical example: your API repo depends on types exported from a sibling
 * shared-types repo. Enabling this lets AI sessions in the API repo clone and
 * read the shared-types repo during builds or runtime.
 */
function RepositoryAccessSettings({ repoId }: { repoId: string }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<{ allowAllRepositoryAccess: boolean }>({
    queryKey: ["repository-access", repoId],
    queryFn: () => apiFetch(`/api/repos/${repoId}/repository-access`),
  });

  const enabled = data?.allowAllRepositoryAccess === true;

  const toggle = useMutation({
    mutationFn: async (nextValue: boolean) => {
      await apiFetch(`/api/repos/${repoId}/repository-access`, {
        method: "PATCH",
        body: JSON.stringify({ allowAllRepositoryAccess: nextValue }),
      });
      return nextValue;
    },
    onSuccess: (nextValue) => {
      queryClient.setQueryData(["repository-access", repoId], {
        allowAllRepositoryAccess: nextValue,
      });
      // Also update the build-settings cache so it stays in sync if the user
      // switches to the Build Config tab without a refetch.
      queryClient.setQueryData<BuildSettingsResponse>(
        ["build-settings", repoId],
        (prev) => prev ? { ...prev, allowAllRepositoryAccess: nextValue } : prev,
      );
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to update repository access");
    },
  });

  return (
    <SettingsCard>
      <div className="flex items-center gap-2 mb-1">
        <GitFork className="size-4 text-zinc-400" />
        <div className="text-xs font-medium text-zinc-300">Cross-Repository Access</div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        By default, AI sessions for this repo can only read and clone <strong className="text-zinc-400">this repository</strong>.
        Enable the option below if sessions also need to access other private repos
        in your GitHub App installation — for example, to pull shared libraries or read
        types from a sibling repo.
      </p>

      <label
        className={cn(
          "flex items-start gap-3 rounded-xl border px-4 py-3 text-sm transition-colors",
          enabled
            ? "border-zinc-600 bg-zinc-800/60 text-zinc-200"
            : "border-zinc-800/60 bg-zinc-900/40 text-zinc-400",
          (isLoading || toggle.isPending) && "opacity-60",
        )}
      >
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => toggle.mutate(event.target.checked)}
          disabled={isLoading || toggle.isPending}
          className="mt-0.5 rounded border-zinc-700 bg-zinc-900 text-zinc-200"
        />
        <span>
          <span className="block font-medium text-zinc-200">Allow access to all installed repositories</span>
          <span className="mt-1 block text-xs leading-5 text-zinc-500">
            When enabled, sandboxes and image builds receive a GitHub token that can clone or pull any
            repo available to the same GitHub App installation. Only enable this for repos that
            genuinely need to read sibling repos — it broadens the access scope of every session.
          </span>
        </span>
      </label>
    </SettingsCard>
  );
}

function RepoDefaultAgentSettings({ repoId }: { repoId: string }) {
  const { preferences, patchPreferences } = useUserPreferences();
  const override = getRepositoryDefaultsOverride({ preferences, repositoryId: repoId });
  const selectedAgent = override?.agentType ?? null;
  const globalDefault = preferences.defaults.agentType;
  const globalLabel = REPOSITORY_AGENT_OPTIONS.find((option) => option.value === globalDefault)?.label ?? "OpenCode";

  const saveAgentOverride = (agentType: AgentType | null) => {
    if (agentType === selectedAgent) return;
    patchPreferences(
      buildRepositoryOverridePatch({
        preferences,
        repositoryId: repoId,
        changes: { agentType },
      }),
    );
  };

  return (
    <SettingsCard>
      <div className="flex items-center gap-2 mb-1">
        <Bot className="size-4 text-zinc-400" />
        <div className="text-xs font-medium text-zinc-300">Default Agent</div>
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        This repository uses this default when starting new sessions. You can still switch per-session.
      </p>

      <div className="space-y-1.5">
        <button
          type="button"
          onClick={() => saveAgentOverride(null)}
          className={cn(
            "w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
            selectedAgent === null
              ? "border-zinc-600 bg-zinc-800/60 text-zinc-200"
              : "border-zinc-800/60 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-300",
          )}
        >
          <div className="flex-1">
            <div className="text-sm font-medium">Use general default ({globalLabel})</div>
            <div className="text-xs text-zinc-500 mt-0.5">Falls back to your global Settings choice.</div>
          </div>
          {selectedAgent === null && <Check className="size-3.5 shrink-0 text-zinc-400" />}
        </button>

        {REPOSITORY_AGENT_OPTIONS.map((option) => (
          <button
            type="button"
            key={option.value}
            onClick={() => saveAgentOverride(option.value)}
            className={cn(
              "w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
              selectedAgent === option.value
                ? "border-zinc-600 bg-zinc-800/60 text-zinc-200"
                : "border-zinc-800/60 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-300",
            )}
          >
            <div className="flex-1">
              <div className="text-sm font-medium">{option.label}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{option.description}</div>
            </div>
            {selectedAgent === option.value && <Check className="size-3.5 shrink-0 text-zinc-400" />}
          </button>
        ))}
      </div>
    </SettingsCard>
  );
}

function RepoDefaultModelSettings({ repoId }: { repoId: string }) {
  const { preferences, patchPreferences } = useUserPreferences();
  const { visibility: visibilityMap, getVisibility } = useModelPrefs();

  const override = getRepositoryDefaultsOverride({ preferences, repositoryId: repoId });
  const pinnedOverrideProviderID = override?.model.providerID ?? null;
  const pinnedOverrideModelID = override?.model.modelID ?? null;
  const effectiveAgentType = override?.agentType ?? preferences.defaults.agentType;
  const [selectedProvider, setSelectedProvider] = useState<string | null>(override?.model.providerID ?? null);
  const [selectedModel, setSelectedModel] = useState<string | null>(override?.model.modelID ?? null);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(normalizeThinkingVariant(override?.thinkingVariant));
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setSelectedProvider(override?.model.providerID ?? null);
    setSelectedModel(override?.model.modelID ?? null);
    setSelectedVariant(normalizeThinkingVariant(override?.thinkingVariant));
    setDirty(false);
  }, [override?.model.modelID, override?.model.providerID, override?.thinkingVariant, repoId]);

  const { data: providerData, isLoading: modelsLoading } = useQuery({
    queryKey: ["repo-settings-providers", effectiveAgentType],
    queryFn: async () => {
      const url = new URL(`${API_BASE}/api/settings/providers`, window.location.origin);
      url.searchParams.set("agentType", effectiveAgentType);
      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) return null;
      return res.json() as Promise<RepoSettingsProvidersResponse>;
    },
    staleTime: 5 * 60 * 1000,
  });

  const availableProviders = useMemo(() => {
    return (providerData?.all ?? [])
        .map((provider) => ({
          ...provider,
          modelsForSettings: getVisibleRepoSettingsModels(
            provider.id,
            provider.models,
            getVisibility,
            pinnedOverrideProviderID === provider.id
              ? pinnedOverrideModelID
              : null,
          ),
        }))
      .filter((provider) => provider.modelsForSettings.length > 0);
  }, [getVisibility, pinnedOverrideModelID, pinnedOverrideProviderID, providerData, visibilityMap]);

  const modelsForProvider: RepoSettingsModelOption[] = selectedProvider
    ? availableProviders.find((provider) => provider.id === selectedProvider)?.modelsForSettings ?? []
    : [];

  const selectedModelOption = selectedModel
    ? modelsForProvider.find((model) => model.id === selectedModel) ?? null
    : null;

  useEffect(() => {
    if (!selectedProvider || !selectedModel) return;
    if (modelsLoading) return;
    const provider = availableProviders.find((p) => p.id === selectedProvider);
    if (!provider) {
      setSelectedProvider(null);
      setSelectedModel(null);
      setSelectedVariant(null);
      return;
    }
    if (provider.modelsForSettings.some((model) => model.id === selectedModel)) return;
    setSelectedModel(null);
    setSelectedVariant(null);
  }, [availableProviders, modelsLoading, selectedModel, selectedProvider]);

  const variantOptions = selectedProvider && selectedModel
    ? getThinkingVariants(selectedProvider, selectedModel, selectedModelOption?.variants)
    : [];
  const defaultVariant = selectedProvider && selectedModel
    ? getPreferredThinkingVariant(selectedProvider, selectedModel, selectedModelOption?.variants)
    : null;

  useEffect(() => {
    if (!selectedProvider || !selectedModel) return;
    if (variantOptions.length === 0) {
      setSelectedVariant(null);
      return;
    }
    if (selectedVariant === THINKING_OFF_VARIANT) return;
    if (selectedVariant && variantOptions.includes(selectedVariant)) return;
    setSelectedVariant(defaultVariant);
  }, [defaultVariant, selectedModel, selectedProvider, selectedVariant, variantOptions]);

  const globalDefaultProvider = preferences.defaults.model.providerID;
  const globalDefaultModel = preferences.defaults.model.modelID;
  const globalDefaultVariant = normalizeThinkingVariant(preferences.defaults.thinkingVariant);
  const hasModelOverride = Boolean(
    override?.model.providerID || override?.model.modelID || override?.thinkingVariant,
  );
  const isUnchanged =
    selectedProvider === (override?.model.providerID ?? null) &&
    selectedModel === (override?.model.modelID ?? null) &&
    selectedVariant === normalizeThinkingVariant(override?.thinkingVariant);

  const handleSave = () => {
    if (selectedProvider && !selectedModel) return;
    patchPreferences(
      buildRepositoryOverridePatch({
        preferences,
        repositoryId: repoId,
        changes: {
          model: {
            providerID: selectedProvider,
            modelID: selectedModel,
          },
          thinkingVariant:
            selectedProvider && selectedModel
              ? (selectedVariant ?? defaultVariant)
              : null,
        },
      }),
    );
    setDirty(false);
  };

  const handleClearOverride = () => {
    setSelectedProvider(null);
    setSelectedModel(null);
    setSelectedVariant(null);
    patchPreferences(
      buildRepositoryOverridePatch({
        preferences,
        repositoryId: repoId,
        changes: {
          model: { providerID: null, modelID: null },
          thinkingVariant: null,
        },
      }),
    );
    setDirty(false);
  };

  return (
    <SettingsCard>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="size-4 text-zinc-400" />
        <div className="text-xs font-medium text-zinc-300">Default Model</div>
      </div>
      <p className="text-xs text-zinc-500 mb-3">
        This repository uses this model override for new sessions. You can still switch per-session.
      </p>

      <div className="space-y-3">
        <div>
          <div className="mb-1.5 text-xs font-medium text-zinc-500">Provider</div>
          <select
            value={selectedProvider ?? ""}
            onChange={(e) => {
              const value = e.target.value || null;
              setSelectedProvider(value);
              setSelectedModel(null);
              setSelectedVariant(null);
              setDirty(true);
            }}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 outline-none focus:ring-1 focus:ring-zinc-700"
          >
            <option value="">Use general default</option>
            {availableProviders.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {PROVIDER_LABELS[provider.id] ?? provider.name}
              </option>
            ))}
          </select>
          {(() => {
            const selectedProviderData = availableProviders.find((p) => p.id === selectedProvider);
            const discoveryLabel = getProviderDiscoveryBadgeLabel(selectedProviderData?.discovery);
            const discoveryDesc = getProviderDiscoveryDescription(selectedProviderData?.discovery);
            if (!discoveryLabel) return null;
            return (
              <div className="mt-1 text-xs text-zinc-500" title={discoveryDesc ?? undefined}>
                Models source: <span className="text-zinc-400">{discoveryLabel}</span>
              </div>
            );
          })()}
        </div>

        {selectedProvider && (
          <div>
            <div className="mb-1.5 text-xs font-medium text-zinc-500">Model</div>
            {modelsLoading ? (
              <div className="text-xs text-zinc-500">Loading models...</div>
            ) : modelsForProvider.length > 0 ? (
              <select
                value={selectedModel ?? ""}
                onChange={(e) => {
                  const value = e.target.value || null;
                  setSelectedModel(value);
                  setSelectedVariant(null);
                  setDirty(true);
                }}
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 outline-none focus:ring-1 focus:ring-zinc-700"
              >
                <option value="">Select a model</option>
                {modelsForProvider.map((model) => (
                  <option key={model.id} value={model.id}>{model.name}</option>
                ))}
              </select>
            ) : (
              <div className="text-xs text-zinc-500">No models available for this provider.</div>
            )}
          </div>
        )}

        {selectedProvider && selectedModel && (
          <div>
            <div className="mb-1.5 text-xs font-medium text-zinc-500">Thinking</div>
            {variantOptions.length === 0 ? (
              <div className="w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm text-zinc-500">
                Not available
              </div>
            ) : (
              <select
                value={selectedVariant ?? defaultVariant ?? ""}
                onChange={(e) => {
                  setSelectedVariant(normalizeThinkingVariant(e.target.value));
                  setDirty(true);
                }}
                className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 outline-none focus:ring-1 focus:ring-zinc-700"
              >
                <option value={THINKING_OFF_VARIANT}>Off</option>
                {variantOptions.map((variant) => (
                  <option key={variant} value={variant}>{formatThinkingVariantLabel(variant)}</option>
                ))}
              </select>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          {hasModelOverride && (
            <button
              type="button"
              onClick={handleClearOverride}
              className="rounded-lg border border-zinc-800 px-3 py-1.5 text-xs text-zinc-400 transition-colors hover:border-zinc-700 hover:text-zinc-300"
            >
              Clear override
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={Boolean(selectedProvider && !selectedModel) || (isUnchanged && !dirty)}
            className="rounded-lg bg-zinc-100 px-3 py-1.5 text-xs font-medium text-zinc-900 transition-colors hover:bg-zinc-200 disabled:opacity-50"
          >
            Save override
          </button>
        </div>

        {selectedProvider && !selectedModel && (
          <p className="text-xs text-zinc-600">Select a model before saving a provider override.</p>
        )}

        {!hasModelOverride && !selectedProvider && (
          <p className="text-xs text-zinc-600">
            No repository model override set. New sessions use your general Settings default
            {globalDefaultProvider && globalDefaultModel
              ? ` (${PROVIDER_LABELS[globalDefaultProvider] ?? globalDefaultProvider} · ${globalDefaultModel}${globalDefaultVariant ? ` · ${formatThinkingVariantLabel(globalDefaultVariant)}` : ""}).`
              : "."}
          </p>
        )}
      </div>
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Build Settings Tab
// ---------------------------------------------------------------------------

function formatRuntimeEndpointPort(value: number | null): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

function parseRuntimeEndpointPort(args: { value: string; maxPort: number }): number | null {
  if (!/^\d+$/.test(args.value.trim())) return null;
  const parsed = Number(args.value.trim());
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= args.maxPort ? parsed : null;
}

function formatRuntimeEndpointPortList(args: { ports: readonly number[] }): string {
  return args.ports.map((port) => String(port)).join(", ")
}

function collectReservedRuntimeEndpointPortsInUse(args: {
  processes: RuntimeProcessDefinition[]
  reservedRuntimeEndpointPorts: readonly number[]
}): number[] {
  const reservedPorts = new Set(args.reservedRuntimeEndpointPorts)
  const portsInUse = new Set<number>()

  for (const process of args.processes) {
    for (const endpoint of process.endpoints) {
      if (endpoint.port !== null && reservedPorts.has(endpoint.port)) {
        portsInUse.add(endpoint.port)
      }
    }
  }

  return Array.from(portsInUse).sort((left, right) => left - right)
}

// TCP/UDP ports are 16-bit unsigned integers. Compose target ports describe the
// service's container-side listener and are not constrained by Cord's preview
// host-port policy, so the full network-port range should be allowed here.
const MAX_COMPOSE_TARGET_PORT = 65_535;

// Three rows keep the build-hooks textarea compact for repos with zero or one
// hook while still making the multiline affordance obvious. Very hook-heavy
// repos can grow the control via the textarea's natural resize behavior.
const BUILD_HOOKS_TEXTAREA_MIN_ROWS = 3;

function countTextareaLines(value: string): number {
  return value.length > 0 ? value.split(/\r?\n/).length : 0;
}

function parseDockerComposeTargetPort(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value.trim());
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= MAX_COMPOSE_TARGET_PORT
    ? parsed
    : null;
}

function formatDockerComposeFileList(files: string[] | null | undefined): string {
  return Array.isArray(files) ? files.join("\n") : "";
}

function parseDockerComposeFileList(input: string): string[] {
  return Array.from(new Set(input.split(/\r?\n/).map((value) => value.trim()).filter((value) => value.length > 0)));
}

type BuildSettingsSubTab = "summary" | "advanced";
type EditableBuildSettingsTextKey = "sandboxProviderId" | "rootDirectory" | "installCommand" | "buildCommand";

interface BuildSettingsTabProps {
  repoId: string;
  onOpenChat: () => void;
  /** AI-proposed config to apply as unsaved local edits. Null when no pending proposal. */
  pendingProposal: BuildSettings | null;
  /** Called after the pending proposal has been consumed (applied to form state). */
  onProposalConsumed: () => void;
  assistantUnavailableReason?: string | null;
  /**
   * When true, the inline "Unsaved changes / Save" bar is hidden inside
   * BuildConfigSummary because the OnboardingFooter handles save-on-continue.
   * Reduces competing CTAs during onboarding.
   */
  hideInlineSave?: boolean;
  /** Called when the tab's dirty state changes. Used by the parent to reactively show unsaved indicators. */
  onDirtyChange?: (dirty: boolean) => void;
}

const BuildSettingsTab = forwardRef<TabSaveHandle, BuildSettingsTabProps>(
  function BuildSettingsTab({ repoId, onOpenChat, pendingProposal, onProposalConsumed, assistantUnavailableReason, hideInlineSave, onDirtyChange }, ref) {
  const queryClient = useQueryClient();
  const [buildSubTab, setBuildSubTab] = useState<BuildSettingsSubTab>("summary");

  const { data, isLoading } = useQuery<BuildSettingsResponse>({
    queryKey: ["build-settings", repoId],
    queryFn: () => apiFetch(`/api/repos/${repoId}/build-settings`),
  });

  const availableSandboxProviderIds = useMemo(
    () => (data?.availableSandboxProviders ?? []).map((provider) => provider.id),
    [data],
  );

  const fallbackSandboxProviderId = availableSandboxProviderIds[0]
    ?? data?.sandboxProviderId
    ?? EMPTY_BUILD_SETTINGS.sandboxProviderId;

  const normalizedData = useMemo(
    () => normalizeBuildSettings(data, {
      fallbackProviderId: fallbackSandboxProviderId,
      allowedProviderIds: availableSandboxProviderIds,
    }),
    [availableSandboxProviderIds, data, fallbackSandboxProviderId],
  );

  const settingsAreEmpty = useMemo(
    () => isEmptyBuildSettings(normalizedData),
    [normalizedData],
  );

  const sandboxProviderOptions = useMemo(() => {
    return (data?.availableSandboxProviders ?? []).map((provider) => ({
      id: provider.id,
      ...getSandboxProviderOptionMetadata(provider.id),
      configured: provider.configured,
      capabilities: provider.capabilities ?? {
        supportsDockerInWorkspace: false,
        supportsComposeInWorkspace: false,
      },
    }));
  }, [data]);

  const [form, setForm] = useState<BuildSettings>(EMPTY_BUILD_SETTINGS);
  const [buildHooksDraft, setBuildHooksDraft] = useState("");
  const [dirty, setDirty] = useState(false);

  // Notify parent when dirty state changes so OnboardingFooter can react.
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const sandboxProviderUiPolicy = getSandboxProviderUiPolicy({
    providerId: form.sandboxProviderId,
  });
  const selectedProviderOption = sandboxProviderOptions.find((option) => option.id === form.sandboxProviderId) ?? null;
  const selectedProviderConfigured = selectedProviderOption?.configured ?? true;
  const selectedProviderSupportsCompose = selectedProviderOption?.capabilities.supportsComposeInWorkspace ?? false;
  const daytonaComposeOption = sandboxProviderOptions.find((option) => option.id === "daytona") ?? null;
  const hasAnyComposeProcess = settingsRequireDocker({ processes: form.runtimeProcesses });
  const composeRuntimeBlockedReason = hasAnyComposeProcess && !selectedProviderSupportsCompose
    ? selectedProviderConfigured
      ? "Docker Compose processes currently require Daytona with the Docker-enabled runtime image configured."
      : "The selected sandbox provider is not configured for Docker Compose processes in this environment."
    : null;
  const composeFilesMissing =
    hasAnyComposeProcess &&
    form.runtimeProcesses.some((process) =>
      process.runner.kind === "docker_compose" && process.runner.files.length === 0,
    );
  const composeEndpointsMissingMetadata =
    hasAnyComposeProcess &&
    form.runtimeProcesses.some((process) =>
      process.runner.kind === "docker_compose" &&
      process.endpoints.some((endpoint) =>
        endpoint.composeServiceName == null || endpoint.composeServiceName.trim().length === 0 || endpoint.composeTargetPort == null,
      ),
    );
  const reservedRuntimeEndpointPortsInUse = useMemo(() => collectReservedRuntimeEndpointPortsInUse({
    processes: form.runtimeProcesses,
    reservedRuntimeEndpointPorts: sandboxProviderUiPolicy.reservedRuntimeEndpointPorts,
  }), [form.runtimeProcesses, sandboxProviderUiPolicy.reservedRuntimeEndpointPorts]);
  const firstReservedRuntimeEndpointPortInUse = reservedRuntimeEndpointPortsInUse[0] ?? null;

  useEffect(() => {
    if (data) {
      if (dirty) return;
      const normalized = normalizeBuildSettings(data, {
        fallbackProviderId: fallbackSandboxProviderId,
        allowedProviderIds: availableSandboxProviderIds,
      });
      setForm(normalized);
      setBuildHooksDraft(formatBuildHooksInput(normalized.buildHooks));
      setDirty(false);
    }
  }, [availableSandboxProviderIds, data, dirty, fallbackSandboxProviderId]);

  const save = useMutation({
    mutationFn: async () => {
      if (!selectedProviderConfigured) {
        throw new Error("The selected sandbox provider is not configured in this environment.");
      }
      if (composeRuntimeBlockedReason) {
        throw new Error(composeRuntimeBlockedReason);
      }
      if (composeFilesMissing) {
        throw new Error("Add at least one Docker Compose file before saving.");
      }
      if (composeEndpointsMissingMetadata) {
        throw new Error("Each Docker Compose endpoint must include both a service name and a container port.");
      }
      if (firstReservedRuntimeEndpointPortInUse !== null) {
        throw new Error(
          `Port ${firstReservedRuntimeEndpointPortInUse} is reserved by Cord and cannot be used for a runtime endpoint. Reserved ports: ${formatRuntimeEndpointPortList({ ports: sandboxProviderUiPolicy.reservedRuntimeEndpointPorts })}.`,
        );
      }
      const normalizedPayload = normalizeBuildSettings(form, {
        fallbackProviderId: fallbackSandboxProviderId,
        allowedProviderIds: availableSandboxProviderIds,
      });
      await apiFetch(`/api/repos/${repoId}/build-settings`, {
        method: "PUT",
        body: JSON.stringify(normalizedPayload),
      });
      return { normalizedPayload };
    },
    onSuccess: ({ normalizedPayload }) => {
      queryClient.setQueryData<BuildSettingsResponse>(["build-settings", repoId], (previous) => ({
        ...previous,
        ...normalizedPayload,
      }));
      setForm(normalizedPayload);
      setBuildHooksDraft(formatBuildHooksInput(normalizedPayload.buildHooks));
      queryClient.invalidateQueries({ queryKey: ["build-settings", repoId] });
      setDirty(false);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to save build settings");
    },
  });

  // Expose imperative handle for the onboarding footer and ProposedConfigCard.
  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!dirty) return true;
      try {
        await save.mutateAsync();
        return true;
      } catch {
        // Error toast is already shown by the mutation's onError handler.
        return false;
      }
    },
    switchToAdvanced: () => {
      setBuildSubTab("advanced");
    },
    isDirty: dirty,
  }), [dirty, save]);

  // Apply AI-proposed config as unsaved local edits when a proposal arrives.
  useEffect(() => {
    if (!pendingProposal) return;
    const normalized = normalizeBuildSettings(pendingProposal, {
      fallbackProviderId: fallbackSandboxProviderId,
      allowedProviderIds: availableSandboxProviderIds,
    });
    setForm(normalized);
    setBuildHooksDraft(formatBuildHooksInput(normalized.buildHooks));
    setDirty(true);
    setBuildSubTab("summary");
    onProposalConsumed();
  }, [availableSandboxProviderIds, fallbackSandboxProviderId, onProposalConsumed, pendingProposal]);

  const update = (key: EditableBuildSettingsTextKey, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value || null }));
    setDirty(true);
  };

  const updateBuildHooks = (value: string) => {
    setBuildHooksDraft(value);
    setForm((prev) => ({
      ...prev,
      buildHooks: parseBuildHooksInput(value),
    }));
    setDirty(true);
  };

  const updateRuntimeProcesses = (runtimeProcesses: RuntimeProcessDefinition[]) => {
    setForm((prev) => ({ ...prev, runtimeProcesses }));
    setDirty(true);
  };

  const saveDisabled =
    !dirty ||
    save.isPending ||
    !selectedProviderConfigured ||
    Boolean(composeRuntimeBlockedReason) ||
    composeFilesMissing ||
    composeEndpointsMissingMetadata ||
    firstReservedRuntimeEndpointPortInUse !== null;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <TabHeader
          title="Build Configuration"
          description="Define how Cord builds the workspace for your repo, then how it runs your runtime processes and endpoints inside each session."
        />
        <SettingsCard>
          <div className="flex items-center justify-center py-8 text-sm text-zinc-500">
            <Loader2 className="mr-2 size-4 animate-spin" />
            Loading settings...
          </div>
        </SettingsCard>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/*
       * Summary card — shown in two situations:
       *   1. Normal (non-onboarding) settings: always visible so the user has
       *      a persistent read-only view with an "Edit" toggle.
       *   2. Onboarding: only shown when NOT in edit mode. Once the user has
       *      opened the edit form (buildSubTab === "advanced"), the summary
       *      card is hidden because the form fields already display the same
       *      values. Showing both is confusing — it's unclear which one is
       *      "authoritative" and where to actually make changes.
       */}
      {!(hideInlineSave && buildSubTab === "advanced") && (
        <BuildConfigSummary
          config={dirty ? form : normalizedData}
          isLoading={isLoading}
          isEmpty={dirty ? isEmptyBuildSettings(form) : settingsAreEmpty}
          assistantUnavailableReason={assistantUnavailableReason}
          onEditClick={() => setBuildSubTab(buildSubTab === "advanced" ? "summary" : "advanced")}
          onOpenChat={onOpenChat}
          isDirty={dirty}
          onSave={() => save.mutate()}
          isSaving={save.isPending}
          isSaveDisabled={saveDisabled}
          hideInlineSave={hideInlineSave}
        />
      )}

      {/* Advanced editing form — toggled by "Edit settings" */}
      {buildSubTab === "advanced" && (<>

      {/* Section 1: Sandbox Provider */}
      {sandboxProviderOptions.length > 1 && (
        <SettingsCard>
          <SectionHeader title="Sandbox Provider" icon={<Blocks className="size-3.5" />} />
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {sandboxProviderOptions.map((option) => {
              const selected = form.sandboxProviderId === option.id;
              const disabledForComposeMode =
                hasAnyComposeProcess && !option.capabilities.supportsComposeInWorkspace;
              return (
                <button
                  key={option.id}
                  type="button"
                  disabled={!option.configured || disabledForComposeMode}
                  onClick={() => update("sandboxProviderId", option.id)}
                  className={cn(
                    "rounded-lg border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                    selected
                      ? "border-zinc-500 bg-zinc-800 text-zinc-100"
                      : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700 hover:bg-zinc-800/80",
                  )}
                >
                  <div className="flex items-center justify-between gap-2 text-sm font-medium">
                    <span>{option.label}</span>
                    {!option.configured && (
                      <span className="rounded-full border border-amber-800/60 bg-amber-950/30 px-2 py-0.5 text-xs font-medium text-amber-300">
                        Not configured
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-zinc-500">{option.hint}</div>
                </button>
              );
            })}
          </div>
          {!selectedProviderConfigured && (
            <p className="mt-2 text-xs text-amber-300">
              The currently selected provider is not configured in this environment.
            </p>
          )}
        </SettingsCard>
      )}

      {/* Section 2: Build Fields */}
      <SettingsCard>
        <SectionHeader title="Build" icon={<Package className="size-3.5" />} />
        {composeRuntimeBlockedReason && (
          <p className="mt-2 text-xs text-amber-300">{composeRuntimeBlockedReason}</p>
        )}

        <div className="mt-4 space-y-3">
          <FormField
            icon={<FolderRoot className="size-3.5" />}
            label="Root directory"
            hint="Subdirectory containing your project (leave empty for repo root)"
            placeholder="e.g. apps/web"
            value={form.rootDirectory ?? ""}
            onChange={(v) => update("rootDirectory", v)}
          />
          <FormField
            icon={<Terminal className="size-3.5" />}
            label="Install command"
            hint="Detected from your lockfile if left empty"
            placeholder="e.g. pnpm install --frozen-lockfile"
            value={form.installCommand ?? ""}
            onChange={(v) => update("installCommand", v)}
          />
          <FormField
            icon={<Hammer className="size-3.5" />}
            label="Build command"
            hint="Optional — runs after install during image build"
            placeholder="e.g. npm run build"
            value={form.buildCommand ?? ""}
            onChange={(v) => update("buildCommand", v)}
          />
          <div>
            <div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-zinc-300">
              <Code2 className="size-3.5 text-zinc-500" />
              <span>Build hooks</span>
            </div>
            <p className="mb-2 text-xs leading-relaxed text-zinc-500">
              Newline-separated bash commands. Cord runs each hook in order after the build command completes.
            </p>
            <textarea
              value={buildHooksDraft}
              onChange={(event) => updateBuildHooks(event.target.value)}
              rows={Math.max(BUILD_HOOKS_TEXTAREA_MIN_ROWS, countTextareaLines(buildHooksDraft))}
              placeholder={"pnpm install sharp\npnpm install sqlite3"}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:ring-1 focus:ring-zinc-700"
              spellCheck={false}
            />
          </div>
        </div>
      </SettingsCard>

      {/* Section 3: Runtime Processes */}
      <RuntimeProcessesEditor
        processes={form.runtimeProcesses}
        maxRuntimeServices={sandboxProviderUiPolicy.maxRuntimeServices}
        maxRuntimeServicePort={sandboxProviderUiPolicy.maxRuntimeServicePort}
        reservedRuntimeEndpointPorts={sandboxProviderUiPolicy.reservedRuntimeEndpointPorts}
        composeAvailable={daytonaComposeOption?.capabilities.supportsComposeInWorkspace ?? false}
        onChange={updateRuntimeProcesses}
      />

      {/* Sticky save bar — sits at the bottom of the advanced view */}
      <div className="sticky bottom-0 z-10 border-t border-zinc-800/60 bg-zinc-950/95 py-3 backdrop-blur-sm">
        {/* Validation warnings — shown above the save button when there are blocking issues */}
        {(
          composeRuntimeBlockedReason ||
          composeFilesMissing ||
          composeEndpointsMissingMetadata ||
          firstReservedRuntimeEndpointPortInUse !== null
        ) && (
          <div className="mb-3 rounded-lg border border-amber-800/40 bg-amber-950/20 px-3 py-2 text-xs text-amber-200">
            <p className="mb-1 font-medium text-amber-300">Fix before saving:</p>
            <ul className="list-inside list-disc space-y-0.5 text-amber-300/90">
              {composeRuntimeBlockedReason && <li>{composeRuntimeBlockedReason}</li>}
              {composeFilesMissing && <li>Add at least one Docker Compose file.</li>}
              {composeEndpointsMissingMetadata && <li>Each Compose endpoint needs a service name and container port.</li>}
              {firstReservedRuntimeEndpointPortInUse !== null && (
                <li>
                  Reserved runtime ports cannot be used for app endpoints. Reserved ports: {formatRuntimeEndpointPortList({ ports: sandboxProviderUiPolicy.reservedRuntimeEndpointPorts })}.
                </li>
              )}
            </ul>
          </div>
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={() => save.mutate()}
            disabled={saveDisabled}
            className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200 disabled:opacity-40"
          >
            {save.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : save.isSuccess && !dirty ? (
              <Check className="size-3.5" />
            ) : null}
            {save.isPending ? "Saving..." : save.isSuccess && !dirty ? "Saved" : "Save"}
          </button>
          {dirty && (
            <span className="text-xs text-zinc-500">Unsaved changes</span>
          )}
          {save.isError && (
            <span className="flex items-center gap-1 text-xs text-red-400">
              <AlertCircle className="size-3" />
              {(save.error as Error).message}
            </span>
          )}
        </div>
      </div>
      </>)}
    </div>
  );
});

function RuntimeProcessesEditor({
  processes,
  maxRuntimeServices,
  maxRuntimeServicePort,
  reservedRuntimeEndpointPorts,
  composeAvailable,
  onChange,
}: {
  processes: RuntimeProcessDefinition[];
  maxRuntimeServices: number;
  maxRuntimeServicePort: number;
  reservedRuntimeEndpointPorts: readonly number[];
  composeAvailable: boolean;
  onChange: (processes: RuntimeProcessDefinition[]) => void;
}) {
  const duplicatePorts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const process of processes) {
      for (const endpoint of process.endpoints) {
        if (endpoint.port === null) continue;
        counts.set(endpoint.port, (counts.get(endpoint.port) ?? 0) + 1);
      }
    }
    return new Set(Array.from(counts.entries()).filter(([, count]) => count > 1).map(([port]) => port));
  }, [processes]);
  const reservedPorts = useMemo(() => new Set(reservedRuntimeEndpointPorts), [reservedRuntimeEndpointPorts]);
  const reservedPortsInUse = useMemo(() => collectReservedRuntimeEndpointPortsInUse({
    processes,
    reservedRuntimeEndpointPorts,
  }), [processes, reservedRuntimeEndpointPorts]);
  const reservedPortsText = useMemo(
    () => formatRuntimeEndpointPortList({ ports: reservedRuntimeEndpointPorts }),
    [reservedRuntimeEndpointPorts],
  );

  const updateProcess = (processIndex: number, nextProcess: RuntimeProcessDefinition) => {
    onChange(processes.map((process, index) => index === processIndex ? nextProcess : process));
  };

  const moveProcess = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= processes.length) return;
    const next = [...processes];
    const [process] = next.splice(index, 1);
    next.splice(nextIndex, 0, process);
    onChange(next);
  };

  const addEndpoint = (processIndex: number) => {
    const process = processes[processIndex];
    if (!process) return;
    const endpoint = createDefaultRuntimeEndpointDefinition({ process });
    updateProcess(processIndex, {
      ...process,
      endpoints: [...process.endpoints, endpoint],
    });
  };

  const updateEndpoint = (
    processIndex: number,
    endpointIndex: number,
    nextEndpoint: RuntimeEndpointDefinition,
  ) => {
    const process = processes[processIndex];
    if (!process) return;
    updateProcess(processIndex, {
      ...process,
      endpoints: process.endpoints.map((endpoint, index) => index === endpointIndex ? nextEndpoint : endpoint),
    });
  };

  const [expandedProcesses, setExpandedProcesses] = useState<Set<string>>(() => {
    // All processes start expanded so existing users see their data
    return new Set(processes.map((p) => p.clientId ?? p.id));
  });

  // When new processes arrive from server data (e.g. after initial load),
  // auto-expand them. Manually collapsed processes are not re-expanded.
  useEffect(() => {
    setExpandedProcesses((prev) => {
      const currentKeys = new Set(processes.map((p) => p.clientId ?? p.id));
      let changed = false;
      const next = new Set(prev);
      for (const key of currentKeys) {
        if (!prev.has(key)) {
          next.add(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [processes]);

  const toggleProcess = useCallback((processKey: string) => {
    setExpandedProcesses((prev) => {
      const next = new Set(prev);
      if (next.has(processKey)) {
        next.delete(processKey);
      } else {
        next.add(processKey);
      }
      return next;
    });
  }, []);

  return (
    <SettingsCard>
      <div className="flex items-start justify-between gap-3">
        <SectionHeader title="Runtime Processes" icon={<Play className="size-3.5" />} />
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              const newProcess = createDefaultRuntimeProcessDefinition({ processes });
              onChange([...processes, newProcess]);
              setExpandedProcesses((prev) => new Set([...prev, newProcess.clientId ?? newProcess.id]));
            }}
            disabled={processes.length >= maxRuntimeServices}
            className="inline-flex items-center gap-1 rounded-lg border border-zinc-700 px-2.5 py-1.5 text-xs font-medium text-zinc-300 transition-colors hover:border-zinc-600 hover:bg-zinc-800/60 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="size-3" />
            Command
          </button>
          <button
            type="button"
            onClick={() => {
              const newProcess = createDefaultDockerComposeRuntimeProcessDefinition({ processes });
              onChange([...processes, newProcess]);
              setExpandedProcesses((prev) => new Set([...prev, newProcess.clientId ?? newProcess.id]));
            }}
            disabled={processes.length >= maxRuntimeServices || !composeAvailable}
            title={!composeAvailable ? "Docker Compose requires Daytona with the Docker-enabled runtime image" : undefined}
            className="inline-flex items-center gap-1 rounded-lg border border-zinc-700 px-2.5 py-1.5 text-xs font-medium text-zinc-300 transition-colors hover:border-zinc-600 hover:bg-zinc-800/60 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="size-3" />
            Compose
          </button>
        </div>
      </div>

      <p className="mt-1.5 text-xs text-zinc-600">
        Each process starts one command (or a Docker Compose stack) and can expose multiple endpoints.
      </p>

      <div className="mt-4 space-y-2">
        {processes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-800 px-4 py-6 text-center text-xs text-zinc-500">
            No runtime processes configured. Add a command or Docker Compose process to get started.
          </div>
        ) : (
          processes.map((process, processIndex) => {
            const isComposeProcess = process.runner.kind === "docker_compose";
            const processKey = process.clientId ?? process.id;
            const isExpanded = expandedProcesses.has(processKey);
            const endpointSummary = process.endpoints.length > 0
              ? process.endpoints.map((ep) => `${ep.name}:${ep.port ?? "?"}`).join(", ")
              : "No endpoints";

            return (
              <div key={processKey} className="rounded-lg border border-zinc-800 bg-zinc-900/40">
                {/* Collapsible header — always visible */}
                <button
                  type="button"
                  onClick={() => toggleProcess(processKey)}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-zinc-800/30"
                >
                  {isExpanded
                    ? <ChevronDown className="size-3.5 shrink-0 text-zinc-500" />
                    : <ChevronRight className="size-3.5 shrink-0 text-zinc-500" />
                  }
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-200 truncate">{process.name}</span>
                      <span className={cn(
                        "shrink-0 rounded-full border px-2 py-px text-xs font-medium",
                        isComposeProcess
                          ? "border-blue-800/40 bg-blue-950/30 text-blue-400"
                          : "border-zinc-700/60 bg-zinc-800/50 text-zinc-400",
                      )}>
                        {isComposeProcess ? "Compose" : "Command"}
                      </span>
                      {process.autoStart && (
                        <span className="shrink-0 rounded-full border border-emerald-800/40 bg-emerald-950/30 px-2 py-px text-xs font-medium text-emerald-400">
                          auto-start
                        </span>
                      )}
                    </div>
                    {!isExpanded && (
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-zinc-500">
                        {process.command && <span className="truncate font-mono">{process.command}</span>}
                        <span className="shrink-0">{endpointSummary}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => moveProcess(processIndex, -1)}
                      disabled={processIndex === 0}
                      className="rounded-md px-1.5 py-1 text-xs text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300 disabled:opacity-30"
                      aria-label="Move up"
                    >
                      <ChevronUp className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveProcess(processIndex, 1)}
                      disabled={processIndex === processes.length - 1}
                      className="rounded-md px-1.5 py-1 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300 disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <ChevronDown className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onChange(processes.filter((_, index) => index !== processIndex))}
                      className="rounded-md px-1.5 py-1 text-zinc-500 transition-colors hover:bg-red-950/40 hover:text-red-400"
                      aria-label="Delete process"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </button>

                {/* Expanded body */}
                {isExpanded && (
                  <div className="border-t border-zinc-800/60 px-4 pb-4 pt-3 space-y-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <FormField
                        icon={<Play className="size-3.5" />}
                        label="Process name"
                        placeholder="e.g. Workspace dev"
                        value={process.name}
                        onChange={(value) => updateProcess(processIndex, { ...process, name: value || `Process ${processIndex + 1}` })}
                      />
                      <FormField
                        icon={<Key className="size-3.5" />}
                        label="Process ID"
                        hint="Stable identifier used by runtime state and logs"
                        placeholder="e.g. workspace-dev"
                        value={process.id}
                        onChange={(value) => updateProcess(processIndex, { ...process, id: value || `process-${processIndex + 1}` })}
                      />
                      <FormField
                        icon={<FolderRoot className="size-3.5" />}
                        label="Working directory"
                        hint={isComposeProcess
                          ? "Optional repo-relative directory where Cord runs the Compose stack"
                          : "Repo-relative subdirectory where this process runs"}
                        placeholder="e.g. apps/web"
                        value={process.workdir ?? ""}
                        onChange={(value) => updateProcess(processIndex, { ...process, workdir: value || null })}
                      />
                      <div>
                        {/* Invisible label spacer — matches FormField's label height so the checkbox aligns with adjacent inputs */}
                        <div className="mb-1.5 text-xs font-medium text-transparent" aria-hidden="true">&nbsp;</div>
                        <label className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-400">
                          <input
                            type="checkbox"
                            checked={process.autoStart}
                            onChange={(event) => updateProcess(processIndex, { ...process, autoStart: event.target.checked })}
                            disabled={!isComposeProcess && !process.command}
                            className="rounded border-zinc-700 bg-zinc-900 text-zinc-200"
                          />
                          Auto-start in new sessions
                        </label>
                      </div>
                    </div>

                    {/* Command or Compose config */}
                    {isComposeProcess && process.runner.kind === "docker_compose" ? (
                      <div className="space-y-3">
                        <div className="rounded-lg border border-sky-800/40 bg-sky-950/20 p-2.5 text-xs text-sky-300/80">
                          Cord manages the Compose lifecycle automatically for this stack.
                        </div>
                        <div>
                          <div className="mb-1.5 flex items-baseline gap-2">
                            <span className="text-xs font-medium text-zinc-300">Docker Compose files</span>
                            <span className="text-xs text-zinc-500">one per line</span>
                          </div>
                          <textarea
                            value={formatDockerComposeFileList(process.runner.files)}
                            onChange={(event) => {
                              const files = parseDockerComposeFileList(event.target.value);
                              const currentRunner = process.runner as RuntimeProcessDockerComposeRunner;
                              updateProcess(processIndex, {
                                ...process,
                                runner: { kind: "docker_compose", files: files.length > 0 ? files : [], projectName: currentRunner.projectName },
                              });
                            }}
                            rows={Math.max(2, process.runner.files.length)}
                            placeholder={"docker-compose.yml\ncompose.yaml"}
                            className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:ring-1 focus:ring-zinc-700"
                            spellCheck={false}
                          />
                          {process.runner.files.length === 0 && (
                            <p className="mt-1.5 text-xs text-amber-300">Add at least one Docker Compose file before saving.</p>
                          )}
                        </div>
                        <FormField
                          icon={<Package className="size-3.5" />}
                          label="Compose project name"
                          hint="Optional stable Docker Compose project name"
                          placeholder="e.g. repo-dev"
                          value={process.runner.projectName ?? ""}
                          onChange={(value) => {
                            const currentRunner = process.runner as RuntimeProcessDockerComposeRunner;
                            updateProcess(processIndex, {
                              ...process,
                              runner: { kind: "docker_compose", files: currentRunner.files, projectName: value || null },
                            });
                          }}
                        />
                      </div>
                    ) : (
                      <FormField
                        icon={<Terminal className="size-3.5" />}
                        label="Command"
                        placeholder="e.g. pnpm turbo dev"
                        value={process.command ?? ""}
                        onChange={(value) => updateProcess(processIndex, {
                          ...process,
                          command: value || null,
                          autoStart: value.trim().length > 0 ? process.autoStart : false,
                        })}
                      />
                    )}

                    {/* Endpoints */}
                    <div className="border-t border-zinc-800/40 pt-3">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs font-medium text-zinc-300">Endpoints</span>
                        <button
                          type="button"
                          onClick={() => addEndpoint(processIndex)}
                          className="inline-flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-300 transition-colors hover:border-zinc-600 hover:bg-zinc-800/60"
                        >
                          <Plus className="size-3" />
                          Add
                        </button>
                      </div>
                      <p className="mb-2 text-xs leading-relaxed text-zinc-600">
                        <span className="font-medium text-zinc-500">Primary</span> and <span className="font-medium text-zinc-500">Exposed</span> ports get browser preview URLs. <span className="font-medium text-zinc-500">Internal</span> ports are only reachable inside the sandbox — if your frontend calls an API on another port, that API should be Exposed.
                      </p>
                      <p className="mb-2 text-xs leading-relaxed text-zinc-600">
                        Cord reserves ports <span className="font-mono text-zinc-500">{reservedPortsText}</span> for internal services like OpenCode, file sidecars, PTY, and VS Code.
                      </p>
                      {reservedPortsInUse.length > 0 && (
                        <p className="mb-2 flex items-start gap-1.5 text-xs leading-relaxed text-amber-400">
                          <AlertCircle className="mt-0.5 size-3 shrink-0" />
                          <span>
                            Update reserved port{reservedPortsInUse.length === 1 ? "" : "s"} <span className="font-mono">{reservedPortsInUse.join(", ")}</span> before saving.
                          </span>
                        </p>
                      )}
                      {process.endpoints.length === 0 ? (
                        <div className="text-xs text-zinc-500">No endpoints yet.</div>
                      ) : (
                        <div>
                          {/* Column headers — shown once */}
                          <div className={cn(
                            "grid gap-x-2 px-1 pb-1",
                            isComposeProcess
                              ? "grid-cols-[1fr_1fr_4.5rem_1fr_4.5rem_7rem_auto]"
                              : "grid-cols-[1fr_1fr_4.5rem_7rem_auto]",
                          )}>
                            <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">Name</span>
                            <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">ID</span>
                            <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">{isComposeProcess ? "Host port" : "Port"}</span>
                            {isComposeProcess && (
                              <>
                                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">Service</span>
                                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">Ctr port</span>
                              </>
                            )}
                            <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">Exposure</span>
                            {/* Spacer for remove button column */}
                            <span />
                          </div>
                          {/* Endpoint rows */}
                          <div className="space-y-1.5">
                            {process.endpoints.map((endpoint, endpointIndex) => {
                              const hasDuplicatePort = endpoint.port !== null && duplicatePorts.has(endpoint.port);
                              const hasReservedPort = endpoint.port !== null && reservedPorts.has(endpoint.port);
                              return (
                                <div key={endpoint.clientId ?? endpoint.id}>
                                  <div className={cn(
                                    "grid items-center gap-x-2",
                                    isComposeProcess
                                      ? "grid-cols-[1fr_1fr_4.5rem_1fr_4.5rem_7rem_auto]"
                                      : "grid-cols-[1fr_1fr_4.5rem_7rem_auto]",
                                  )}>
                                    {/* Name */}
                                    <input
                                      type="text"
                                      value={endpoint.name}
                                      onChange={(e) => updateEndpoint(processIndex, endpointIndex, { ...endpoint, name: e.target.value || `Port ${endpointIndex + 1}` })}
                                      placeholder="e.g. web"
                                      className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:ring-1 focus:ring-zinc-700"
                                    />
                                    {/* ID */}
                                    <input
                                      type="text"
                                      value={endpoint.id}
                                      onChange={(e) => updateEndpoint(processIndex, endpointIndex, { ...endpoint, id: e.target.value || `endpoint-${endpointIndex + 1}` })}
                                      placeholder="e.g. web"
                                      className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:ring-1 focus:ring-zinc-700"
                                    />
                                    {/* Port */}
                                    <input
                                      type="text"
                                      value={formatRuntimeEndpointPort(endpoint.port)}
                                      onChange={(e) => updateEndpoint(processIndex, endpointIndex, {
                                        ...endpoint,
                                        port: parseRuntimeEndpointPort({ value: e.target.value, maxPort: maxRuntimeServicePort }),
                                      })}
                                      placeholder="3000"
                                      className={cn(
                                        "w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:ring-1 focus:ring-zinc-700",
                                        (hasDuplicatePort || hasReservedPort) && "border-amber-500/60 focus:ring-amber-500/60",
                                      )}
                                    />
                                    {/* Compose-only: service name + container port */}
                                    {isComposeProcess && (
                                      <>
                                        <input
                                          type="text"
                                          value={endpoint.composeServiceName ?? ""}
                                          onChange={(e) => updateEndpoint(processIndex, endpointIndex, { ...endpoint, composeServiceName: e.target.value || null })}
                                          placeholder="app"
                                          className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:ring-1 focus:ring-zinc-700"
                                        />
                                        <input
                                          type="text"
                                          value={formatRuntimeEndpointPort(endpoint.composeTargetPort ?? null)}
                                          onChange={(e) => updateEndpoint(processIndex, endpointIndex, { ...endpoint, composeTargetPort: parseDockerComposeTargetPort(e.target.value) })}
                                          placeholder="3000"
                                          className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:ring-1 focus:ring-zinc-700"
                                        />
                                      </>
                                    )}
                                    {/* Exposure */}
                                    <select
                                      value={endpoint.exposure}
                                      onChange={(event) => updateEndpoint(processIndex, endpointIndex, { ...endpoint, exposure: event.target.value as RuntimeEndpointDefinition["exposure"] })}
                                      className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-200 outline-none focus:ring-1 focus:ring-zinc-700"
                                    >
                                      <option value="primary">Primary</option>
                                      <option value="exposed">Exposed</option>
                                      <option value="internal">Internal</option>
                                    </select>
                                    {/* Remove */}
                                    <button
                                      type="button"
                                      onClick={() => updateProcess(processIndex, { ...process, endpoints: process.endpoints.filter((_, index) => index !== endpointIndex) })}
                                      className="rounded-md p-1 text-zinc-600 transition-colors hover:bg-red-950/40 hover:text-red-400"
                                      aria-label="Remove endpoint"
                                    >
                                      <X className="size-3.5" />
                                    </button>
                                  </div>
                                  {hasDuplicatePort && (
                                    <p className="mt-1 pl-1 text-xs text-amber-400">Ports must be unique across all endpoints.</p>
                                  )}
                                  {hasReservedPort && (
                                    <p className="mt-1 pl-1 text-xs text-amber-400">Port {endpoint.port} is reserved by Cord for internal services.</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {processes.length >= maxRuntimeServices && (
        <p className="mt-2 text-xs text-zinc-500">
          Maximum of {maxRuntimeServices} runtime processes per repo.
        </p>
      )}
    </SettingsCard>
  );
}

// ---------------------------------------------------------------------------
// Environment Files Tab
// ---------------------------------------------------------------------------

interface EnvFileEntry {
  id?: string;
  filePath: string;
  content: string;
}


/** Shape of each storage attachment returned by the persistent storage API. */
interface StorageAttachmentSummary {
  id: string;
  name: string;
  kind: "file" | "directory";
  targetPath: string | null;
  autoAttach: boolean;
  autoAttachRepositoryId: string | null;
  latestVersionId: string | null;
  latestVersion: {
    id: string;
    rootRelativePath: string | null;
    objectCount: number;
    totalBytes: number;
    createdAt: string;
  } | null;
}

interface StorageAttachmentsListResponse {
  mountingEnabled: boolean;
  attachments: StorageAttachmentSummary[];
}

// Default repo-scoped mounts into a hidden repo-local subdirectory so the first
// suggested path is unlikely to collide with tracked checkout files. Users can
// still edit the path to any other `/repo/...` location they prefer.
const REPO_SETTINGS_DEFAULT_STORAGE_MOUNT_DIRECTORY = "/repo/.persistent-storage";

// Maximum visible rows in the env-file textarea before it starts scrolling.
// 12 rows ≈ 200px — keeps the section compact while showing a useful amount.
// Users can still resize or scroll within to see more.
const MAX_ENV_TEXTAREA_ROWS = 12;

// Most preview secret files should be conventional dotenv filenames. This regex
// intentionally checks only the final path segment so nested app paths such as
// `apps/web/.env.local` are accepted, while directory/env-var-looking entries
// like `DATABASE_URL` or `apps` get a soft warning instead of a hard block.
const DOTENV_FILE_BASENAME_REGEX = /^\.env(?:\.[A-Za-z0-9_-]+)*$/;

// Byte formatting thresholds for the mounted files size display. Binary units
// match the persistent storage tab for consistency.
const ENV_TAB_BYTES_PER_KIBIBYTE = 1024;
const ENV_TAB_BYTES_PER_MEBIBYTE = ENV_TAB_BYTES_PER_KIBIBYTE * ENV_TAB_BYTES_PER_KIBIBYTE;

function formatMountedFileSize(args: { bytes: number }): string {
  if (args.bytes < ENV_TAB_BYTES_PER_KIBIBYTE) return `${args.bytes} B`;
  if (args.bytes < ENV_TAB_BYTES_PER_MEBIBYTE) {
    return `${(args.bytes / ENV_TAB_BYTES_PER_KIBIBYTE).toFixed(1)} KB`;
  }
  return `${(args.bytes / ENV_TAB_BYTES_PER_MEBIBYTE).toFixed(1)} MB`;
}

function buildRepoSettingsDefaultMountPath(args: { basename: string }): string {
  const sanitizedBasename = args.basename.replace(/[\\/]/g, "").trim() || "attachment";
  return `${REPO_SETTINGS_DEFAULT_STORAGE_MOUNT_DIRECTORY}/${sanitizedBasename}`;
}

function getEnvFilePathWarning(args: { filePath: string }): string | null {
  const trimmedPath = args.filePath.trim();
  if (!trimmedPath) {
    return null;
  }

  const pathSegments = trimmedPath.split("/").filter((segment) => segment.length > 0);
  const basename = pathSegments[pathSegments.length - 1] ?? trimmedPath;
  if (DOTENV_FILE_BASENAME_REGEX.test(basename)) {
    return null;
  }

  return `Most secret env files are named .env, .env.local, or .env.production. Make sure “${trimmedPath}” is a file path your project expects, not an environment variable name or existing directory.`;
}

function buildRepoSettingsDefaultMountPathForAttachment(args: {
  attachment: StorageAttachmentSummary;
}): string {
  const latestVersionPath = args.attachment.kind === "file"
    ? args.attachment.latestVersion?.rootRelativePath?.trim() || ""
    : "";
  const preferredBasename = latestVersionPath.split("/").filter(Boolean).at(-1) || args.attachment.name;
  return buildRepoSettingsDefaultMountPath({ basename: preferredBasename });
}

/**
 * Filter storage attachments to those that will be mounted for a specific repo:
 * - autoAttach is true AND (no repo scope OR scoped to this repo)
 * - Must have a target path and an uploaded version
 */
function filterMountedAttachmentsForRepo(args: {
  attachments: StorageAttachmentSummary[];
  repoId: string;
}): StorageAttachmentSummary[] {
  return args.attachments.filter(
    (attachment) =>
      attachment.autoAttach &&
      attachment.targetPath &&
      attachment.latestVersionId &&
      (attachment.autoAttachRepositoryId === null ||
        attachment.autoAttachRepositoryId === args.repoId),
  );
}

/** Builds the URL for PATCH operations on a storage attachment. */
function buildStorageAttachmentUrl(args: { attachmentId: string }): string {
  return `/api/settings/storage-attachments/${args.attachmentId}`;
}

/**
 * Section container for the Environment tab.
 * Clean zinc-on-zinc — color is reserved for status indicators only.
 */
function EnvironmentSection(args: {
  icon: React.ReactNode;
  title: string;
  subtitle: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
  /** Start expanded by default (true if omitted) */
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(args.defaultOpen ?? true);

  return (
    <div className="rounded-xl border border-zinc-800/60 bg-zinc-900/30 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 px-4 py-3 text-left transition-colors hover:bg-white/[0.02]"
      >
        <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-zinc-800 text-zinc-400">
          {args.icon}
        </div>
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-200">{args.title}</h3>
        {args.badge}
        <ChevronDown
          className={cn(
            "size-3.5 shrink-0 text-zinc-600 transition-transform duration-200",
            !open && "-rotate-90",
          )}
        />
      </button>

      {open && (
        <>
          <div className="mx-4 mb-3 -mt-0.5 text-xs leading-relaxed text-zinc-500">
            {args.subtitle}
          </div>
          <div className="mx-4 h-px bg-zinc-800/50" />
          <div className="p-4">{args.children}</div>
        </>
      )}
    </div>
  );
}

const EnvFilesTab = forwardRef<TabSaveHandle, { repoId: string; onDirtyChange?: (dirty: boolean) => void }>(
  function EnvFilesTab({ repoId, onDirtyChange }, ref) {
  const queryClient = useQueryClient();

  // ── Secret files (env vars) ─────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ["env-files", repoId],
    queryFn: () =>
      apiFetch(`/api/repos/${repoId}/env-files`) as Promise<EnvFileEntry[]>,
  });

  const [files, setFiles] = useState<EnvFileEntry[]>([]);
  const [dirty, setDirty] = useState(false);

  // ── Mounted files (persistent storage) ──────────────────────────────
  const { data: storageData, isLoading: storageLoading, error: storageError } = useQuery({
    queryKey: ["env-tab-storage-attachments", repoId],
    queryFn: () =>
      apiFetch<StorageAttachmentsListResponse>("/api/settings/storage-attachments"),
  });

  const mountedAttachments = useMemo(() => {
    if (!storageData?.attachments) return [];
    return filterMountedAttachmentsForRepo({
      attachments: storageData.attachments,
      repoId,
    });
  }, [storageData, repoId]);

  /** Attachments in storage that are currently "stored only" — not auto-mounted
   *  anywhere. These can be claimed as repo-scoped mounts from this tab. */
  const availableToMount = useMemo(() => {
    if (!storageData?.attachments) return [];
    return storageData.attachments.filter(
      (a) => !a.autoAttach && a.latestVersionId,
    );
  }, [storageData]);

  // ── Inline mount editing state ──────────────────────────────────────
  const [mountPickerOpen, setMountPickerOpen] = useState(false);
  const [pendingMount, setPendingMount] = useState<{
    attachmentId: string;
    name: string;
    kind: "file" | "directory";
    targetPath: string;
  } | null>(null);
  const [busyAttachmentId, setBusyAttachmentId] = useState<string | null>(null);
  const [editingMountId, setEditingMountId] = useState<string | null>(null);
  const [editingMountPath, setEditingMountPath] = useState("");

  // Notify parent when dirty state changes so OnboardingFooter can react.
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (data) {
      if (dirty) return;
      setFiles(data.map((f) => ({ filePath: f.filePath, content: f.content })));
      setDirty(false);
    }
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/api/repos/${repoId}/env-files`, {
        method: "PUT",
        body: JSON.stringify({ files }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["env-files", repoId] });
      setDirty(false);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to save environment files");
    },
  });

  // ── Mount mutations ─────────────────────────────────────────────────

  const refreshStorageAttachments = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["env-tab-storage-attachments", repoId] });
  }, [queryClient, repoId]);

  /** Mount a stored-only attachment for this repo at a given path. */
  const mountAttachment = useMutation({
    mutationFn: (args: { attachmentId: string; targetPath: string }) =>
      apiFetch(buildStorageAttachmentUrl({ attachmentId: args.attachmentId }), {
        method: "PATCH",
        body: JSON.stringify({
          autoAttach: true,
          autoAttachRepositoryId: repoId,
          targetPath: args.targetPath,
        }),
      }),
    onSuccess: () => {
      refreshStorageAttachments();
      setPendingMount(null);
      setMountPickerOpen(false);
      toast.success("Persistent Storage mounted for this repo");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to mount file");
    },
    onSettled: () => setBusyAttachmentId(null),
  });

  /** Unmount a repo-scoped attachment (returns it to stored-only). */
  const unmountAttachment = useMutation({
    mutationFn: (args: { attachmentId: string }) =>
      apiFetch(buildStorageAttachmentUrl({ attachmentId: args.attachmentId }), {
        method: "PATCH",
        body: JSON.stringify({ autoAttach: false }),
      }),
    onSuccess: () => {
      refreshStorageAttachments();
      toast.success("Persistent Storage unmounted from this repo");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to unmount file");
    },
    onSettled: () => setBusyAttachmentId(null),
  });

  /** Update the mount path for a repo-scoped attachment. */
  const updateMountPath = useMutation({
    mutationFn: (args: { attachmentId: string; targetPath: string }) =>
      apiFetch(buildStorageAttachmentUrl({ attachmentId: args.attachmentId }), {
        method: "PATCH",
        body: JSON.stringify({ targetPath: args.targetPath }),
      }),
    onSuccess: () => {
      refreshStorageAttachments();
      setEditingMountId(null);
      toast.success("Mount path updated");
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to update path");
    },
    onSettled: () => setBusyAttachmentId(null),
  });

  // Expose imperative save handle for the onboarding footer.
  useImperativeHandle(ref, () => ({
    save: async () => {
      if (!dirty) return true;
      try {
        await save.mutateAsync();
        return true;
      } catch {
        return false;
      }
    },
    isDirty: dirty,
  }), [dirty, save]);

  const addFile = () => {
    setFiles((prev) => [...prev, { filePath: "", content: "" }]);
    setDirty(true);
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const updateFile = (idx: number, key: keyof EnvFileEntry, value: string) => {
    setFiles((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, [key]: value } : f))
    );
    setDirty(true);
  };

  const discardChanges = () => {
    if (data) {
      setFiles(data.map((f) => ({ filePath: f.filePath, content: f.content })));
      setDirty(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-zinc-500">
        <Loader2 className="mr-2 size-4 animate-spin" />
        Loading environment…
      </div>
    );
  }

  // Whether any mounted-files mutation is in progress.
  const isMountBusy = mountAttachment.isPending || unmountAttachment.isPending || updateMountPath.isPending;

  return (
    <div className="space-y-6">
      {/* ── Section 1: Secret files ─────────────────────────────────── */}
      <EnvironmentSection
        icon={<Shield className="size-3.5" />}
        title="Secret Files"
        subtitle={
          <>
            <span className="text-zinc-400">Encrypted at rest</span> and written to the path you specify inside every new sandbox.
            Ideal for <code className="rounded bg-zinc-800/60 px-1 py-0.5 font-mono text-xs text-zinc-400">.env</code> files, API keys, and credentials.
          </>
        }
      >
        {files.length === 0 ? (
          /* ── Empty state ── */
          <div className="flex flex-col items-center py-8">
            <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-zinc-800/60 text-zinc-600">
              <FileKey2 className="size-5" />
            </div>
            <p className="text-sm font-medium text-zinc-300">No secret files yet</p>
            <p className="mt-1.5 max-w-[300px] text-center text-xs leading-relaxed text-zinc-600">
              Add encrypted .env files that are injected into every sandbox session for this repository.
            </p>
            <button
              onClick={addFile}
              className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
            >
              <Plus className="size-3.5" />
              Add secret file
            </button>
          </div>
        ) : (
          /* ── File entries ── */
          <div className="space-y-4">
            <div className="space-y-3">
              {files.map((file, idx) => {
                const envFilePathWarning = getEnvFilePathWarning({ filePath: file.filePath });
                return (
                  <div
                    key={idx}
                    className="group/file rounded-lg border border-zinc-800/60 bg-zinc-950/30 overflow-hidden transition-all duration-200 hover:border-zinc-700/60"
                  >
                      {/* File header */}
                      <div className="flex items-center gap-2.5 bg-zinc-900/50 px-3.5 py-2.5">
                        <FileKey2 className="size-3.5 shrink-0 text-zinc-500" />
                        <input
                          type="text"
                          value={file.filePath}
                          onChange={(e) => updateFile(idx, "filePath", e.target.value)}
                          placeholder="File path, e.g. .env or apps/web/.env.local"
                          className="flex-1 bg-transparent font-mono text-sm text-zinc-200 placeholder-zinc-600 outline-none"
                        />
                        <button
                          onClick={() => removeFile(idx)}
                          className="rounded-md p-1.5 text-zinc-700 transition-all duration-200 hover:bg-red-950/40 hover:text-red-400 opacity-0 group-hover/file:opacity-100 focus:opacity-100"
                          title="Remove file"
                          aria-label={`Remove ${file.filePath || "environment file"}`}
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>

                      {/* Resolved path hint */}
                      {file.filePath && (
                        <div className="border-t border-zinc-800/30 bg-zinc-950/20 px-3.5 py-1.5">
                          <p className="text-xs text-zinc-600">
                            Written to{" "}
                            <span className="font-mono text-zinc-500">/repo/{file.filePath}</span>
                          </p>
                        </div>
                      )}

                      {envFilePathWarning && (
                        <div role="alert" className="flex items-start gap-2 border-t border-amber-500/20 bg-amber-500/10 px-3.5 py-2.5 text-xs leading-relaxed text-amber-200/90">
                          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-300" />
                          <span>{envFilePathWarning}</span>
                        </div>
                      )}

                      {/* Content textarea — cap visible rows so the section stays compact.
                         MAX_ENV_TEXTAREA_ROWS = 12: enough to see a typical .env without
                         the section ballooning; user can still scroll or resize within. */}
                      <textarea
                        value={file.content}
                        onChange={(e) => updateFile(idx, "content", e.target.value)}
                        placeholder={"DATABASE_URL=postgres://...\nAPI_KEY=sk-..."}
                        rows={Math.min(MAX_ENV_TEXTAREA_ROWS, Math.max(3, file.content.split("\n").length + 1))}
                        className="w-full border-t border-zinc-800/30 bg-zinc-950/40 px-3.5 py-3 font-mono text-xs leading-relaxed text-zinc-300 placeholder-zinc-700 outline-none resize-y max-h-[200px]"
                        spellCheck={false}
                      />
                  </div>
                );
              })}
            </div>

            {/* Add another file — dashed button */}
            <button
              onClick={addFile}
              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-zinc-800 px-3 py-2 text-xs font-medium text-zinc-500 transition-all duration-200 hover:border-zinc-600 hover:bg-zinc-900/50 hover:text-zinc-300"
            >
              <Plus className="size-3" />
              Add another file
            </button>

            {/* Unsaved changes bar */}
            {dirty && (
              <div className="flex items-center gap-3 rounded-lg border border-zinc-700/60 bg-zinc-800/40 px-4 py-2.5">
                <div className="size-1.5 shrink-0 rounded-full bg-amber-400" />
                <span className="flex-1 text-xs text-zinc-400">Unsaved changes</span>
                <button
                  type="button"
                  onClick={discardChanges}
                  className="rounded-md px-3 py-1.5 text-xs text-zinc-500 transition-colors hover:text-zinc-300"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={() => save.mutate()}
                  disabled={save.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-4 py-1.5 text-xs font-medium text-zinc-900 transition-colors hover:bg-white disabled:opacity-50"
                >
                  {save.isPending ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : null}
                  {save.isPending ? "Saving…" : "Save"}
                </button>
              </div>
            )}

            {/* Save error */}
            {save.isError && (
              <div className="flex items-center gap-2 text-xs text-red-400">
                <AlertCircle className="size-3 shrink-0" />
                {(save.error as Error).message}
              </div>
            )}

            {/* Success confirmation */}
            {save.isSuccess && !dirty && (
              <div className="flex items-center gap-2 text-xs text-emerald-400/70">
                <Check className="size-3 shrink-0" />
                Changes saved
              </div>
            )}
          </div>
        )}
      </EnvironmentSection>

      {/* ── Section 2: Mounted files ────────────────────────────────── */}
      <EnvironmentSection
        icon={<HardDrive className="size-3.5" />}
        title="Mounted Files"
        subtitle={
          <>
            Files from{" "}
            <Link
              to="/settings"
              search={{ tab: "persistent-storage" as any }}
              className="text-zinc-400 underline underline-offset-2 decoration-zinc-700 hover:text-zinc-300 transition-colors"
            >
              Persistent Storage
            </Link>
            {" "}mounted <span className="text-zinc-400">read-write</span> into sandboxes. Changes apply to new sandboxes only.
          </>
        }
        badge={
          mountedAttachments.length > 0 ? (
            <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-xs font-medium tabular-nums text-zinc-400">
              {mountedAttachments.length}
            </span>
          ) : null
        }
      >
        {storageLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-zinc-500">
            <Loader2 className="mr-2 size-4 animate-spin" />
            Loading storage…
          </div>
        ) : storageError ? (
          <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/[0.04] px-4 py-3 text-sm text-red-300">
            <AlertCircle className="size-4 shrink-0" />
            <span>{storageError instanceof Error ? storageError.message : "Failed to load mounted files"}</span>
          </div>
        ) : mountedAttachments.length === 0 && !mountPickerOpen && !pendingMount ? (
          /* ── Empty state ── */
          <div className="flex flex-col items-center py-8">
            <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-zinc-800/60 text-zinc-600">
              <HardDrive className="size-5" />
            </div>
            <p className="text-sm font-medium text-zinc-300">No files mounted</p>
            <p className="mt-1.5 max-w-[300px] text-center text-xs leading-relaxed text-zinc-600">
              Mount files from Persistent Storage into every sandbox — great for shared configs, Claude settings, or local data.
            </p>
            <div className="mt-5 flex items-center gap-3">
              {availableToMount.length > 0 && (
                <button
                  onClick={() => setMountPickerOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
                >
                  <Plus className="size-3.5" />
                  Mount from storage
                </button>
              )}
              <Link
                to="/settings"
                search={{ tab: "persistent-storage" as any }}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                  availableToMount.length > 0
                    ? "border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:bg-zinc-800/50"
                    : "bg-zinc-100 text-zinc-900 hover:bg-zinc-200",
                )}
              >
                <ExternalLink className="size-3.5" />
                {availableToMount.length > 0 ? "Upload more" : "Go to Persistent Storage"}
              </Link>
            </div>
          </div>
        ) : (
          /* ── Mounted files list ── */
          <div className="space-y-3">
            {mountedAttachments.length > 0 && (
              <div className="rounded-lg border border-zinc-800/60 overflow-hidden divide-y divide-zinc-800/40">
                {mountedAttachments.map((attachment) => {
                  const isRepoScoped = attachment.autoAttachRepositoryId === repoId;
                  const isBusy = busyAttachmentId === attachment.id;
                  const isEditingPath = editingMountId === attachment.id;

                  return (
                    <div
                      key={attachment.id}
                      className={cn(
                        "flex items-center gap-3 px-4 py-3.5 transition-all duration-200",
                        isBusy ? "opacity-50" : "hover:bg-white/[0.02]",
                      )}
                    >
                      {/* Kind icon */}
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-zinc-800 text-zinc-400">
                        {attachment.kind === "file" ? (
                          <File className="size-4" />
                        ) : (
                          <FolderOpen className="size-4" />
                        )}
                      </div>

                      {/* Info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-zinc-200">
                            {attachment.name}
                          </span>
                          {isBusy && (
                            <Loader2 className="size-3 shrink-0 animate-spin text-zinc-500" />
                          )}
                          <span className={cn(
                            "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                            isRepoScoped
                              ? "bg-zinc-800/80 text-zinc-400"
                              : "bg-zinc-800/80 text-zinc-500 italic",
                          )}>
                            {isRepoScoped ? "this repo" : "all repos"}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-zinc-600">
                          <span>{attachment.kind === "file" ? "File" : "Folder"}</span>
                          {attachment.latestVersion && (
                            <>
                              <span className="text-zinc-800">·</span>
                              <span>
                                {attachment.latestVersion.objectCount}{" "}
                                {attachment.latestVersion.objectCount === 1 ? "file" : "files"}
                              </span>
                              <span className="text-zinc-800">·</span>
                              <span>{formatMountedFileSize({ bytes: attachment.latestVersion.totalBytes })}</span>
                            </>
                          )}
                          {!isRepoScoped && (
                            <>
                              <span className="text-zinc-800">·</span>
                              <span className="italic">Managed globally</span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Mount path + status */}
                      <div className="hidden shrink-0 items-center gap-2.5 sm:flex">
                        {isEditingPath ? (
                          <form
                            className="flex items-center gap-1.5"
                            onSubmit={(e) => {
                              e.preventDefault();
                              if (!editingMountPath.trim()) return;
                              setBusyAttachmentId(attachment.id);
                              updateMountPath.mutate({
                                attachmentId: attachment.id,
                                targetPath: editingMountPath.trim(),
                              });
                            }}
                          >
                            <input
                              type="text"
                              value={editingMountPath}
                              onChange={(e) => setEditingMountPath(e.target.value)}
                              className="w-[200px] rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 font-mono text-xs text-zinc-200 outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-700"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                  setEditingMountId(null);
                                }
                              }}
                            />
                            <button
                              type="submit"
                              disabled={isBusy || !editingMountPath.trim()}
                              className="rounded-md p-1.5 text-emerald-400 transition-colors hover:bg-emerald-500/10 disabled:opacity-40"
                              title="Save"
                            >
                              <Check className="size-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingMountId(null)}
                              className="rounded-md p-1.5 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
                              title="Cancel"
                            >
                              <X className="size-3.5" />
                            </button>
                          </form>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={!isRepoScoped || isMountBusy}
                              onClick={isRepoScoped ? () => {
                                setEditingMountId(attachment.id);
                                setEditingMountPath(attachment.targetPath ?? "");
                              } : undefined}
                              className={cn(
                                "group/path flex items-center gap-1.5 max-w-[240px] truncate rounded-md px-2.5 py-1.5 text-xs font-mono transition-all duration-200",
                                isRepoScoped
                                  ? "bg-zinc-800/40 text-zinc-300 hover:bg-zinc-800/70 cursor-pointer"
                                  : "bg-zinc-800/30 text-zinc-500 cursor-default",
                              )}
                              title={isRepoScoped ? "Click to edit mount path" : "Managed in Persistent Storage settings"}
                            >
                              <span className="truncate">{attachment.targetPath}</span>
                              {isRepoScoped && (
                                <Pencil className="size-2.5 shrink-0 text-zinc-600 opacity-0 transition-opacity duration-200 group-hover/path:opacity-100" />
                              )}
                            </button>
                            <span className="flex items-center gap-1.5 rounded-full bg-emerald-500/[0.06] px-2 py-0.5 text-xs font-medium text-emerald-400/80">
                              <span className="size-1.5 rounded-full bg-emerald-400/70" />
                              Mounted
                            </span>
                          </>
                        )}
                      </div>

                      {/* Remove button — repo-scoped only */}
                      {isRepoScoped && !isEditingPath && (
                        <button
                          type="button"
                          disabled={isBusy || isMountBusy}
                          onClick={() => {
                            setBusyAttachmentId(attachment.id);
                            unmountAttachment.mutate({ attachmentId: attachment.id });
                          }}
                          className="rounded-md p-1.5 text-zinc-700 transition-all duration-200 hover:bg-red-950/40 hover:text-red-400 disabled:opacity-40"
                          title="Unmount from this repo"
                        >
                          <X className="size-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pending mount — configuring path for a newly selected attachment */}
            {pendingMount && (
              <div className="rounded-lg border border-zinc-700/60 bg-zinc-900/40 overflow-hidden">
                <form
                  className="px-4 py-3.5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!pendingMount.targetPath.trim()) return;
                    setBusyAttachmentId(pendingMount.attachmentId);
                    mountAttachment.mutate({
                      attachmentId: pendingMount.attachmentId,
                      targetPath: pendingMount.targetPath.trim(),
                    });
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-zinc-700 bg-zinc-800/50 text-zinc-400">
                      {pendingMount.kind === "file" ? (
                        <File className="size-4" />
                      ) : (
                        <FolderOpen className="size-4" />
                      )}
                    </div>
                    <p className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-200">{pendingMount.name}</p>
                  </div>
                  <div className="mt-2.5 flex items-center gap-2 pl-11">
                    <label className="shrink-0 text-xs font-medium text-zinc-500">Mount at</label>
                    <input
                      type="text"
                      value={pendingMount.targetPath}
                      onChange={(e) => setPendingMount((prev) => prev ? { ...prev, targetPath: e.target.value } : null)}
                      placeholder="/repo/.persistent-storage/..."
                      className="min-w-0 flex-1 rounded-md border border-zinc-700 bg-zinc-900 px-2.5 py-1.5 font-mono text-xs text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 focus:ring-1 focus:ring-zinc-700"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setPendingMount(null);
                        }
                      }}
                    />
                    <button
                      type="submit"
                      disabled={mountAttachment.isPending || !pendingMount.targetPath.trim()}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-zinc-100 px-3.5 py-1.5 text-xs font-medium text-zinc-900 transition-colors hover:bg-white disabled:opacity-50"
                    >
                      {mountAttachment.isPending ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <Check className="size-3" />
                      )}
                      Mount
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingMount(null)}
                      disabled={mountAttachment.isPending}
                      className="shrink-0 rounded-md p-1.5 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Mount picker — choose from available stored-only attachments */}
            {mountPickerOpen && !pendingMount && (
              <div className="rounded-lg border border-zinc-800/60 bg-zinc-900/30 overflow-hidden">
                <div className="border-b border-zinc-800/40 px-4 py-3">
                  <p className="text-xs font-medium text-zinc-300">Select from Persistent Storage</p>
                  <p className="mt-0.5 text-xs text-zinc-600">Choose a file or folder to mount into this repo.</p>
                </div>
                {availableToMount.length === 0 ? (
                  <div className="px-4 py-6 text-center">
                    <p className="text-xs text-zinc-500 mb-2">
                      No unmounted files available.
                    </p>
                    <Link
                      to="/settings"
                      search={{ tab: "persistent-storage" as any }}
                      className="text-xs text-zinc-400 underline underline-offset-2 decoration-zinc-700 hover:text-zinc-300 transition-colors"
                    >
                      Upload files in Persistent Storage
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800/30">
                    {availableToMount.map((attachment) => (
                      <button
                        key={attachment.id}
                        type="button"
                        onClick={() => {
                          setPendingMount({
                            attachmentId: attachment.id,
                            name: attachment.name,
                            kind: attachment.kind,
                            targetPath: buildRepoSettingsDefaultMountPathForAttachment({ attachment }),
                          });
                        }}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-all duration-200 hover:bg-white/[0.02]"
                      >
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-zinc-700/50 bg-zinc-800/50 text-zinc-400">
                          {attachment.kind === "file" ? (
                            <File className="size-3.5" />
                          ) : (
                            <FolderOpen className="size-3.5" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-zinc-200">{attachment.name}</span>
                          <span className="text-xs text-zinc-600">
                            {attachment.kind === "file" ? "File" : "Folder"}
                            {attachment.latestVersion && (
                              <> · {formatMountedFileSize({ bytes: attachment.latestVersion.totalBytes })}</>
                            )}
                          </span>
                        </div>
                        <ChevronRight className="size-3.5 shrink-0 text-zinc-700" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="border-t border-zinc-800/40 px-4 py-2.5 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setMountPickerOpen(false)}
                    className="text-xs text-zinc-500 transition-colors hover:text-zinc-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Action buttons below mounted list */}
            {!mountPickerOpen && !pendingMount && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMountPickerOpen(true)}
                  disabled={isMountBusy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-zinc-800 px-3 py-2 text-xs font-medium text-zinc-500 transition-all duration-200 hover:border-zinc-600 hover:bg-zinc-900/50 hover:text-zinc-300 disabled:opacity-40"
                >
                  <Plus className="size-3" />
                  Mount from storage
                </button>
                <Link
                  to="/settings"
                  search={{ tab: "persistent-storage" as any }}
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-600 transition-colors hover:text-zinc-400"
                >
                  <HardDrive className="size-3" />
                  Manage Persistent Storage
                  <ExternalLink className="size-2.5" />
                </Link>
              </div>
            )}
          </div>
        )}
      </EnvironmentSection>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Build Status Tab
// ---------------------------------------------------------------------------

type BuildStatus = RepoBuildStatus;

interface ImageBuild extends RepoBuildRecord {
  /** Sandbox runtime id for the running build sandbox. */
  buildSandboxId?: string | null;
  /** Current build phase label (e.g. "clone", "install", "snapshot"). */
  buildPhase?: string | null;
  /** ISO timestamp — when the current build phase started. */
  buildPhaseStartedAt?: string | null;
  /** ISO timestamp — last time the sandbox provider reported any progress. */
  buildLastProgressAt?: string | null;
}

interface BuildsResponse {
  builds: ImageBuild[];
  currentScopeBuilds: ImageBuild[];
  currentBaseImageHash: string | null;
  currentSandboxProviderId: string | null;
  defaultBranch: string | null;
  buildHealth?: {
    status: "failing" | "blocked";
    consecutiveFailures: number;
    lastFailureMessage: string | null;
    lastFailurePhase: string | null;
    lastFailedAt: string | null;
    blockedAt: string | null;
  } | null;
}

interface CancelBuildResponse {
  id: string;
  status: "cancelled";
  providerCancelWarning: string | null;
}

interface RebuildBuildResponse {
  id: string;
  repo: string;
  branch: string;
  status: "pending";
}

interface BuildEventRecord {
  id: number;
  kind: "phase" | "log" | "note" | "status";
  phase: string | null;
  providerPhase: string | null;
  stream: "stdout" | "stderr" | "system" | null;
  message: string | null;
  phaseStartedAt: string | null;
  sandboxId: string | null;
  payloadJson: Record<string, unknown> | null;
  createdAt: string;
}

interface BuildEventsResponse {
  buildId: string;
  events: BuildEventRecord[];
  latestId: number;
  hasMore: boolean;
  /** Present on tail/beforeId responses: true when older events exist before the first returned event. */
  hasOlderEvents?: boolean;
}

interface LogLine {
  id: number;
  text: string;
  createdAt: string;
}

// If the rebuilt row does not show up after a successful mutation, clear the
// optimistic spinner so a transient polling miss does not leave the page stuck.
const REBUILD_APPEAR_TIMEOUT_MS = 30 * 1000;

// Poll live build events every 3s. This keeps expanded logs live without making
// active builds one of the app's most aggressive repeated HTTP readers.
const BUILD_EVENTS_POLL_INTERVAL_MS = 3 * 1000;

// Request up to 100 events per poll to match API-side limits and catch up
// quickly after brief tab inactivity.
const BUILD_EVENTS_FETCH_LIMIT = 100;

// Fetch at most two pages per tick so catch-up is bounded even when build output
// is very noisy; the next timer continues from the latest persisted cursor.
const BUILD_EVENTS_MAX_PAGES_PER_TICK = 2;

// Consider the viewer "at the tail" when the user is within one or two text
// lines of the bottom. This keeps follow-mode stable despite fractional layout
// rounding while still letting users scroll up without being pulled back down.
const BUILD_LOG_TAIL_FOLLOW_THRESHOLD_PX = 32;

// Maximum number of log lines kept in the live viewer's in-memory buffer. Lines
// are trimmed from the front (oldest) so the user always sees the freshest output.
// 5 000 lines × ~100 chars ≈ 500 KB — acceptable for a single open log panel.
// Users can scroll up and click "Load older events" to access earlier history.
const LIVE_BUILD_LOG_MAX_LINES = 5000;

// How long to wait before retrying a failed initial tail fetch. Long enough to
// avoid hammering a temporarily-overloaded API, short enough to feel responsive.
const LIVE_BUILD_TAIL_RETRY_DELAY_MS = 3000;

// Terminal logs are persisted once at build completion; cache briefly to avoid
// stale forever behavior across in-flight -> terminal transitions.
const TERMINAL_BUILD_LOG_STALE_TIME_MS = 30 * 1000;

// Persisted terminal logs can land slightly after the build status flips out of
// "building" because result processing and list polling are separate requests.
// Retry briefly so the UI does not get stuck on a one-time null read.
const TERMINAL_BUILD_LOG_RETRY_INTERVAL_MS = 1500;
const TERMINAL_BUILD_LOG_RETRY_TIMEOUT_MS = 30 * 1000;

function formatDuration(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remSecs = secs % 60;
  return `${mins}m ${remSecs}s`;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(ms / 1000);
  if (secs < 10) return "just now";
  if (secs < 600) {
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    if (mins === 0) return `${secs}s ago`;
    return rem === 0 ? `${mins}m ago` : `${mins}m ${rem}s ago`;
  }
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * Human-readable labels for provider-neutral build progress phases.
 * Falls back to the raw phase string if no mapping exists.
 */
const BUILD_PHASE_LABELS: Record<string, string> = {
  create_sandbox: "Creating sandbox",
  clone_repository: "Cloning repository",
  install_dependencies: "Installing dependencies",
  run_build_command: "Running build command",
  run_build_hooks: "Running build hooks",
  compute_dependency_hash: "Computing dependency hash",
  prepare_runtime: "Preparing runtime",
  snapshot_workspace: "Snapshotting filesystem",
  capture_file_tree: "Capturing file tree",
  read_commit_sha: "Reading commit SHA",
  cleanup: "Cleaning up",
};

function getBuildPhaseLabel(args: {
  phase: string | null | undefined;
  sandboxProviderId?: string | null;
}): string | null {
  if (!args.phase) return null;
  if (args.sandboxProviderId === "daytona" && args.phase === "snapshot_workspace") {
    return "Daytona snapshot";
  }
  return BUILD_PHASE_LABELS[args.phase] ?? args.phase;
}

function getBuildEventPhaseLabel(event: BuildEventRecord): string {
  if (event.phase === "snapshot_workspace" && event.providerPhase?.startsWith("snapshot_")) {
    return "Daytona snapshot";
  }

  return getBuildPhaseLabel({ phase: event.phase, sandboxProviderId: null }) ?? "Build";
}

/** Format elapsed time as a compact "Xm Ys" or "Xs" string. */
function formatElapsed(startIso: string): string {
  const ms = Date.now() - new Date(startIso).getTime();
  if (ms < 0) return "0s";
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remSecs = secs % 60;
  return remSecs === 0 ? `${mins}m` : `${mins}m ${remSecs}s`;
}

/**
 * Threshold after which we warn that no progress has been received.
 * Two minutes without a progress callback usually means the current
 * step is either very slow or hung.
 */
const PROGRESS_STALE_THRESHOLD_MS = 2 * 60 * 1000;

function useBuildLog(args: { buildId: string | null; enabled: boolean }) {
  return useQuery({
    queryKey: ["build-log", args.buildId],
    queryFn: () =>
      apiFetch(`/api/builds/${args.buildId}/log`) as Promise<{ buildLog: string | null }>,
    enabled: args.enabled && !!args.buildId,
    staleTime: TERMINAL_BUILD_LOG_STALE_TIME_MS,
  });
}

function formatBuildEventLine(event: BuildEventRecord): string {
  const timestamp = new Date(event.createdAt).toISOString().slice(11, 19);
  const message = event.message || "";
  if (event.kind === "log") {
    return `[${timestamp}] ${message}`;
  }
  const kindLabel = event.kind.toUpperCase();
  const phaseLabel = getBuildEventPhaseLabel(event);
  const providerDetail =
    event.providerPhase && event.providerPhase !== event.phase
      ? ` [${event.providerPhase}]`
      : "";
  return `[${timestamp}] [${kindLabel}] [${phaseLabel}]${providerDetail} ${message}`;
}

function isBuildLogScrolledToTail(args: {
  element: HTMLElement;
  thresholdPx: number;
}): boolean {
  const distanceFromBottom =
    args.element.scrollHeight - args.element.clientHeight - args.element.scrollTop;
  return distanceFromBottom <= args.thresholdPx;
}

function scrollBuildLogToTail(args: { element: HTMLElement }) {
  args.element.scrollTop = args.element.scrollHeight;
}

function BuildLogViewer(args: { logText: string }) {
  const preRef = useRef<HTMLPreElement | null>(null);
  const shouldFollowTailRef = useRef(true);

  useEffect(() => {
    const element = preRef.current;
    if (!element || !shouldFollowTailRef.current) {
      return;
    }

    scrollBuildLogToTail({ element });
  }, [args.logText]);

  return (
    <pre
      ref={preRef}
      onScroll={(event) => {
        shouldFollowTailRef.current = isBuildLogScrolledToTail({
          element: event.currentTarget,
          thresholdPx: BUILD_LOG_TAIL_FOLLOW_THRESHOLD_PX,
        });
      }}
      className="max-h-64 overflow-auto whitespace-pre-wrap text-xs leading-relaxed text-zinc-400 font-mono"
    >
      {args.logText}
    </pre>
  );
}

/**
 * Live build log viewer.
 *
 * Behaviour:
 *  - Opens scrolled to the bottom (most-recent events visible immediately).
 *  - Follows the tail automatically as new events arrive, unless the user has
 *    scrolled up to read older lines.
 *  - Shows a "Load older events" button at the top of the scrollable area when
 *    the server says there are events before the oldest one currently loaded.
 *  - Preserves the user's reading position when older events are prepended so
 *    the view does not jump.
 */
function LiveBuildLogViewer(args: {
  lines: LogLine[];
  hasOlderEvents: boolean;
  isLoadingOlder: boolean;
  onLoadOlderEvents: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shouldFollowTailRef = useRef(true);
  // Snapshot of scrollHeight taken when the user clicks "Load older".
  const prevScrollHeightRef = useRef<number | null>(null);
  // ID of lines[0] at the moment "Load older" was clicked.  Used to tell apart
  // a genuine prepend (lines[0].id changed) from a concurrent poll append
  // (lines[0].id unchanged) so the scroll compensation is never misapplied.
  const pendingPrependFirstIdRef = useRef<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (prevScrollHeightRef.current !== null && pendingPrependFirstIdRef.current !== null) {
      const prependArrived = args.lines[0]?.id !== pendingPrependFirstIdRef.current;
      if (prependArrived) {
        // Older events arrived — restore the reading position.
        container.scrollTop += container.scrollHeight - prevScrollHeightRef.current;
        prevScrollHeightRef.current = null;
        pendingPrependFirstIdRef.current = null;
      } else {
        // A poll appended new lines while loadOlder is still in-flight.
        // Don't apply the height compensation; honour tail-follow as usual.
        if (shouldFollowTailRef.current) {
          container.scrollTop = container.scrollHeight;
        }
      }
    } else if (shouldFollowTailRef.current) {
      // Normal case: new events appended — keep following the tail.
      container.scrollTop = container.scrollHeight;
    }
  }, [args.lines]);

  const handleLoadOlder = () => {
    const container = containerRef.current;
    if (container && args.lines.length > 0) {
      // Snapshot both the height and the current first-line ID before the state
      // update so the effect above can detect the prepend unambiguously.
      prevScrollHeightRef.current = container.scrollHeight;
      pendingPrependFirstIdRef.current = args.lines[0].id;
    }
    args.onLoadOlderEvents();
  };

  const logText = useMemo(
    () => args.lines.map((line) => line.text).join("\n"),
    [args.lines],
  );

  return (
    <div
      ref={containerRef}
      onScroll={(event) => {
        shouldFollowTailRef.current = isBuildLogScrolledToTail({
          element: event.currentTarget,
          thresholdPx: BUILD_LOG_TAIL_FOLLOW_THRESHOLD_PX,
        });
      }}
      className="max-h-64 overflow-auto"
    >
      {(args.hasOlderEvents || args.isLoadingOlder) && (
        <div className="flex items-center justify-center py-1.5 text-xs text-zinc-600">
          {args.isLoadingOlder ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="size-2.5 animate-spin" />
              Loading older events…
            </span>
          ) : (
            <button
              onClick={handleLoadOlder}
              className="flex items-center gap-1 transition-colors hover:text-zinc-400"
            >
              ↑ Load older events
            </button>
          )}
        </div>
      )}
      <pre className="whitespace-pre-wrap text-xs leading-relaxed text-zinc-400 font-mono">
        {logText}
      </pre>
    </div>
  );
}

function isDocumentVisible(): boolean {
  if (typeof document === "undefined") {
    return true;
  }
  return document.visibilityState !== "hidden";
}

function useBuildEvents(args: { buildId: string | null; enabled: boolean }) {
  const [lines, setLines] = useState<LogLine[]>([]);
  const [hasOlderEvents, setHasOlderEvents] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refs carry mutable cursors across async operations without stale-closure problems.
  const maxIdRef = useRef(0); // forward-poll cursor: latest id seen
  // 0 is the sentinel meaning "unknown/unset". loadOlderEvents is guarded by
  // this sentinel so it never fires a beforeId=0 request against the API.
  const minIdRef = useRef(0); // backward-pagination cursor: oldest id loaded
  const initialLoadDoneRef = useRef(false);
  const isLoadingOlderRef = useRef(false); // ref mirror of isLoadingOlder for the click-handler guard

  // Reset everything when the user switches to a different build.
  useEffect(() => {
    setLines([]);
    setHasOlderEvents(false);
    setIsLoadingInitial(false);
    setIsLoadingOlder(false);
    setLastEventAt(null);
    setError(null);
    maxIdRef.current = 0;
    minIdRef.current = 0;
    initialLoadDoneRef.current = false;
    isLoadingOlderRef.current = false;
  }, [args.buildId]);

  // Initial tail load + live-polling loop.
  useEffect(() => {
    if (!args.enabled || !args.buildId) {
      return;
    }

    const buildId = args.buildId;
    let cancelled = false;
    let pollInFlight = false;
    // Local retry counter — up to 3 attempts before showing a permanent error.
    // This handles transient network blips without requiring the user to re-open the panel.
    let tailRetries = 0;
    const MAX_TAIL_RETRIES = 3;

    const loadTail = async () => {
      setIsLoadingInitial(true);
      try {
        const response = await apiFetch<BuildEventsResponse>(
          `/api/builds/${buildId}/events?tail=true&limit=${BUILD_EVENTS_FETCH_LIMIT}`,
        );
        if (cancelled) return;

        const initialLines: LogLine[] = response.events.map((event) => ({
          id: event.id,
          text: formatBuildEventLine(event),
          createdAt: event.createdAt,
        }));

        setLines(initialLines);
        setHasOlderEvents(response.hasOlderEvents ?? false);
        maxIdRef.current = response.latestId;
        minIdRef.current = initialLines.length > 0 ? initialLines[0].id : 0;

        if (initialLines.length > 0) {
          setLastEventAt(initialLines[initialLines.length - 1].createdAt);
        }

        initialLoadDoneRef.current = true;
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load build events");
          if (tailRetries < MAX_TAIL_RETRIES) {
            tailRetries++;
            window.setTimeout(() => {
              if (!cancelled) void loadTail();
            }, LIVE_BUILD_TAIL_RETRY_DELAY_MS);
          }
        }
      } finally {
        if (!cancelled) setIsLoadingInitial(false);
      }
    };

    const poll = async () => {
      if (cancelled || pollInFlight || !initialLoadDoneRef.current || !isDocumentVisible()) {
        return;
      }

      pollInFlight = true;
      try {
        let afterId = maxIdRef.current;
        let pageCount = 0;
        let hasMore = true;

        while (hasMore && pageCount < BUILD_EVENTS_MAX_PAGES_PER_TICK) {
          const response = await apiFetch<BuildEventsResponse>(
            `/api/builds/${buildId}/events?afterId=${afterId}&limit=${BUILD_EVENTS_FETCH_LIMIT}`,
          );
          if (cancelled) return;

          pageCount += 1;
          hasMore = response.hasMore;
          afterId = response.latestId;
          // Update the cursor inside the loop so a cancelled early-return never
          // loses progress — the next poll will continue from the right position.
          maxIdRef.current = afterId;

          if (response.events.length > 0) {
            const newLines: LogLine[] = response.events.map((event) => ({
              id: event.id,
              text: formatBuildEventLine(event),
              createdAt: event.createdAt,
            }));

            setLines((previous) => {
              const combined = [...previous, ...newLines];
              if (combined.length > LIVE_BUILD_LOG_MAX_LINES) {
                // Trim from the front (oldest) to stay within the cap. Mutating
                // minIdRef here is safe — refs are plain mutable objects outside
                // React's rendering model.
                const trimmed = combined.slice(combined.length - LIVE_BUILD_LOG_MAX_LINES);
                minIdRef.current = trimmed[0].id;
                return trimmed;
              }
              return combined;
            });

            // If minIdRef is still the sentinel, the poll beat the initial tail
            // fetch (e.g. build had no events at mount time). Set it now so
            // "Load older events" can work once history accumulates.
            if (minIdRef.current === 0) {
              minIdRef.current = newLines[0].id;
            }

            setLastEventAt(response.events[response.events.length - 1].createdAt);
          }
        }

        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to fetch build events");
        }
      } finally {
        pollInFlight = false;
      }
    };

    // Kick off the tail load, then immediately start polling for new events.
    void loadTail().then(() => {
      if (!cancelled) void poll();
    });

    const timerId = window.setInterval(() => void poll(), BUILD_EVENTS_POLL_INTERVAL_MS);
    const handleVisibilityChange = () => {
      if (isDocumentVisible()) void poll();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      // Reset the initial-load gate so re-enabling for the same buildId starts
      // a fresh tail fetch rather than jumping straight into polling.
      initialLoadDoneRef.current = false;
      window.clearInterval(timerId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [args.enabled, args.buildId]);

  // Called by the viewer when the user clicks "Load older events".
  // Stable across renders: all mutable state is read from refs, and the only
  // closure dep is args.buildId which changes at the same time the hook resets.
  const loadOlderEvents = useCallback(() => {
    if (
      !args.buildId ||
      !initialLoadDoneRef.current ||
      isLoadingOlderRef.current ||
      // 0 is the sentinel — no events loaded yet, nothing older to fetch.
      minIdRef.current === 0
    ) {
      return;
    }

    const buildId = args.buildId;
    const beforeId = minIdRef.current;

    isLoadingOlderRef.current = true;
    setIsLoadingOlder(true);

    void apiFetch<BuildEventsResponse>(
      `/api/builds/${buildId}/events?beforeId=${beforeId}&limit=${BUILD_EVENTS_FETCH_LIMIT}`,
    )
      .then((response) => {
        if (response.events.length > 0) {
          const olderLines: LogLine[] = response.events.map((event) => ({
            id: event.id,
            text: formatBuildEventLine(event),
            createdAt: event.createdAt,
          }));
          setLines((previous) => [...olderLines, ...previous]);
          minIdRef.current = olderLines[0].id;
          setHasOlderEvents(response.hasOlderEvents ?? false);
        } else {
          setHasOlderEvents(false);
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Failed to load older events");
      })
      .finally(() => {
        isLoadingOlderRef.current = false;
        setIsLoadingOlder(false);
      });
  }, [args.buildId]);

  return {
    lines,
    hasOlderEvents,
    isLoadingInitial,
    isLoadingOlder,
    loadOlderEvents,
    lastEventAt,
    error,
  };
}

function useNow(intervalMs: number, enabled = true) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, enabled]);
  return now;
}

function BuildStatusTab({ repoId }: { repoId: string }) {
  const queryClient = useQueryClient();
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [pendingRebuildId, setPendingRebuildId] = useState<string | null>(null);

  const {
    data: buildsResponse,
    isLoading,
    isError: isBuildsError,
    error: buildsError,
  } = useQuery({
    queryKey: ["builds", repoId],
    queryFn: () =>
      apiFetch(`/api/builds?repoId=${repoId}`) as Promise<BuildsResponse>,
    refetchInterval: (query) => {
      const data = query.state.data?.builds;
      const hasInFlight = data?.some(
        (b) => b.status === "pending" || b.status === "building"
      );
      return (hasInFlight || isRebuilding) ? 5000 : false;
    },
  });

  // Build-settings lookup so the "Build now" / "Rebuild" buttons can be
  // disabled when the repo has no saved dev-environment config. Without
  // config a build would just clone the repo and snapshot the base image —
  // the server now rejects those requests too, this just gives clearer UX.
  // Shares the same cache key as the Build Config tab so switching tabs
  // doesn't refetch.
  const {
    data: buildSettingsData,
    isLoading: buildSettingsLoading,
  } = useQuery<BuildSettingsResponse>({
    queryKey: ["build-settings", repoId],
    queryFn: () => apiFetch(`/api/repos/${repoId}/build-settings`),
  });
  const hasBuildConfig = useMemo(() => {
    if (!buildSettingsData) {
      // Optimistically assume config exists while loading so the button isn't
      // momentarily disabled then enabled for a configured repo.
      return true;
    }
    const rootDirectory = buildSettingsData.rootDirectory?.trim() ?? "";
    const installCommand = buildSettingsData.installCommand?.trim() ?? "";
    const buildCommand = buildSettingsData.buildCommand?.trim() ?? "";
    const hooks = Array.isArray(buildSettingsData.buildHooks)
      ? buildSettingsData.buildHooks
      : [];
    const runtimeProcesses = Array.isArray(buildSettingsData.runtimeProcesses)
      ? buildSettingsData.runtimeProcesses
      : [];
    const hasRunnableRuntimeProcess = runtimeProcesses.some((process) => {
      if (process.runner.kind === "docker_compose") {
        return process.runner.files.length > 0;
      }
      return (process.command?.trim() ?? "").length > 0;
    });
    return (
      rootDirectory.length > 0 ||
      installCommand.length > 0 ||
      buildCommand.length > 0 ||
      hooks.length > 0 ||
      hasRunnableRuntimeProcess
    );
  }, [buildSettingsData]);

  const rebuild = useMutation({
    mutationFn: () =>
      apiFetch<RebuildBuildResponse>(`/api/builds/repo/${repoId}/rebuild`, { method: "POST" }),
    onMutate: () => {
      setIsRebuilding(true);
    },
    onSuccess: (result) => {
      setPendingRebuildId(result.id);
      queryClient.invalidateQueries({ queryKey: ["builds", repoId] });
      // Keep isRebuilding=true until the new build row appears in the list.
    },
    onError: () => {
      setIsRebuilding(false);
      setPendingRebuildId(null);
      queryClient.invalidateQueries({ queryKey: ["builds", repoId] });
    },
  });

  const cancelBuild = useMutation({
    mutationFn: (buildId: string) =>
      apiFetch<CancelBuildResponse>(`/api/builds/${buildId}/cancel`, { method: "POST" }),
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Failed to cancel build.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["builds", repoId] });
    },
  });

  const allBuilds = buildsResponse?.builds || [];
  const currentScopeBuilds = buildsResponse?.currentScopeBuilds || [];
  useInvalidateReposQueryWhenBuildsSettle({ builds: currentScopeBuilds, enabled: true });
  const expandedBuild = allBuilds.find((build) => build.id === expandedLog) ?? null;
  const isExpandedBuildInFlight = !!(
    expandedBuild &&
    (expandedBuild.status === "pending" || expandedBuild.status === "building")
  );
  const logQuery = useBuildLog({
    buildId: expandedLog,
    enabled: !!expandedLog && !isExpandedBuildInFlight,
  });
  const refetchBuildLog = logQuery.refetch;
  const liveEventsQuery = useBuildEvents({
    buildId: expandedLog,
    enabled: isExpandedBuildInFlight,
  });
  const lastLiveEventAt = liveEventsQuery.lastEventAt;
  const currentBaseImageHash = buildsResponse?.currentBaseImageHash ?? null;
  const currentSandboxProviderId = buildsResponse?.currentSandboxProviderId ?? null;
  const {
    activeImage,
    inFlight,
    history,
    hasInFlightBuilds,
    hasRepeatedFailures,
  } = deriveRepoBuildsViewModel({
    allBuilds,
    currentScopeBuilds,
  });
  // Disable the rebuild buttons while a build is already happening, while we
  // haven't finished loading config, OR when the repo has no dev-environment
  // config to build. The empty-config case has its own UI treatment further
  // down so users know exactly why the button is disabled.
  const rebuildBlockedReason: "in-flight" | "loading-config" | "no-config" | null = hasInFlightBuilds
    ? "in-flight"
    : buildSettingsLoading
      ? "loading-config"
      : !hasBuildConfig
        ? "no-config"
        : null;
  const rebuildDisabled =
    rebuild.isPending || isRebuilding || rebuildBlockedReason !== null;

  // Once an in-flight build appears, we can clear the optimistic "rebuilding" state
  useEffect(() => {
    const rebuildHasMaterialized =
      !!pendingRebuildId &&
      (buildsResponse?.builds?.some((build) => build.id === pendingRebuildId) ?? false);

    if (isRebuilding && (hasInFlightBuilds || rebuildHasMaterialized)) {
      setIsRebuilding(false);
      if (rebuildHasMaterialized) {
        setPendingRebuildId(null);
      }
    }
  }, [isRebuilding, hasInFlightBuilds, pendingRebuildId, buildsResponse?.builds]);

  useEffect(() => {
    if (!isRebuilding) return;

    const timeoutId = window.setTimeout(() => {
      setIsRebuilding(false);
      setPendingRebuildId(null);
    }, REBUILD_APPEAR_TIMEOUT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isRebuilding]);

  const wasExpandedBuildInFlightRef = useRef(false);
  useEffect(() => {
    const wasInFlight = wasExpandedBuildInFlightRef.current;
    wasExpandedBuildInFlightRef.current = isExpandedBuildInFlight;

    if (wasInFlight && !isExpandedBuildInFlight && expandedLog) {
      const startedAt = Date.now();
      let cancelled = false;

      const retryUntilAvailable = async () => {
        while (!cancelled && Date.now() - startedAt < TERMINAL_BUILD_LOG_RETRY_TIMEOUT_MS) {
          const result = await refetchBuildLog();
          if (result.data?.buildLog !== null && result.data?.buildLog !== undefined) {
            return;
          }
          await new Promise((resolve) => window.setTimeout(resolve, TERMINAL_BUILD_LOG_RETRY_INTERVAL_MS));
        }
      };

      void retryUntilAvailable();
      return () => {
        cancelled = true;
      };
    }
  }, [isExpandedBuildInFlight, expandedLog, refetchBuildLog]);

  // Force re-renders so timeAgo() and phase elapsed times stay fresh
  // while builds are in progress.  5 seconds keeps the phase timer
  // visually smooth without excessive re-renders.
  const NOW_REFRESH_INTERVAL_MS = 5_000;
  useNow(NOW_REFRESH_INTERVAL_MS, inFlight.length > 0 || isRebuilding);

   if (isLoading) {
    return (
      <div className="space-y-6">
        <SettingsCard>
          <div className="flex items-center justify-center py-8 text-sm text-zinc-500">
            <Loader2 className="mr-2 size-4 animate-spin" />
            Loading builds...
          </div>
        </SettingsCard>
      </div>
    );
  }

  if (isBuildsError && !buildsResponse) {
    return (
      <div className="space-y-6">
        <SettingsCard>
          <div className="py-8 text-sm text-red-400">
            {buildsError instanceof Error ? buildsError.message : "Failed to load builds."}
          </div>
        </SettingsCard>
      </div>
    );
  }

  // Detect stale active image: the image was built against an older baked build
  // base, so its installed toolchains/runtime layer may be behind the current deploy.
  const isActiveImageStale = !!(
    activeImage &&
    currentBaseImageHash &&
    activeImage.baseImageHash !== currentBaseImageHash
  );

  return (
    <div className="space-y-6">
      {currentSandboxProviderId && (
        <div className="text-xs text-zinc-500">
          Provider: <SandboxProviderBadge providerId={currentSandboxProviderId} />
        </div>
      )}

      {isBuildsError && buildsResponse && (
        <div className="rounded-xl border border-amber-800/40 bg-amber-950/20 px-4 py-3 text-xs text-amber-300">
          Showing last known build state. Live refresh failed.
        </div>
      )}

      {/* Build health circuit breaker banner */}
      {buildsResponse?.buildHealth?.status === "blocked" && (
        <div className="rounded-xl border border-red-800/40 bg-red-950/20 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-400" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-300">
                Builds are paused
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-zinc-400">
                The last {buildsResponse.buildHealth.consecutiveFailures} builds failed
                {buildsResponse.buildHealth.lastFailurePhase
                  ? ` during ${buildsResponse.buildHealth.lastFailurePhase}`
                  : ""}
                . Automatic rebuilds are paused until the issue is fixed.
              </p>
              {buildsResponse.buildHealth.lastFailureMessage && (
                <p className="mt-1.5 truncate rounded bg-zinc-900/60 px-2 py-1 font-mono text-[11px] text-red-300/70">
                  {buildsResponse.buildHealth.lastFailureMessage}
                </p>
              )}
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={() => {
                    rebuild.mutate();
                    setIsRebuilding(true);
                  }}
                  disabled={rebuildDisabled}
                  className="inline-flex items-center gap-1.5 rounded-md bg-red-900/30 px-3 py-1.5 text-xs font-medium text-red-200 transition-colors hover:bg-red-900/50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Reset & Rebuild
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Failure banner (softer warning when not yet blocked) */}
      {buildsResponse?.buildHealth?.status === "failing" && (
        <div className="rounded-xl border border-amber-800/40 bg-amber-950/20 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
            <div>
              <p className="text-sm font-medium text-amber-300">
                Latest build failed
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-zinc-400">
                One more failure will pause automatic builds. Check your build settings or expand the failed build below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Legacy failure banner — shown only when health data is unavailable */}
      {hasRepeatedFailures && !buildsResponse?.buildHealth && (
        <div className="rounded-xl border border-amber-800/40 bg-amber-950/20 px-4 py-3">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
            <div>
              <p className="text-sm font-medium text-amber-300">
                Recent builds are failing
              </p>
              <p className="mt-0.5 text-xs leading-relaxed text-zinc-400">
                Check your build settings or expand a failed build below to see what went wrong.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Current active image */}
      {activeImage ? (
        <SettingsCard className={isActiveImageStale ? "border-amber-800/30 bg-amber-950/10" : "border-emerald-800/30 bg-emerald-950/10"}>
          <div className="flex items-start gap-3">
            <div className={cn(
              "flex size-8 shrink-0 items-center justify-center rounded-full ring-1",
              isActiveImageStale
                ? "bg-amber-950/50 ring-amber-800/40"
                : "bg-emerald-950/50 ring-emerald-800/40"
            )}>
              {isActiveImageStale ? (
                <AlertTriangle className="size-4 text-amber-400" />
              ) : (
                <CheckCircle2 className="size-4 text-emerald-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-200">
                    {isActiveImageStale ? "Image outdated" : "Image ready"}
                  </p>
                  {isActiveImageStale && (
                    <p className="text-xs text-amber-400/80 mt-0.5">
                      Built against an older baked runtime base. Rebuild to pick up the latest tooling layer.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() =>
                      setExpandedLog(expandedLog === activeImage.id ? null : activeImage.id)
                    }
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-400 transition-colors hover:border-zinc-600 hover:bg-zinc-800/50 hover:text-zinc-300"
                    title="Toggle build log"
                  >
                    <Terminal className="size-3" />
                    {expandedLog === activeImage.id ? "Hide logs" : "Logs"}
                  </button>
                  <button
                    onClick={() => rebuild.mutate()}
                    disabled={rebuildDisabled}
                    title={
                      rebuildBlockedReason === "no-config"
                        ? "Configure install/build commands, hooks, a root directory, or runtime processes before rebuilding."
                        : undefined
                    }
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-400 transition-colors hover:border-zinc-600 hover:bg-zinc-800/50 hover:text-zinc-300 disabled:opacity-40"
                  >
                    {rebuild.isPending || isRebuilding ? (
                      <Loader2 className="size-3 animate-spin" />
                    ) : (
                      <RefreshCw className="size-3" />
                    )}
                    {hasInFlightBuilds ? "Build in progress" : "Rebuild"}
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-zinc-500">
                <SandboxProviderBadge providerId={activeImage.sandboxProviderId} />
                {activeImage.branch && (
                  <span className="font-mono text-zinc-400">
                    {activeImage.branch}
                  </span>
                )}
                {activeImage.commitSha && (
                  <span className="font-mono">
                    {activeImage.commitSha.slice(0, 7)}
                  </span>
                )}
                <span>{timeAgo(activeImage.createdAt)}</span>
                {activeImage.buildStartedAt &&
                  activeImage.buildCompletedAt && (
                    <span className="flex items-center gap-1">
                      <Clock className="size-3" />
                      {formatDuration(
                        activeImage.buildStartedAt,
                        activeImage.buildCompletedAt
                      )}
                    </span>
                  )}
              </div>
            </div>
          </div>

          {expandedLog === activeImage.id && (
            <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-950/50 p-3">
              {isExpandedBuildInFlight ? (
                <>
                  <div className="mb-2 flex items-center justify-between text-xs text-zinc-500">
                    <span>Live build events</span>
                    {lastLiveEventAt && <span>last event {timeAgo(lastLiveEventAt)}</span>}
                  </div>
                  {liveEventsQuery.isLoadingInitial ? (
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                      <Loader2 className="size-3 animate-spin" />
                      Waiting for live events...
                    </div>
                  ) : liveEventsQuery.lines.length > 0 ? (
                    <LiveBuildLogViewer
                      key={expandedLog}
                      lines={liveEventsQuery.lines}
                      hasOlderEvents={liveEventsQuery.hasOlderEvents}
                      isLoadingOlder={liveEventsQuery.isLoadingOlder}
                      onLoadOlderEvents={liveEventsQuery.loadOlderEvents}
                    />
                  ) : (
                    <p className="text-xs text-zinc-600">No live output yet.</p>
                  )}
                  {liveEventsQuery.error && (
                    <p className="mt-2 text-xs text-amber-400/80">{liveEventsQuery.error}</p>
                  )}
                </>
              ) : logQuery.isLoading ? (
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <Loader2 className="size-3 animate-spin" />
                  Loading log...
                </div>
              ) : logQuery.data?.buildLog ? (
                <BuildLogViewer logText={logQuery.data.buildLog} />
              ) : (
                <p className="text-xs text-zinc-600">No build log available.</p>
              )}
            </div>
          )}
        </SettingsCard>
      ) : rebuildBlockedReason === "no-config" ? (
        <SettingsCard className="text-center py-12 px-6">
          <Package className="mx-auto mb-4 size-8 text-zinc-600" />
          <p className="text-base font-medium text-zinc-300 mb-3">
            Nothing to build yet
          </p>
          <p className="text-sm text-zinc-500 mb-6 max-w-sm mx-auto leading-relaxed">
            Configure an install command, build command, build hooks, a root
            directory, or runtime processes first. Without any of those a build
            would just clone your repo onto the base image — no faster than
            starting fresh each time.
          </p>
          <Link
            to="/$repoId/settings"
            params={{ repoId }}
            search={{ onboarding: false, tab: "build" } as never}
            className="inline-flex items-center gap-2 rounded-lg bg-zinc-100 px-5 py-2.5 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
          >
            Set up build config
          </Link>
          <p className="mt-4 text-xs text-zinc-600">
            Threads still work without a prebuilt image — they just boot from
            the bare base image.
          </p>
        </SettingsCard>
      ) : (
        <SettingsCard className="text-center py-12 px-6">
          <Package className="mx-auto mb-4 size-8 text-zinc-600" />
          <p className="text-base font-medium text-zinc-300 mb-3">No prebuilt image yet</p>
          <p className="text-sm text-zinc-500 mb-6 max-w-sm mx-auto leading-relaxed">
            A prebuilt image saves your dependencies so new threads start in seconds.
            Without one, cold-start threads boot from the base image with your
            repo cloned in, but dependencies and previews stay cold until a build finishes.
          </p>
          <button
            onClick={() => rebuild.mutate()}
            disabled={rebuildDisabled}
            className="inline-flex items-center gap-2 rounded-lg bg-zinc-100 px-5 py-2.5 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200 disabled:opacity-40"
          >
            {rebuild.isPending || isRebuilding ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            {hasInFlightBuilds ? "Build in progress" : "Build now"}
          </button>
          <p className="mt-4 text-xs text-zinc-600">
            You can skip this — threads work without it, they&apos;re just slower to start.
          </p>
        </SettingsCard>
      )}
      {rebuild.isError && (
        <p className="text-xs text-red-400">
          {(rebuild.error as Error).message}
        </p>
      )}

      {/* In-flight builds */}
      {inFlight.length > 0 && (
        <div className="space-y-2">
          {inFlight.map((build) => {
            const ageMs = Date.now() - new Date(build.createdAt).getTime();
            // Use progress heartbeat staleness when available, fall back
            // to overall build age for backwards compatibility.
            const progressStaleSinceMs = build.buildLastProgressAt
              ? Date.now() - new Date(build.buildLastProgressAt).getTime()
              : null;
            const isProgressStale =
              progressStaleSinceMs !== null &&
              progressStaleSinceMs > PROGRESS_STALE_THRESHOLD_MS;
            const isOverallStale = ageMs > 5 * 60 * 1000;
            const isStale = isProgressStale || (isOverallStale && !build.buildPhase);
            const phaseLabel = getBuildPhaseLabel({
              phase: build.buildPhase,
              sandboxProviderId: build.sandboxProviderId,
            });
            return (
              <SettingsCard
                key={build.id}
                className={cn(
                  isStale
                    ? "border-amber-800/30 bg-amber-950/10"
                    : "border-blue-800/30 bg-blue-950/10"
                )}
              >
                <div className="flex items-center gap-3">
                  <Loader2 className={cn("size-4 animate-spin", isStale ? "text-amber-400" : "text-blue-400")} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-200">
                      {build.status === "pending"
                        ? "Build queued"
                        : phaseLabel
                          ? `Building — ${phaseLabel}`
                          : "Building image..."}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs text-zinc-500">
                      <SandboxProviderBadge providerId={build.sandboxProviderId} />
                      {build.branch && (
                        <span className="font-mono text-zinc-400">
                          {build.branch}
                        </span>
                      )}
                      <span>started {timeAgo(build.createdAt)}</span>
                      {build.buildPhaseStartedAt && build.buildPhase && (
                        <span className="flex items-center gap-1">
                          <Clock className="size-3" />
                          {formatElapsed(build.buildPhaseStartedAt)}
                        </span>
                      )}
                      {build.buildSandboxId && (
                        <span
                          className="font-mono text-zinc-700"
                          title={build.buildSandboxId}
                        >
                          {build.buildSandboxId.slice(0, 12)}…
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setExpandedLog(expandedLog === build.id ? null : build.id)
                    }
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-400 transition-colors hover:border-zinc-600 hover:bg-zinc-800/50 hover:text-zinc-300"
                    title="Toggle live logs"
                  >
                    <Terminal className="size-3" />
                    {expandedLog === build.id ? "Hide logs" : "Logs"}
                  </button>
                  <button
                    onClick={() => cancelBuild.mutate(build.id)}
                    disabled={cancelBuild.isPending}
                    className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-400 transition-colors hover:border-red-800/60 hover:bg-red-950/20 hover:text-red-400 disabled:opacity-40"
                    title="Cancel build"
                  >
                    <X className="size-3" />
                    Cancel
                  </button>
                </div>
                {isProgressStale && build.buildPhase && build.buildLastProgressAt && (
                  <p className="mt-2 text-xs text-amber-400/80">
                    No output received for {formatElapsed(build.buildLastProgressAt)}.
                    The <span className="font-mono">{phaseLabel ?? build.buildPhase}</span> step may be hung — consider cancelling.
                  </p>
                )}
                {!isProgressStale && isOverallStale && !build.buildPhase && (
                  <p className="mt-2 text-xs text-amber-400/80">
                    This build is taking longer than usual. You may want to cancel and try again.
                  </p>
                )}
                {build.statusMessage && (
                  <p className="mt-2 text-xs text-zinc-500">
                    {build.statusMessage}
                  </p>
                )}
                {expandedLog === build.id && (
                  <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-950/50 p-3">
                    <div className="mb-2 flex items-center justify-between text-xs text-zinc-500">
                      <span>Live build events</span>
                      {lastLiveEventAt && <span>last event {timeAgo(lastLiveEventAt)}</span>}
                    </div>
                    {liveEventsQuery.isLoadingInitial ? (
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <Loader2 className="size-3 animate-spin" />
                        Waiting for live events...
                      </div>
                    ) : liveEventsQuery.lines.length > 0 ? (
                      <LiveBuildLogViewer
                        key={expandedLog}
                        lines={liveEventsQuery.lines}
                        hasOlderEvents={liveEventsQuery.hasOlderEvents}
                        isLoadingOlder={liveEventsQuery.isLoadingOlder}
                        onLoadOlderEvents={liveEventsQuery.loadOlderEvents}
                      />
                    ) : (
                      <p className="text-xs text-zinc-600">No live output yet.</p>
                    )}
                    {liveEventsQuery.error && (
                      <p className="mt-2 text-xs text-amber-400/80">{liveEventsQuery.error}</p>
                    )}
                  </div>
                )}
              </SettingsCard>
            );
          })}
        </div>
      )}

      {/* Build history */}
      {history.length > 0 && (
        <div>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-1.5 text-xs font-medium text-zinc-500 transition-colors hover:text-zinc-400"
          >
            {showHistory ? (
              <ChevronDown className="size-3" />
            ) : (
              <ChevronRight className="size-3" />
            )}
            Past builds ({history.length})
          </button>

          {showHistory && (
            <div className="mt-3 rounded-xl border border-zinc-800/60 divide-y divide-zinc-800/40">
              {history.slice(0, 20).map((build) => (
                <div key={build.id} className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <BuildStatusDot status={build.status} />
                    <div className="flex flex-1 items-center gap-2 min-w-0 text-xs">
                      <SandboxProviderBadge providerId={build.sandboxProviderId} />
                      <BuildStatusBadge status={build.status} />
                      {build.branch && (
                        <span className="font-mono text-zinc-400">
                          {build.branch}
                        </span>
                      )}
                      {build.commitSha && (
                        <span className="font-mono text-zinc-600">
                          {build.commitSha.slice(0, 7)}
                        </span>
                      )}
                      <span className="text-zinc-600">
                        {timeAgo(build.createdAt)}
                      </span>
                      {build.buildStartedAt && build.buildCompletedAt && (
                        <span className="flex items-center gap-1 text-zinc-600">
                          <Clock className="size-3" />
                          {formatDuration(
                            build.buildStartedAt,
                            build.buildCompletedAt
                          )}
                        </span>
                      )}
                    </div>
                    {(build.status === "ready" ||
                      build.status === "superseded" ||
                      build.status === "failed" ||
                      build.status === "cancelled") && (
                      <button
                        onClick={() =>
                          setExpandedLog(
                            expandedLog === build.id ? null : build.id
                          )
                        }
                        className="rounded-md p-1 text-zinc-600 transition-colors hover:bg-zinc-800 hover:text-zinc-400"
                        title="Toggle build log"
                      >
                        {expandedLog === build.id ? (
                          <ChevronDown className="size-3" />
                        ) : (
                          <ChevronRight className="size-3" />
                        )}
                      </button>
                    )}
                  </div>

                  {expandedLog === build.id && (
                    <div className="mt-2 rounded-lg border border-zinc-800 bg-zinc-950/50 p-3">
                      {build.errorMessage && (
                        <div className="mb-2 text-xs text-red-400">
                          {build.errorMessage}
                        </div>
                      )}
                      {logQuery.isLoading ? (
                        <div className="flex items-center gap-2 text-xs text-zinc-500">
                          <Loader2 className="size-3 animate-spin" />
                          Loading log...
                        </div>
                      ) : logQuery.data?.buildLog ? (
                        <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-xs leading-relaxed text-zinc-400 font-mono">
                          {logQuery.data.buildLog}
                        </pre>
                      ) : (
                        <p className="text-xs text-zinc-600">
                          No build log available.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status components
// ---------------------------------------------------------------------------

function BuildStatusDot({ status }: { status: BuildStatus }) {
  const colors: Record<BuildStatus, string> = {
    ready: "bg-emerald-400",
    superseded: "bg-emerald-400",
    failed: "bg-red-400",
    cancelled: "bg-amber-400",
    building: "bg-blue-400",
    pending: "bg-zinc-500",
  };
  return (
    <span
      className={cn(
        "inline-block size-1.5 shrink-0 rounded-full",
        colors[status] || "bg-zinc-600"
      )}
    />
  );
}

function BuildStatusBadge({ status }: { status: BuildStatus }) {
  const config: Record<BuildStatus, { label: string; className: string }> = {
    ready: {
      label: "Ready",
      className: "border-emerald-800/60 bg-emerald-950/30 text-emerald-400",
    },
    building: {
      label: "Building",
      className: "border-blue-800/60 bg-blue-950/30 text-blue-400",
    },
    pending: {
      label: "Pending",
      className: "border-zinc-700/60 bg-zinc-800/30 text-zinc-400",
    },
    failed: {
      label: "Failed",
      className: "border-red-800/60 bg-red-950/30 text-red-400",
    },
    cancelled: {
      label: "Cancelled",
      className: "border-amber-800/60 bg-amber-950/30 text-amber-400",
    },
    superseded: {
      label: "Superseded",
      className: "border-zinc-700/60 bg-zinc-800/30 text-zinc-500",
    },
  };
  const c = config[status] || config.pending;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        c.className
      )}
    >
      {status === "building" && (
        <Loader2 className="mr-1 size-3 animate-spin" />
      )}
      {c.label}
    </span>
  );
}

function SandboxProviderBadge({ providerId }: { providerId: string }) {
  const label = SANDBOX_PROVIDER_LABELS[providerId] ?? providerId;
  const className = providerId === "daytona"
    ? "border-cyan-800/60 bg-cyan-950/30 text-cyan-300"
    : providerId === "freestyle"
      ? "border-violet-800/60 bg-violet-950/30 text-violet-300"
      : "border-fuchsia-800/60 bg-fuchsia-950/30 text-fuchsia-300";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        className,
      )}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers for JSON config paste detection
// ---------------------------------------------------------------------------

const SECRET_HEADER_PATTERNS_RE = /(?:api[_-]?key|token|secret|auth|password|bearer|credential)/i;
/** Matches clearly placeholder values that need to be replaced by the user. */
const PLACEHOLDER_VALUE_RE = /^(?:YOUR[_-]|<.*>$|\*{3,}$)/i;
/** Matches real token prefixes — indicates the value IS a secret, but already filled in (not a placeholder). */
const TOKEN_PREFIX_RE = /^(?:sk[_-]|ghp_|ghu_|ghs_|xoxb[_-]|xoxp[_-])/i;

interface DetectedSecretHeader {
  name: string;
  value: string;
  /** true when the value is empty or a placeholder like YOUR_API_KEY / <token> */
  isPlaceholder: boolean;
}

interface DetectedServer {
  key: string;
  url: string;
  enabled: boolean;
  secretHeaders: DetectedSecretHeader[];
  plainHeaders: Record<string, string>;
}

/** Mask a secret value for display: show first few + last few chars. */
function maskSecretValue(value: string): string {
  if (value.length <= 8) return "••••••••";
  const visibleStart = Math.min(6, Math.floor(value.length / 4));
  const visibleEnd = Math.min(4, Math.floor(value.length / 6));
  return value.slice(0, visibleStart) + "••••" + value.slice(-visibleEnd);
}

function detectServersFromJson(jsonStr: string): { servers: DetectedServer[]; error?: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonStr);
  } catch (e) {
    const msg = e instanceof SyntaxError ? e.message : "Invalid JSON";
    return { servers: [], error: `Invalid JSON: ${msg}` };
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { servers: [], error: "JSON must be an object, not an array or primitive value." };
  }

  const obj = parsed as Record<string, unknown>;
  let serversMap: Record<string, unknown> = {};

  if (obj.mcp && typeof obj.mcp === "object" && !Array.isArray(obj.mcp)) {
    serversMap = obj.mcp as Record<string, unknown>;
  } else if (obj.mcpServers && typeof obj.mcpServers === "object" && !Array.isArray(obj.mcpServers)) {
    serversMap = obj.mcpServers as Record<string, unknown>;
  } else {
    const entries = Object.entries(obj);
    const looksLikeBare = entries.length > 0 && entries.every(
      ([, v]) => v && typeof v === "object" && !Array.isArray(v),
    );
    if (looksLikeBare) {
      serversMap = obj as Record<string, unknown>;
    } else {
      return {
        servers: [],
        error: 'Could not find MCP servers in the pasted config. Expected format: { "mcp": { "server-name": { "url": "..." } } } or { "mcpServers": { ... } }.',
      };
    }
  }

  const servers: DetectedServer[] = [];

  for (const [serverName, serverDef] of Object.entries(serversMap)) {
    if (!serverDef || typeof serverDef !== "object" || Array.isArray(serverDef)) continue;
    const def = serverDef as Record<string, unknown>;
    const key = serverName.toLowerCase().replace(/[^a-z0-9_-]/g, "-").replace(/^-+/, "").slice(0, 64);
    const url = typeof def.url === "string" ? def.url.trim() : "";

    const rawHeaders =
      def.headers && typeof def.headers === "object" && !Array.isArray(def.headers)
        ? (def.headers as Record<string, unknown>)
        : {};

    const secretHeaders: DetectedSecretHeader[] = [];
    const plainHeaders: Record<string, string> = {};

    for (const [hName, hValue] of Object.entries(rawHeaders)) {
      if (typeof hValue !== "string") continue;
      if (hName.toLowerCase() === "authorization") continue;

      const nameIsSecret = SECRET_HEADER_PATTERNS_RE.test(hName);
      const valueIsPlaceholder = PLACEHOLDER_VALUE_RE.test(hValue.trim());
      const valueIsTokenPrefix = TOKEN_PREFIX_RE.test(hValue.trim());
      const valueIsEmpty = hValue.trim() === "" || hValue.trim() === "YOUR_API_KEY";

      if (nameIsSecret || valueIsPlaceholder || valueIsTokenPrefix || valueIsEmpty) {
        secretHeaders.push({
          name: hName,
          value: hValue.trim(),
          // Only true when the value needs to be replaced — not for real token prefixes.
          isPlaceholder: valueIsPlaceholder || valueIsEmpty,
        });
      } else {
        plainHeaders[hName] = hValue;
      }
    }

    servers.push({
      key,
      url,
      enabled: def.enabled === undefined ? true : Boolean(def.enabled),
      secretHeaders,
      plainHeaders,
    });
  }

  if (servers.length === 0) {
    return { servers: [], error: "No MCP servers found in the pasted config" };
  }

  // Validate that all servers have a URL
  const missingUrl = servers.filter((s) => !s.url);
  if (missingUrl.length > 0) {
    return { servers: [], error: `Server "${missingUrl[0].key}" is missing a url field` };
  }

  return { servers };
}

// ---------------------------------------------------------------------------
// Form components
// ---------------------------------------------------------------------------

interface RepoScopedMcpConfig {
  id: string;
  key: string;
  displayName: string;
  upstreamUrl: string;
  enabled: boolean;
  authMode: "none" | "api_key" | "oauth";
  authBindingId: string | null;
}

interface RepoManagedSkill {
  id: string;
  skillName: string;
  description: string;
  sourceUrl: string | null;
  sourceHash: string | null;
  enabled: boolean;
  files: Array<{ path: string; content: string }>;
}

interface RepoAuthBinding {
  id: string;
  name: string;
  provider: string;
  authMode: "api_key" | "oauth";
  ownerUserId: string | null;
  oauthStatus?: {
    connected: boolean;
    status: "connected" | "needs_auth" | "expired" | "needs_reauth" | "error" | "not_configured";
    expiresAt: number | null;
    hasRefreshToken: boolean;
    canRefresh: boolean;
    message: string | null;
  };
}

interface RepoEffectiveConfig {
  effective: {
    mcp: Array<{ key: string; displayName: string; enabled: boolean; authMode: string; upstreamUrl: string }>;
    managedSkills: Array<{ skillName: string; description: string; enabled: boolean }>;
  };
  issues: Array<{ code: string; key: string; message: string }>;
}

interface RepoOrgPolicyView {
  canManage: boolean;
}

type AgentConfigTestSessionRecord = {
  id: string;
  repositoryId: string | null;
  title: string | null;
  status: string;
  agentType?: AgentType;
  createdAt: string;
  lifecycleStatus: string;
  parentSessionId?: string | null;
  archivedAt?: string | null;
  hasHistory?: boolean;
  [key: string]: unknown;
};

function AgentConfigTab({ repoId }: { repoId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const openOauthPopup = (authorizationUrl: string) => {
    const popup = window.open(
      authorizationUrl,
      "cord-mcp-oauth",
      "width=560,height=720,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes,status=no",
    );
    if (!popup) {
      window.location.href = authorizationUrl;
      return;
    }
    popup.focus();
  };

  const startOauthForBinding = async (bindingId: string) => {
    const data = await apiFetch<{ authorizationUrl: string; expiresAt: number }>(
      `/api/settings/mcp-auth-bindings/${bindingId}/oauth/start`,
      { method: "POST" },
    );
    openOauthPopup(data.authorizationUrl);
  };

  useEffect(() => {
    const apiOrigin = (() => {
      const apiBase = import.meta.env.VITE_API_URL || window.location.origin;
      try {
        return new URL(apiBase, window.location.origin).origin;
      } catch {
        return window.location.origin;
      }
    })();

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== apiOrigin && event.origin !== window.location.origin) {
        return;
      }

      const data = event.data as {
        source?: string;
        result?: "success" | "error";
        message?: string;
      } | null;
      if (!data || data.source !== "cord-mcp-oauth") return;

      queryClient.invalidateQueries({ queryKey: ["repo-mcp-config", repoId] });
      queryClient.invalidateQueries({ queryKey: ["repo-auth-bindings"] });
      queryClient.invalidateQueries({ queryKey: ["effective-config", repoId] });
      setConnectingBindingId(null);

      if (data.result === "success") {
        toast.success(data.message || "OAuth connected");
      } else {
        toast.error(data.message || "OAuth failed");
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [queryClient, repoId]);

  // MCP add form
  const [showAddForm, setShowAddForm] = useState(false);

  // Paste mode state
  const [pasteJson, setPasteJson] = useState("");
  const [detectedServers, setDetectedServers] = useState<DetectedServer[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [secretValues, setSecretValues] = useState<Record<string, Record<string, string>>>({});
  const [showSecretValues, setShowSecretValues] = useState<Record<string, boolean>>({});
  const [connectingBindingId, setConnectingBindingId] = useState<string | null>(null);
  const [disconnectingBindingIds, setDisconnectingBindingIds] = useState<Record<string, boolean>>({});
  const [pendingRepoMcpToggleIds, setPendingRepoMcpToggleIds] = useState<Set<string>>(() => new Set());
  const [pendingRepoMcpDeleteIds, setPendingRepoMcpDeleteIds] = useState<Set<string>>(() => new Set());

  // Skills state
  const [skillName, setSkillName] = useState("");
  const [skillMd, setSkillMd] = useState("---\nname: repo-skill\ndescription: Describe what this skill does\n---\n\nWrite your instructions here.\n");
  const [extraSkillFiles, setExtraSkillFiles] = useState<Array<{ path: string; content: string }>>([]);
  const [pendingRepoSkillToggleIds, setPendingRepoSkillToggleIds] = useState<Set<string>>(() => new Set());
  const [pendingRepoSkillDeleteIds, setPendingRepoSkillDeleteIds] = useState<Set<string>>(() => new Set());
  const [agentType, setAgentType] = useState<"opencode" | "claude_code" | "cursor">("opencode");
  const [sessionOverrideEdits, setSessionOverrideEdits] = useState<Record<string, boolean>>({});

  const resetMcpForm = () => {
    setShowAddForm(false);
    setPasteJson("");
    setDetectedServers(null);
    setParseError(null);
    setSecretValues({});
    setShowSecretValues({});
  };

  const effectiveOverridesQuery = useQuery<RepoEffectiveConfig>({
    queryKey: ["effective-config", repoId, sessionOverrideEdits],
    queryFn: () => {
      const params = new URLSearchParams({ repositoryId: repoId });
      const overrides = Object.entries(sessionOverrideEdits).map(([key, enabled]) => ({ key, enabled }));
      if (overrides.length > 0) {
        params.set("sessionMcpOverrides", JSON.stringify(overrides));
      }
      return apiFetch<RepoEffectiveConfig>(`/api/settings/effective-config?${params.toString()}`);
    },
  });

  const { data: mcpConfigs, isLoading: mcpLoading } = useQuery<RepoScopedMcpConfig[]>({
    queryKey: ["repo-mcp-config", repoId],
    queryFn: () => apiFetch<RepoScopedMcpConfig[]>(`/api/settings/mcp-config?scopeKind=repo&repositoryId=${encodeURIComponent(repoId)}`),
  });

  const { data: skills } = useQuery<RepoManagedSkill[]>({
    queryKey: ["repo-managed-skills", repoId],
    queryFn: () => apiFetch<RepoManagedSkill[]>(`/api/settings/managed-skills?scopeKind=repo&repositoryId=${encodeURIComponent(repoId)}`),
  });

  const { data: bindings } = useQuery<RepoAuthBinding[]>({
    queryKey: ["repo-auth-bindings"],
    queryFn: () => apiFetch<RepoAuthBinding[]>("/api/settings/mcp-auth-bindings"),
  });

  const { data: orgPolicy } = useQuery<RepoOrgPolicyView>({
    queryKey: ["org-policy"],
    queryFn: () => apiFetch<RepoOrgPolicyView>("/api/settings/org-policy"),
  });

  const { data: effective } = useQuery<RepoEffectiveConfig>({
    queryKey: ["effective-config", repoId],
    queryFn: () => apiFetch<RepoEffectiveConfig>(`/api/settings/effective-config?repositoryId=${encodeURIComponent(repoId)}`),
  });


  // Parse pasted JSON on change
  const handlePasteChange = (value: string) => {
    setPasteJson(value);
    if (!value.trim()) {
      setDetectedServers(null);
      setParseError(null);
      return;
    }
    const result = detectServersFromJson(value);
    if (result.error) {
      setDetectedServers(null);
      setParseError(result.error);
    } else {
      setDetectedServers(result.servers);
      setParseError(null);
      // Pre-fill secret values: use the actual value from JSON for real keys,
      // empty string for placeholders; preserve any value the user already typed.
      setSecretValues((prev) => {
        const next: Record<string, Record<string, string>> = {};
        for (const server of result.servers) {
          if (server.secretHeaders.length > 0) {
            next[server.key] = {};
            for (const h of server.secretHeaders) {
              next[server.key][h.name] = prev[server.key]?.[h.name] ?? (h.isPlaceholder ? "" : h.value);
            }
          }
        }
        return next;
      });
    }
  };

  // Import mutation (paste mode)
  const importConfig = useMutation({
    mutationFn: async () => {
      return apiFetch<{
        results: Array<{
          key: string;
          status: string;
          error?: string;
          authMode?: "none" | "api_key" | "oauth";
          authBindingId?: string | null;
          needsConnection?: boolean;
        }>;
      }>(
        "/api/settings/mcp-config/import",
        {
          method: "POST",
          body: JSON.stringify({
            scopeKind: "repo",
            repositoryId: repoId,
            json: pasteJson,
            secrets: secretValues,
          }),
        },
      );
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["repo-mcp-config", repoId] });
      queryClient.invalidateQueries({ queryKey: ["repo-auth-bindings"] });
      queryClient.invalidateQueries({ queryKey: ["effective-config", repoId] });

      const created = data.results.filter((r) => r.status === "created");
      const errors = data.results.filter((r) => r.status === "error");
      const oauthNeedingConnection = created.filter(
        (r) => r.authMode === "oauth" && r.authBindingId && r.needsConnection,
      );

      if (created.length > 0) {
        toast.success(`${created.length} MCP server${created.length > 1 ? "s" : ""} added`);
      }
      if (errors.length > 0) {
        for (const err of errors) {
          toast.error(`${err.key}: ${err.error}`);
        }
      }
      if (errors.length === 0) {
        resetMcpForm();
        if (oauthNeedingConnection.length === 1) {
          const bindingId = oauthNeedingConnection[0]?.authBindingId;
          if (!bindingId) {
            return;
          }
          try {
            await startOauthForBinding(bindingId);
          } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to start OAuth");
          }
        } else if (oauthNeedingConnection.length > 1) {
          toast.info(
            `Added ${oauthNeedingConnection.length} OAuth-backed MCP servers. Connect them individually using the Connect buttons.`,
          );
        }
      }
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Failed to import MCP config"),
  });

  const disconnectOauth = useMutation({
    mutationFn: async (bindingId: string) =>
      apiFetch<{ ok: true }>(`/api/settings/mcp-auth-bindings/${bindingId}/oauth/disconnect`, {
        method: "POST",
      }),
    onSuccess: (_data, bindingId) => {
      setDisconnectingBindingIds((current) => {
        const { [bindingId]: _ignored, ...rest } = current;
        return rest;
      });
      queryClient.invalidateQueries({ queryKey: ["repo-auth-bindings"] });
      queryClient.invalidateQueries({ queryKey: ["repo-mcp-config", repoId] });
      toast.success("OAuth disconnected");
    },
    onError: (error, bindingId) => {
      setDisconnectingBindingIds((current) => {
        const { [bindingId]: _ignored, ...rest } = current;
        return rest;
      });
      toast.error(error instanceof Error ? error.message : "Failed to disconnect OAuth");
    },
  });

  const toggleMcp = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiFetch(`/api/settings/mcp-config/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled }),
      }),
    onMutate: ({ id }) => {
      setPendingRepoMcpToggleIds((current) => new Set(current).add(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repo-mcp-config", repoId] });
      queryClient.invalidateQueries({ queryKey: ["effective-config", repoId] });
    },
    onSettled: (_data, _error, variables) => {
      setPendingRepoMcpToggleIds((current) => {
        const next = new Set(current);
        next.delete(variables.id);
        return next;
      });
    },
  });

  const deleteMcp = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/settings/mcp-config/${id}`, { method: "DELETE" }),
    onMutate: (id) => {
      setPendingRepoMcpDeleteIds((current) => new Set(current).add(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repo-mcp-config", repoId] });
      queryClient.invalidateQueries({ queryKey: ["effective-config", repoId] });
      toast.success("Repo MCP config deleted");
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Failed to delete repo MCP config"),
    onSettled: (_data, _error, id) => {
      setPendingRepoMcpDeleteIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    },
  });

  const saveSkill = useMutation({
    mutationFn: () => apiFetch("/api/settings/managed-skills", {
      method: "POST",
      body: JSON.stringify({
        scopeKind: "repo",
        repositoryId: repoId,
        skillName,
        files: [{ path: "SKILL.md", content: skillMd }, ...extraSkillFiles.filter((file) => file.path.trim())],
        enabled: true,
      }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repo-managed-skills", repoId] });
      queryClient.invalidateQueries({ queryKey: ["effective-config", repoId] });
      setSkillName("");
      setExtraSkillFiles([]);
      toast.success("Repo managed skill saved");
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Failed to save repo managed skill"),
  });

  const toggleSkill = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiFetch(`/api/settings/managed-skills/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled }),
      }),
    onMutate: ({ id }) => {
      setPendingRepoSkillToggleIds((current) => new Set(current).add(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repo-managed-skills", repoId] });
      queryClient.invalidateQueries({ queryKey: ["effective-config", repoId] });
    },
    onSettled: (_data, _error, variables) => {
      setPendingRepoSkillToggleIds((current) => {
        const next = new Set(current);
        next.delete(variables.id);
        return next;
      });
    },
  });

  const deleteSkill = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/settings/managed-skills/${id}`, { method: "DELETE" }),
    onMutate: (id) => {
      setPendingRepoSkillDeleteIds((current) => new Set(current).add(id));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["repo-managed-skills", repoId] });
      queryClient.invalidateQueries({ queryKey: ["effective-config", repoId] });
      toast.success("Repo managed skill deleted");
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "Failed to delete repo managed skill"),
    onSettled: (_data, _error, id) => {
      setPendingRepoSkillDeleteIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    },
  });

  const launchSession = useMutation({
    mutationFn: async (input: {
      sessionId: string;
      title: string;
      agentType: AgentType;
      sessionMcpOverrides: Array<{ key: string; enabled: boolean }>;
      cacheSnapshot: SessionCacheMutationSnapshot;
    }) => {
      const session = await apiFetch<AgentConfigTestSessionRecord>("/api/sessions", {
        method: "POST",
        body: JSON.stringify({
          repositoryId: repoId,
          title: input.title,
          agentType: input.agentType,
          id: input.sessionId,
          sessionMcpOverrides: input.sessionMcpOverrides,
        }),
      });
      return session;
    },
    onSuccess: (session, input) => {
      const existingSession = queryClient.getQueryData<AgentConfigTestSessionRecord>(["session", session.id]);
      const mergedSession = mergeSessionRecordPreservingHasHistory({
        incoming: session,
        existing: existingSession,
      });
      queryClient.setQueryData(["session", session.id], mergedSession);
      applyOptimisticSessionMembershipToSessionListCaches({
        queryClient,
        snapshot: input.cacheSnapshot,
        sessionId: session.id,
        item: mergedSession,
      });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["sessions", repoId] });
      toast.success("Test session started");
    },
    onError: (error, input) => {
      restoreSessionCacheMutationSnapshot({
        queryClient,
        sessionId: input.sessionId,
        snapshot: input.cacheSnapshot,
      });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      queryClient.invalidateQueries({ queryKey: ["sessions", repoId] });
      sessionStorage.removeItem(`cord:session-title:${input.sessionId}`);
      sessionStorage.removeItem(`cord:session-agent-type:${input.sessionId}`);
      navigate({ to: "/$repoId/settings", params: { repoId }, search: { onboarding: false }, replace: true });
      toast.error(error instanceof Error ? error.message : "Failed to start test session");
    },
  });

  const handleLaunchTestSession = useCallback(() => {
    if (launchSession.isPending) return;
    const sessionId = crypto.randomUUID();
    const title = `Test MCP/skills ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    const sessionMcpOverrides = Object.entries(sessionOverrideEdits).map(([key, enabled]) => ({ key, enabled }));
    const placeholder = {
      ...buildOptimisticEmptyThreadSessionRecord({
        sessionId,
        repositoryId: repoId,
        title,
        agentType,
      }),
      parentSessionId: null,
      archivedAt: null,
    } satisfies AgentConfigTestSessionRecord & { hasHistory: false };
    void queryClient.cancelQueries({ queryKey: ["sessions"] });
    const cacheSnapshot = captureSessionCacheMutationSnapshot({
      queryClient,
      sessionId,
      repoId,
    });
    applyOptimisticSessionMembershipToSessionListCaches({
      queryClient,
      snapshot: cacheSnapshot,
      sessionId,
      item: placeholder,
    });
    queryClient.setQueryData(["session", sessionId], placeholder);
    sessionStorage.setItem(`cord:session-title:${sessionId}`, title);
    sessionStorage.setItem(`cord:session-agent-type:${sessionId}`, agentType);
    navigate({ to: "/$repoId/$sessionId", params: { repoId, sessionId } });
    launchSession.mutate({
      sessionId,
      title,
      agentType,
      sessionMcpOverrides,
      cacheSnapshot,
    });
  }, [agentType, launchSession, navigate, queryClient, repoId, sessionOverrideEdits]);

  const hasServers = mcpConfigs && mcpConfigs.length > 0;

  // Check if all required secrets have been filled in
  const allSecretsProvided = detectedServers
    ? detectedServers.every((s) => {
        if (s.secretHeaders.length === 0) return true;
        const sv = secretValues[s.key];
        if (!sv) return false;
        return s.secretHeaders.every((h) => sv[h.name]?.trim());
      })
    : true;

  const hasDetectedSecrets = detectedServers?.some((s) => s.secretHeaders.length > 0) ?? false;
  const canManageOauthBindings = orgPolicy?.canManage ?? false;

  const bindingById = useMemo(() => {
    const map = new Map<string, RepoAuthBinding>();
    for (const b of bindings ?? []) map.set(b.id, b);
    return map;
  }, [bindings]);

  const getOauthStatusForConfig = (config: RepoScopedMcpConfig) => {
    if (config.authMode !== "oauth" || !config.authBindingId) return null;
    const binding = bindingById.get(config.authBindingId);
    if (!binding || binding.authMode !== "oauth") return null;
    return binding.oauthStatus ?? null;
  };

  const oauthStatusLabel = (status: NonNullable<RepoAuthBinding["oauthStatus"]>): { text: string; className: string } => {
    switch (status.status) {
      case "connected":
        return { text: "Connected", className: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20" };
      case "needs_auth":
        return { text: "Needs auth", className: "bg-amber-500/15 text-amber-300 border border-amber-500/20" };
      case "expired":
        return { text: "Expired", className: "bg-orange-500/15 text-orange-300 border border-orange-500/20" };
      case "needs_reauth":
        return { text: "Reconnect", className: "bg-amber-500/15 text-amber-300 border border-amber-500/20" };
      case "error":
        return { text: "Auth error", className: "bg-red-500/15 text-red-300 border border-red-500/20" };
      case "not_configured":
        return { text: "Not configured", className: "bg-zinc-700/40 text-zinc-300 border border-zinc-700" };
      default:
        return { text: "OAuth", className: "bg-zinc-700/40 text-zinc-300 border border-zinc-700" };
    }
  };

  return (
    <div className="space-y-6">
      {/* ========== Repo MCP Servers ========== */}
      <SettingsCard>
        <div className="flex items-center gap-2 mb-1">
          <Blocks className="size-4 text-zinc-400" />
          <div className="text-sm font-semibold text-zinc-100">Repo MCP servers</div>
          <span className="ml-auto text-xs text-zinc-600">
            {mcpLoading ? "..." : `${mcpConfigs?.length ?? 0} server${(mcpConfigs?.length ?? 0) !== 1 ? "s" : ""}`}
          </span>
        </div>
        <p className="text-xs text-zinc-500 mb-4">MCP servers scoped to this repository. They are merged with org and user servers for new sessions.</p>

        {/* Existing servers list */}
        {hasServers && (
          <div className="space-y-2 mb-4">
            {mcpConfigs!.map((config) => {
              const oauthStatus = getOauthStatusForConfig(config);
              const repoMcpTogglePending = pendingRepoMcpToggleIds.has(config.id);
              const repoMcpDeletePending = pendingRepoMcpDeleteIds.has(config.id);
              const repoMcpRowPending = repoMcpTogglePending || repoMcpDeletePending;
              return (
                <div key={config.id} className="group rounded-lg border border-zinc-800/50 bg-zinc-950/30 px-3 py-2.5 transition-colors hover:border-zinc-700/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={cn(
                        "flex size-8 items-center justify-center rounded-lg text-xs font-semibold shrink-0",
                        config.enabled ? "bg-emerald-950/40 text-emerald-400" : "bg-zinc-800/60 text-zinc-600",
                      )}>
                        <Blocks className="size-3.5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-zinc-200 truncate">{config.displayName || config.key}</div>
                        <div className="text-xs text-zinc-500 font-mono truncate">{config.upstreamUrl}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {config.authMode === "api_key" && (
                        <span className="rounded-full bg-zinc-800/60 px-1.5 py-0.5 text-xs text-zinc-500">
                          API Key
                        </span>
                      )}
                      {oauthStatus && (
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${oauthStatusLabel(oauthStatus).className}`}>
                          {oauthStatusLabel(oauthStatus).text}
                        </span>
                      )}
                      {config.authMode === "oauth" && oauthStatus && !oauthStatus.connected && canManageOauthBindings && (
                        <button
                          onClick={async () => {
                            if (!config.authBindingId) return;
                            setConnectingBindingId(config.authBindingId);
                            try {
                              await startOauthForBinding(config.authBindingId);
                            } catch (error) {
                              setConnectingBindingId(null);
                              toast.error(error instanceof Error ? error.message : "Failed to start OAuth");
                            }
                          }}
                          disabled={connectingBindingId === config.authBindingId}
                          className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50"
                        >
                          {connectingBindingId === config.authBindingId ? <Loader2 className="size-3 animate-spin" /> : "Connect"}
                        </button>
                      )}
                      {config.authMode === "oauth" && oauthStatus?.connected && canManageOauthBindings && (
                        <>
                          <button
                            onClick={async () => {
                              if (!config.authBindingId) return;
                              setConnectingBindingId(config.authBindingId);
                              try {
                                await startOauthForBinding(config.authBindingId);
                              } catch (error) {
                                setConnectingBindingId(null);
                                toast.error(error instanceof Error ? error.message : "Failed to start OAuth");
                              }
                            }}
                            disabled={connectingBindingId === config.authBindingId}
                            className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50"
                          >
                            Reconnect
                          </button>
                          <button
                            onClick={() => {
                              const bindingId = config.authBindingId;
                              if (!bindingId) return;
                              setDisconnectingBindingIds((current) => ({
                                ...current,
                                [bindingId]: true,
                              }));
                              disconnectOauth.mutate(bindingId);
                            }}
                            disabled={Boolean(config.authBindingId && disconnectingBindingIds[config.authBindingId])}
                            className="rounded-md border border-zinc-700 px-2 py-1 text-xs text-zinc-300 hover:border-zinc-600 disabled:opacity-50"
                          >
                            {config.authBindingId && disconnectingBindingIds[config.authBindingId] ? <Loader2 className="size-3 animate-spin" /> : "Disconnect"}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => toggleMcp.mutate({ id: config.id, enabled: !config.enabled })}
                        disabled={repoMcpRowPending}
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors disabled:opacity-60",
                          config.enabled
                            ? "bg-emerald-950/30 text-emerald-400 hover:bg-emerald-950/50"
                            : "bg-zinc-800/60 text-zinc-500 hover:bg-zinc-700/60",
                        )}
                      >
                        {repoMcpTogglePending ? <Loader2 className="size-3 animate-spin" /> : null}
                        {repoMcpTogglePending
                          ? config.enabled ? "Disabling..." : "Enabling..."
                          : config.enabled ? "Active" : "Disabled"}
                      </button>
                      <button
                        onClick={() => deleteMcp.mutate(config.id)}
                        disabled={repoMcpRowPending}
                        className="opacity-0 group-hover:opacity-100 rounded-md p-1 text-zinc-600 hover:text-red-400 hover:bg-red-950/20 transition-all disabled:opacity-50"
                        title="Remove server"
                      >
                        {repoMcpDeletePending ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {!mcpLoading && !hasServers && !showAddForm && (
          <div className="rounded-lg border border-dashed border-zinc-800/60 px-4 py-6 text-center mb-4">
            <Blocks className="size-6 text-zinc-700 mx-auto mb-2" />
            <p className="text-xs text-zinc-500 mb-1">No repo MCP servers configured yet</p>
            <p className="text-xs text-zinc-600">
              Paste a JSON config from your MCP server provider to get started.
            </p>
          </div>
        )}

        {/* Add server button / form */}
        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-zinc-700/60 px-3 py-2 text-xs text-zinc-400 hover:border-zinc-600 hover:text-zinc-300 transition-colors w-full justify-center"
          >
            <Plus className="size-3.5" />
            Add MCP server
          </button>
        ) : (
          <div className="rounded-lg border border-zinc-700/60 bg-zinc-950/50 p-4 space-y-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-medium text-zinc-300">Add new MCP server</div>
              <button
                onClick={resetMcpForm}
                className="rounded-md p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <X className="size-3.5" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs text-zinc-500">
                  Paste the JSON config from your MCP server provider
                </label>
                <textarea
                  value={pasteJson}
                  onChange={(e) => handlePasteChange(e.target.value)}
                  rows={6}
                  className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-200 placeholder:text-zinc-600 font-mono resize-y"
                  placeholder={'{\n  "mcp": {\n    "server-name": {\n      "type": "remote",\n      "url": "https://mcp.example.com/mcp",\n      "headers": {\n        "API_KEY": "YOUR_API_KEY"\n      }\n    }\n  }\n}'}
                  spellCheck={false}
                />
              </div>

              {/* Parse error */}
              {parseError && (
                <div className="rounded-lg border border-red-900/40 bg-red-950/20 px-3 py-2 text-xs text-red-400">
                  {parseError}
                </div>
              )}

              {/* Detected servers preview */}
              {detectedServers && detectedServers.length > 0 && (
                <div className="space-y-3">
                  {detectedServers.map((server) => (
                    <div key={server.key} className="rounded-lg border border-zinc-800/50 bg-zinc-900/50 p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Blocks className="size-3.5 text-emerald-400" />
                        <span className="text-sm font-medium text-zinc-200">{server.key}</span>
                        <span className="text-xs text-zinc-500 font-mono truncate">{server.url}</span>
                      </div>

                      {/* Secret header display */}
                      {server.secretHeaders.length > 0 && (
                        <div className="space-y-2 pt-1">
                          {server.secretHeaders.every((h) => !h.isPlaceholder) ? (
                            <div className="flex items-center gap-1.5">
                              <Check className="size-3 text-emerald-400" />
                              <span className="text-xs text-emerald-400 font-medium">
                                {server.secretHeaders.length === 1 ? "API key included" : `${server.secretHeaders.length} API keys included`}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <Key className="size-3 text-amber-400" />
                              <span className="text-xs text-amber-400 font-medium">
                                {server.secretHeaders.filter((h) => h.isPlaceholder).length === 1
                                  ? "Credential required"
                                  : `${server.secretHeaders.filter((h) => h.isPlaceholder).length} credentials required`}
                              </span>
                            </div>
                          )}

                          {server.secretHeaders.map((h) => (
                            <div key={h.name}>
                              <label className="mb-1 block text-xs text-zinc-500 font-mono">
                                {h.name}
                              </label>
                              {h.isPlaceholder ? (
                                <div className="relative">
                                  <input
                                    type={showSecretValues[`${server.key}:${h.name}`] ? "text" : "password"}
                                    value={secretValues[server.key]?.[h.name] ?? ""}
                                    onChange={(e) => {
                                      setSecretValues((prev) => ({
                                        ...prev,
                                        [server.key]: {
                                          ...prev[server.key],
                                          [h.name]: e.target.value,
                                        },
                                      }));
                                    }}
                                    className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 pr-9 text-sm text-zinc-200 placeholder:text-zinc-600 font-mono"
                                    placeholder="Enter your API key..."
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setShowSecretValues((prev) => ({ ...prev, [`${server.key}:${h.name}`]: !prev[`${server.key}:${h.name}`] }))}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400 transition-colors"
                                  >
                                    {showSecretValues[`${server.key}:${h.name}`] ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 rounded-lg border border-zinc-800/50 bg-zinc-950/50 px-3 py-2">
                                  <span className="text-sm text-zinc-400 font-mono truncate">{maskSecretValue(h.value)}</span>
                                  <Check className="size-3 text-emerald-400 shrink-0 ml-auto" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {Object.keys(server.plainHeaders).length > 0 && (
                        <div className="text-xs text-zinc-600">
                          + {Object.keys(server.plainHeaders).length} non-secret header{Object.keys(server.plainHeaders).length > 1 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Submit */}
              {detectedServers && detectedServers.length > 0 && (
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={resetMcpForm}
                    className="rounded-lg px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => importConfig.mutate()}
                    disabled={importConfig.isPending || (hasDetectedSecrets && !allSecretsProvided)}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-3 py-1.5 text-xs font-medium text-zinc-900 hover:bg-zinc-200 disabled:opacity-50 transition-colors"
                  >
                    {importConfig.isPending ? <Loader2 className="size-3 animate-spin" /> : <Plus className="size-3" />}
                    {detectedServers.length === 1
                      ? detectedServers[0].secretHeaders.length === 0
                        ? "Add and connect"
                        : "Add server"
                      : `Add ${detectedServers.length} servers`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </SettingsCard>

      <SettingsCard>
        <div className="mb-3 text-sm font-semibold text-zinc-100">Repo managed skills</div>
        <div className="space-y-3">
          <FormField icon={<Bot className="size-3.5" />} label="Skill name" placeholder="deploy-checklist" value={skillName} onChange={setSkillName} />
          <textarea value={skillMd} onChange={(e) => setSkillMd(e.target.value)} rows={10} className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:ring-1 focus:ring-zinc-700" />
          <div className="space-y-2 rounded-lg border border-zinc-800/70 p-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-zinc-300">Additional files</div>
              <button type="button" onClick={() => setExtraSkillFiles((prev) => [...prev, { path: "references/example.md", content: "" }])} className="rounded-md border border-zinc-800 px-2 py-1 text-xs text-zinc-500 hover:border-zinc-700 hover:text-zinc-300">Add file</button>
            </div>
            {extraSkillFiles.length === 0 ? (
              <div className="text-xs text-zinc-500">Optional files copied alongside `SKILL.md` into the sandbox.</div>
            ) : extraSkillFiles.map((file, idx) => (
              <div key={idx} className="space-y-2 rounded-md border border-zinc-800/60 p-2">
                <input value={file.path} onChange={(e) => setExtraSkillFiles((prev) => prev.map((entry, entryIdx) => entryIdx === idx ? { ...entry, path: e.target.value } : entry))} className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-xs text-zinc-200" placeholder="references/guide.md" />
                <textarea value={file.content} onChange={(e) => setExtraSkillFiles((prev) => prev.map((entry, entryIdx) => entryIdx === idx ? { ...entry, content: e.target.value } : entry))} rows={4} className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-xs text-zinc-200" />
                <div className="flex justify-end">
                  <button type="button" onClick={() => setExtraSkillFiles((prev) => prev.filter((_, entryIdx) => entryIdx !== idx))} className="rounded-md border border-zinc-800 px-2 py-1 text-xs text-zinc-500 hover:border-zinc-700 hover:text-zinc-300">Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={() => saveSkill.mutate()} disabled={saveSkill.isPending || !skillName.trim() || !skillMd.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-3 py-1.5 text-xs font-medium text-zinc-900 hover:bg-zinc-200 disabled:opacity-50">
            {saveSkill.isPending ? <Loader2 className="size-3 animate-spin" /> : <Plus className="size-3" />} Save managed skill
          </button>
        </div>
        <div className="mt-4 space-y-2 border-t border-zinc-800/60 pt-4">
          {(skills ?? []).length === 0 ? <div className="text-xs text-zinc-500">No repo managed skills yet.</div> : (skills ?? []).map((skill) => {
            const repoSkillTogglePending = pendingRepoSkillToggleIds.has(skill.id);
            const repoSkillDeletePending = pendingRepoSkillDeleteIds.has(skill.id);
            const repoSkillRowPending = repoSkillTogglePending || repoSkillDeletePending;
            return (
              <div key={skill.id} className="flex items-center justify-between rounded-lg border border-zinc-800/70 px-3 py-2">
                <div>
                  <div className="text-sm text-zinc-200">{skill.skillName}</div>
                  <div className="text-xs text-zinc-500">{skill.description}</div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-xs text-zinc-500">
                    <input
                      type="checkbox"
                      checked={skill.enabled}
                      disabled={repoSkillRowPending}
                      onChange={(e) => toggleSkill.mutate({ id: skill.id, enabled: e.target.checked })}
                    />
                    {repoSkillTogglePending ? (
                      <>
                        <Loader2 className="size-3 animate-spin" />
                        <span>{skill.enabled ? "disabling..." : "enabling..."}</span>
                      </>
                    ) : (
                      <span>enabled</span>
                    )}
                  </label>
                  <button
                    onClick={() => deleteSkill.mutate(skill.id)}
                    disabled={repoSkillRowPending}
                    className="inline-flex items-center gap-1 rounded-lg border border-zinc-800 px-2.5 py-1 text-xs text-zinc-500 hover:border-zinc-700 hover:text-zinc-300 disabled:opacity-50"
                  >
                    {repoSkillDeletePending ? <Loader2 className="size-3 animate-spin" /> : null}
                    {repoSkillDeletePending ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </SettingsCard>

      <SettingsCard>
        <div className="mb-3 text-sm font-semibold text-zinc-100">Effective config preview</div>
        <p className="mb-3 text-xs text-zinc-500">This is the merged config new sessions for this repo will receive after applying org, repo, and user scopes.</p>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="mb-2 text-xs font-medium text-zinc-300">MCP servers</div>
            <div className="space-y-2">
              {(effective?.effective.mcp ?? []).length === 0 ? <div className="text-xs text-zinc-500">No MCP servers resolved.</div> : effective?.effective.mcp.map((mcp) => (
                <div key={mcp.key} className="rounded-lg border border-zinc-800/70 px-3 py-2">
                  <div className="text-sm text-zinc-200">{mcp.displayName}</div>
                  <div className="text-xs text-zinc-500">{mcp.key} · {mcp.authMode} · {mcp.enabled ? "enabled" : "disabled"}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs font-medium text-zinc-300">Managed skills</div>
            <div className="space-y-2">
              {(effective?.effective.managedSkills ?? []).length === 0 ? <div className="text-xs text-zinc-500">No managed skills resolved.</div> : effective?.effective.managedSkills.map((skill) => (
                <div key={skill.skillName} className="rounded-lg border border-zinc-800/70 px-3 py-2">
                  <div className="text-sm text-zinc-200">{skill.skillName}</div>
                  <div className="text-xs text-zinc-500">{skill.enabled ? "enabled" : "disabled"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {(effective?.issues?.length ?? 0) > 0 && (
          <div className="mt-4 rounded-lg border border-amber-900/40 bg-amber-950/20 px-3 py-2 text-xs text-amber-200">
            {effective?.issues.map((issue) => (
              <div key={`${issue.key}-${issue.code}`}>{issue.key}: {issue.message}</div>
            ))}
          </div>
        )}
      </SettingsCard>

      <SettingsCard>
        <div className="mb-3 text-sm font-semibold text-zinc-100">Launch test session</div>
        <p className="mb-3 text-xs text-zinc-500">Apply session-only MCP toggles and launch a fresh session to verify the sandbox receives the expected config.</p>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-zinc-300">Agent</label>
            <select value={agentType} onChange={(e) => setAgentType(e.target.value === "claude_code" ? "claude_code" : e.target.value === "cursor" ? "cursor" : "opencode")} className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200">
              <option value="opencode">OpenCode</option>
              <option value="claude_code">Claude Code</option>
              {/* <option value="cursor">Cursor</option> */}
            </select>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {(effective?.effective.mcp ?? []).length === 0 ? (
            <div className="text-xs text-zinc-500">No MCP servers available to toggle for this repo.</div>
          ) : (
            (effective?.effective.mcp ?? []).map((mcp) => (
              <label key={mcp.key} className="flex items-center justify-between rounded-lg border border-zinc-800/70 px-3 py-2">
                <div>
                  <div className="text-sm text-zinc-200">{mcp.displayName}</div>
                  <div className="text-xs text-zinc-500">{mcp.key}</div>
                </div>
                <input
                  type="checkbox"
                  checked={sessionOverrideEdits[mcp.key] ?? mcp.enabled}
                  onChange={(e) => {
                    const nextChecked = e.target.checked;
                    setSessionOverrideEdits((prev) => {
                      if (nextChecked === mcp.enabled) {
                        const { [mcp.key]: _removed, ...rest } = prev;
                        return rest;
                      }
                      return { ...prev, [mcp.key]: nextChecked };
                    });
                  }}
                />
              </label>
            ))
          )}
        </div>
        {(effectiveOverridesQuery.data?.issues?.length ?? 0) > 0 && (
          <div className="mt-4 rounded-lg border border-amber-900/40 bg-amber-950/20 px-3 py-2 text-xs text-amber-200">
            {effectiveOverridesQuery.data?.issues.map((issue) => (
              <div key={`${issue.key}-${issue.code}`}>{issue.key}: {issue.message}</div>
            ))}
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <button onClick={handleLaunchTestSession} disabled={launchSession.isPending || (effectiveOverridesQuery.data?.issues?.length ?? 0) > 0} className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 px-3 py-1.5 text-xs font-medium text-zinc-900 hover:bg-zinc-200 disabled:opacity-50">
            {launchSession.isPending ? <Loader2 className="size-3 animate-spin" /> : <Play className="size-3" />} Launch test session
          </button>
        </div>
      </SettingsCard>
    </div>
  );
}

function FormField({
  icon,
  label,
  hint,
  placeholder,
  value,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-zinc-300">
        <span className="text-zinc-500">{icon}</span>
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:ring-1 focus:ring-zinc-700"
      />
      {hint && (
        <p className="mt-1 text-xs text-zinc-500">{hint}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Testing Tab — browser testing instructions (repo-level + org defaults)
// ---------------------------------------------------------------------------

/**
 * Hook for auto-save-on-blur textarea state management.
 * Handles local editing, dirty tracking, pending save deduplication,
 * and server value reconciliation.
 */
function useAutoSaveTextarea(args: {
  serverValue: string | null;
  stableKey: string;
  onSave: (value: string | null) => void;
}) {
  const { serverValue, stableKey, onSave } = args;
  const [localValue, setLocalValue] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [pendingSaveValue, setPendingSaveValue] = useState<string | null>(null);
  const latestKeyRef = useRef(stableKey);

  useEffect(() => {
    latestKeyRef.current = stableKey;
    setLocalValue("");
    setIsDirty(false);
    setPendingSaveValue(null);
  }, [stableKey]);

  useEffect(() => {
    const normalized = serverValue ?? "";
    if (pendingSaveValue !== null) {
      if (normalized === pendingSaveValue) {
        setPendingSaveValue(null);
        setIsDirty(false);
        setLocalValue(normalized);
      }
      return;
    }
    if (!isDirty) {
      setLocalValue(normalized);
    }
  }, [serverValue, isDirty, pendingSaveValue]);

  const handleChange = useCallback((value: string) => {
    setPendingSaveValue(null);
    setIsDirty(true);
    setLocalValue(value);
  }, []);

  const handleBlur = useCallback(() => {
    const trimmed = localValue.trim();
    const current = (serverValue ?? "").trim();
    if (trimmed !== current) {
      setPendingSaveValue(trimmed);
      onSave(trimmed || null);
      return;
    }
    setIsDirty(false);
  }, [localValue, serverValue, onSave]);

  const confirmSave = useCallback((savedValue: string | null) => {
    if (latestKeyRef.current !== stableKey) return;
    setPendingSaveValue(null);
    setIsDirty(false);
    setLocalValue(savedValue ?? "");
  }, [stableKey]);

  const clearPending = useCallback(() => {
    if (latestKeyRef.current !== stableKey) return;
    setPendingSaveValue(null);
  }, [stableKey]);

  return { localValue, handleChange, handleBlur, confirmSave, clearPending };
}

/**
 * Textarea state for fields that must save only after an explicit user action.
 * This intentionally has no blur handler so prompt edits cannot be persisted by
 * focus changes; callers decide when to show and invoke their Save action.
 */
function useExplicitSaveTextarea(args: {
  serverValue: string;
  stableKey: string;
}) {
  const { serverValue, stableKey } = args;
  const [localValue, setLocalValue] = useState(serverValue);
  const [isDirty, setIsDirty] = useState(false);
  const latestKeyRef = useRef(stableKey);

  useEffect(() => {
    if (latestKeyRef.current !== stableKey) {
      latestKeyRef.current = stableKey;
      setLocalValue(serverValue);
      setIsDirty(false);
      return;
    }
    if (isDirty) return;
    setLocalValue(serverValue);
  }, [isDirty, serverValue, stableKey]);

  const handleChange = useCallback((value: string) => {
    setLocalValue(value);
    setIsDirty(value !== serverValue);
  }, [serverValue]);

  const confirmSave = useCallback((savedValue: string) => {
    if (latestKeyRef.current !== stableKey) return;
    setLocalValue(savedValue);
    setIsDirty(false);
  }, [stableKey]);

  return { localValue, isDirty, handleChange, confirmSave };
}

// ---------------------------------------------------------------------------
// Prompts Tab — repo-level prompt overrides stored in generic settings JSON
// ---------------------------------------------------------------------------

function PromptsTab({ repoId }: { repoId: string }) {
  const queryClient = useQueryClient();
  const reviewPromptQuery = useQuery<{
    canManage: boolean;
    defaultReviewPrompt: string;
    reviewPrompt: string | null;
  }>({
    queryKey: ["repo-review-prompt", repoId],
    queryFn: () => apiFetch(`/api/repos/${repoId}/review-prompt`),
  });

  const defaultPrompt = reviewPromptQuery.data?.defaultReviewPrompt ?? DEFAULT_REVIEW_PROMPT;
  const storedPrompt = reviewPromptQuery.data?.reviewPrompt ?? null;
  const effectivePrompt = storedPrompt ?? defaultPrompt;
  const canManage = reviewPromptQuery.data?.canManage ?? false;
  const hasCustomPrompt = Boolean(storedPrompt?.trim());

  const reviewPromptTextarea = useExplicitSaveTextarea({
    serverValue: effectivePrompt,
    stableKey: `review-prompt-${repoId}`,
  });

  const saveMutation = useMutation({
    mutationFn: async (args: { value: string | null; repoId: string }) => {
      await apiFetch(`/api/repos/${args.repoId}/review-prompt`, {
        method: "PATCH",
        body: JSON.stringify({ reviewPrompt: args.value }),
      });
    },
    onSuccess: (_result, variables) => {
      queryClient.setQueryData(["repo-review-prompt", variables.repoId], {
        canManage: true,
        defaultReviewPrompt: defaultPrompt,
        reviewPrompt: variables.value,
      });
      reviewPromptTextarea.confirmSave(variables.value ?? defaultPrompt);
      toast.success(variables.value ? "Review prompt saved" : "Review prompt reset to default");
    },
  });

  const normalizeReviewPromptForSave = useCallback((value: string) => {
      const normalizedDefault = defaultPrompt.trim();
      const normalizedValue = value.trim();
      return normalizedValue.length === 0 || normalizedValue === normalizedDefault ? null : normalizedValue;
  }, [defaultPrompt]);

  const hasUnsavedChanges = reviewPromptTextarea.isDirty;

  const saveReviewPrompt = useCallback(() => {
    if (!hasUnsavedChanges) return;
      saveMutation.mutate({
        value: normalizeReviewPromptForSave(reviewPromptTextarea.localValue),
        repoId,
      });
  }, [hasUnsavedChanges, normalizeReviewPromptForSave, repoId, reviewPromptTextarea.localValue, saveMutation]);

  const resetToDefault = useCallback(() => {
    saveMutation.mutate({ value: null, repoId });
  }, [repoId, saveMutation]);

  const isDisabled = reviewPromptQuery.isLoading || saveMutation.isPending || !canManage;

  return (
    <SettingsCard>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-800/80">
            <Pencil className="h-3.5 w-3.5 text-zinc-400" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-zinc-200">Review prompt</h3>
            <p className="text-xs text-zinc-500">
              Customize the prompt used when agents run review mode for this repo.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <span className="text-xs text-amber-400/80">Unsaved changes</span>
          )}
          <button
            type="button"
            onClick={resetToDefault}
            disabled={isDisabled || !hasCustomPrompt}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 px-2.5 py-1 text-xs font-medium text-zinc-500 transition-colors hover:border-zinc-700 hover:text-zinc-300 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className="size-3" />
            Reset default
          </button>
          {hasUnsavedChanges && (
            <button
              type="button"
              onClick={saveReviewPrompt}
              disabled={isDisabled}
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300 transition-colors hover:border-emerald-400/50 hover:bg-emerald-500/15 disabled:cursor-not-allowed disabled:border-zinc-800 disabled:bg-transparent disabled:text-zinc-500 disabled:opacity-50"
            >
              {saveMutation.isPending ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3" />}
              {saveMutation.isPending ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>

      <textarea
        className="w-full min-h-[420px] rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2.5 font-mono text-xs leading-relaxed text-zinc-200 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-700 focus:ring-1 focus:ring-zinc-700 disabled:opacity-50"
        value={reviewPromptTextarea.localValue}
        disabled={isDisabled}
        onChange={(event) => reviewPromptTextarea.handleChange(event.target.value)}
        spellCheck={false}
      />

      {reviewPromptQuery.isError && (
        <p className="mt-1.5 text-xs text-red-400">
          {reviewPromptQuery.error instanceof Error ? reviewPromptQuery.error.message : "Failed to load review prompt"}
        </p>
      )}
      {saveMutation.isPending && (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-zinc-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          Saving…
        </p>
      )}
      {saveMutation.isError && (
        <p className="mt-1.5 text-xs text-red-400">
          {saveMutation.error instanceof Error ? saveMutation.error.message : "Failed to save review prompt"}
        </p>
      )}
      {!canManage && !reviewPromptQuery.isLoading && (
        <p className="mt-2 text-xs text-zinc-500">
          Only the repo owner or an admin can edit this prompt.
        </p>
      )}
      {canManage && (
        <p className="mt-2 text-xs text-zinc-500">
          Leave this equal to the default, or reset it, to inherit Cord’s OpenCode-compatible default prompt.
        </p>
      )}
    </SettingsCard>
  );
}

/**
 * Scope for the admin toggle:
 * - "me"       → user's personal instructions for THIS repo (user-level override)
 * - "everyone" → instructions all org members see for THIS repo (repo-level)
 */
type TestingScope = "me" | "everyone";

function TestingTab({ repoId }: { repoId: string }) {
  const queryClient = useQueryClient();

  // ---- Repo-level data ("for everyone") ----
  const repoQuery = useQuery<{
    browserTestingInstructions: string | null;
    canManage: boolean;
  }>({
    queryKey: ["repo-browser-testing-instructions", repoId],
    queryFn: () => apiFetch(`/api/repos/${repoId}/browser-testing-instructions`),
  });

  const repoCanManage = repoQuery.data?.canManage ?? false;
  const repoInstructions = repoQuery.data?.browserTestingInstructions ?? null;

  const repoSaveMutation = useMutation({
    mutationFn: async (args: { value: string | null; repoId: string }) => {
      await apiFetch(`/api/repos/${args.repoId}/browser-testing-instructions`, {
        method: "PATCH",
        body: JSON.stringify({ browserTestingInstructions: args.value }),
      });
    },
    onSuccess: (_result, variables) => {
      queryClient.setQueryData(["repo-browser-testing-instructions", variables.repoId], {
        browserTestingInstructions: variables.value,
        canManage: true,
      });
      repoTextarea.confirmSave(variables.value);
    },
    onError: () => {
      repoTextarea.clearPending();
    },
  });

  const repoTextarea = useAutoSaveTextarea({
    serverValue: repoInstructions,
    stableKey: `repo-${repoId}`,
    onSave: useCallback((value: string | null) => {
      repoSaveMutation.mutate({ value, repoId });
    }, [repoId]),
  });

  // ---- User-level data ("for me") ----
  const userQuery = useQuery<{ instructions: string | null }>({
    queryKey: ["user-repo-browser-testing-instructions", repoId],
    queryFn: () => apiFetch(`/api/repos/${repoId}/my-browser-testing-instructions`),
  });

  const userInstructions = userQuery.data?.instructions ?? null;

  const userSaveMutation = useMutation({
    mutationFn: async (args: { value: string | null; repoId: string }) => {
      await apiFetch(`/api/repos/${args.repoId}/my-browser-testing-instructions`, {
        method: "PATCH",
        body: JSON.stringify({ instructions: args.value }),
      });
    },
    onSuccess: (_result, variables) => {
      queryClient.setQueryData(["user-repo-browser-testing-instructions", variables.repoId], {
        instructions: variables.value,
      });
      userTextarea.confirmSave(variables.value);
    },
    onError: () => {
      userTextarea.clearPending();
    },
  });

  /**
   * For the user textarea, the server value is their personal override.
   * If they haven't set one, show the repo-level instructions as a starting
   * point (they can edit and save to create their own override).
   */
  const userEffectiveValue = userInstructions ?? repoInstructions;

  const userTextarea = useAutoSaveTextarea({
    serverValue: userEffectiveValue,
    stableKey: `user-${repoId}`,
    onSave: useCallback((value: string | null) => {
      userSaveMutation.mutate({ value, repoId });
    }, [repoId]),
  });

  // ---- Scope toggle (admin only) ----
  const [scope, setScope] = useState<TestingScope>("me");
  const dataReady = !repoQuery.isLoading && !userQuery.isLoading;

  // Determine what to show.
  const isAdmin = repoCanManage;
  const activeScope: TestingScope = isAdmin ? scope : "me";
  const isOnMe = activeScope === "me";

  // Active textarea / mutation based on scope.
  const activeTextarea = isOnMe ? userTextarea : repoTextarea;
  const activeSaveMutation = isOnMe ? userSaveMutation : repoSaveMutation;
  const activeQueryError = isOnMe ? userQuery.isError : repoQuery.isError;
  const activeQueryErrorObj = isOnMe ? userQuery.error : repoQuery.error;
  const isDisabled = isOnMe
    ? (userQuery.isLoading || repoQuery.isLoading || userSaveMutation.isPending)
    : (repoQuery.isLoading || repoSaveMutation.isPending);

  // Whether the user has a personal override set.
  const hasUserOverride = Boolean(userInstructions?.trim());

  return (
    <SettingsCard>
      {/* Header */}
      <div className="mb-3 flex items-center gap-2.5">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-zinc-800/80">
          <MonitorCheck className="h-3.5 w-3.5 text-zinc-400" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-zinc-200">Browser testing instructions</h3>
          <p className="text-xs text-zinc-500">
            Tell the agent how to test your app — login steps, test credentials, key pages to check.
          </p>
        </div>
      </div>

      {/* Scope toggle — admins only */}
      {isAdmin && (
        <div className="mb-3 flex items-center gap-1 rounded-lg bg-zinc-800/50 p-0.5 w-fit">
          <button
            type="button"
            className={cn(
              "rounded-md px-3 py-1 text-xs font-medium transition-colors",
              isOnMe
                ? "bg-zinc-700 text-zinc-100 shadow-sm"
                : "text-zinc-400 hover:text-zinc-300",
            )}
            onClick={() => setScope("me")}
          >
            For me
          </button>
          <button
            type="button"
            className={cn(
              "rounded-md px-3 py-1 text-xs font-medium transition-colors",
              !isOnMe
                ? "bg-zinc-700 text-zinc-100 shadow-sm"
                : "text-zinc-400 hover:text-zinc-300",
            )}
            onClick={() => setScope("everyone")}
          >
            For everyone
          </button>
        </div>
      )}

      {/* Single textarea */}
      <textarea
        className="w-full min-h-[140px] rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2.5 font-mono text-xs leading-relaxed text-zinc-200 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-700 focus:ring-1 focus:ring-zinc-700 disabled:opacity-50"
        placeholder={"## Login\n- URL: /login\n- Email: test@example.com\n- Password: test123\n\n## Key Pages\n- Dashboard: /dashboard\n- Settings: /settings"}
        value={activeTextarea.localValue}
        disabled={isDisabled}
        onChange={(e) => activeTextarea.handleChange(e.target.value)}
        onBlur={activeTextarea.handleBlur}
        spellCheck={false}
      />

      {/* Status indicators */}
      {activeQueryError && (
        <p className="mt-1.5 text-xs text-red-400">
          {activeQueryErrorObj instanceof Error ? activeQueryErrorObj.message : "Failed to load instructions"}
        </p>
      )}
      {activeSaveMutation.isPending && (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-zinc-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          Saving…
        </p>
      )}
      {activeSaveMutation.isError && (
        <p className="mt-1.5 text-xs text-red-400">
          {activeSaveMutation.error instanceof Error ? activeSaveMutation.error.message : "Failed to save"}
        </p>
      )}

      {/* Contextual notes */}
      {isAdmin && isOnMe && hasUserOverride && dataReady && (
        <p className="mt-2 text-xs text-zinc-500">
          Your personal instructions override the team defaults for your sessions.
        </p>
      )}
      {isAdmin && !isOnMe && dataReady && (
        <p className="mt-2 text-xs text-zinc-500">
          These instructions are used by everyone on your team for this repo.
        </p>
      )}
    </SettingsCard>
  );
}
